import { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate, useNavigationType, useParams } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from 'motion/react';
import {
  Activity,
  ArrowRight,
  BarChart3,
  Bot,
  Eye,
  Clock,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Code,
  Coins,
  Cpu,
  CreditCard,
  Database,
  FileCheck,
  FileText,
  Globe,
  Layers,
  LayoutGrid,
  Instagram,
  Linkedin,
  Search,
  Settings,
  LifeBuoy,
  LineChart,
  Lock,
  MessageSquare,
  Monitor,
  Network,
  Pause,
  Phone,
  Play,
  Quote,
  RefreshCcw,
  RefreshCw,
  Share2,
  Shield,
  ShieldCheck,
  ShoppingCart,
  Star,
  Terminal,
  Unplug,
  X,
  Users,
  Zap,
  Sparkles
} from 'lucide-react';
import { Navbar } from './components/Navbar';
import { Button } from './components/Button';
import { SectionHeader } from './components/SectionHeader';
import { Logo } from './components/Logo';
import { TiltCard } from './components/TiltCard';
import { LiveCounter } from './components/LiveCounter';
import velosioAvatar from './components/images/velosio.png';
import tatalogo from './components/images/tatatelelogo.png';
import softwareOneAvatar from './components/images/softwareonelogo.webp';
import embeeAvatar from './components/images/embeelogo.png';
import jioAvatar from './components/images/jioLogo-removebg-preview.png';
import compuageLogo from './components/images/Compuage_logo.png';
import saveXLogo from './components/images/saveX-removebg-preview.webp';
import redLogo from './components/images/RedLogo.png';
import enterpriseDnaImage from './components/images/DNAsection.jpg';
import rajeevImage from './components/images/rajivvij.jfif';
import saranSirImage from './components/images/saransir.jpg';
import ashokImage from './components/images/ashok.jpg';
import testimonial1 from './components/images/jim.png';
import testimonial2 from './components/images/prayank.png';
import testimonial3 from './components/images/mike.png';
import testimonial4 from './components/images/tapas.png';
import testimonial5 from './components/images/abhishektata.jfif';
import marketplaceHomepageImage from './components/images/marketplace_homepage1.jpg';
import cpqHomepageImage from './components/images/cpq_homepage1.jfif';
import unifiedBillingHomepageImage from './components/images/unifiedbilling_homepage1.jfif';
import operationalLayerImage from './components/images/operational-layer.jfif';
import telcoHomepageImage from './components/images/telco_homepage1.jfif';
import telcototechoImage from './components/images/telcototecho.jpg';
import cxOrchestrationImage from './components/images/CX_orchetration.png';
import revenueEngineImage from './components/images/RevenueEngine.png';
import digitalLifecycleImage from './components/images/digitalLifecycle.png';
import aiRevenueImage from './components/images/airevenue.png';
import saasHomepageImage from './components/images/saas_homepage1.jfif';
import isvImage from './components/images/isv&saas.jpg';
import microsoftLogo from './components/images/micrsoftlogo.png';
import googleLogo from './components/images/Logo-google.avif';
import awsLogo from './components/images/aws-icon.webp';
import serviceNowLogo from './components/images/servicenow_logo.webp';
import avalaraLogo from './components/images/avalara-logo.png';
import acronisLogo from './components/images/Acronis_logo.png';
import wabaLogo from './components/images/waba-logo.webp';
import freshdeskLogo from './components/images/freshdesk-logo.png';
import appGallopLogoWhite from './components/images/appgalloplogowhite.svg';
import soc1Type2Logo from './components/images/soc1type2.png';
import soc2Type2Logo from './components/images/soc2type2.png';
import avalaraPartnerLogo from './components/images/avalarapartner.png';
import idcLogo from './components/images/idc.png';
import isoLogo from './components/images/ISO.webp';
import microsoftCertifiedLogo from './components/images/microsoft_certified.png';
import { UCBillingPageContent } from './components/UCBillingPage.tsx';
import { RMSPageContent } from './components/RMSPage.tsx';
import {
  consumeNavigationIntentForCurrentPath,
  getStoredOriginScrollY,
  getStoredOriginSectionId,
  storeCurrentNavigationOrigin,
  storeNavigationIntent,
} from './utils/navigationMemory';
import { executeRecaptchaV3 } from './utils/recaptchaV3';
// --- Global Scroll to Top ---

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const hasClickNavigationIntent = consumeNavigationIntentForCurrentPath();

    if (hash) {
      const timer = setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }

    if (navigationType === 'POP' && !hasClickNavigationIntent) {
      const timer = setTimeout(() => {
        const savedSectionId = getStoredOriginSectionId();
        if (savedSectionId) {
          const savedSection = document.getElementById(savedSectionId);
          if (savedSection) {
            savedSection.scrollIntoView({ behavior: 'auto', block: 'start' });
            return;
          }
        }

        const savedScroll = getStoredOriginScrollY();
        if (savedScroll) {
          window.scrollTo({ top: Number(savedScroll), behavior: 'auto' });
        }
      }, 120);
      return () => clearTimeout(timer);
    }

    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [pathname, hash, navigationType]);

  return null;
}

import { NetworkLattice } from './components/NetworkLattice';
import { RecaptchaV3Badge } from './components/RecaptchaV3Badge';
import { ScrollProgress } from './components/ScrollProgress';
import ScrollToTopButton from './components/ScrollToTopButton';
import { Chatbot } from './components/Chatbot';
import TelcoToTechcoPage from './pages/industries/TelcoToTechcoPage';
import CloudDistributionPage from './pages/industries/CloudDistributionPage';
import SaasIsvPage from './pages/industries/SaasIsvPage';
import CoreCapabilitiesPage from './pages/CoreCapabilitiesPage';
import SystemResiliencePage from './pages/SystemResiliencePage';
import CoreAdvantagePage from './pages/CoreAdvantagePage';
import LeadToOrderPage from './pages/LeadToOrderPage';
import OrderToCashPage from './pages/OrderToCashPage';
import TicketToResolutionPage from './pages/TicketToResolutionPage';
import CaseStudiesPage from './pages/CaseStudiesPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import NotFoundPage from './pages/NotFoundPage';

// --- Shared Components ---

const SITE_URL = "https://appgallop.com";
const SITE_NAME = "AppGallop";
const CONTACT_EMAIL = "sales@appgallop.com";
const CONTACT_PHONE = "+91-11-45059130";
const LOGO_URL = `${SITE_URL}/logo_appgallop.png`;
const SOCIAL_PROFILE_URLS = [
  "https://www.linkedin.com/company/appgallop/",
  "https://www.instagram.com/appgallop/",
];
const OFFICE_ADDRESS = {
  streetAddress: "912, Westend Mall, Janakpuri District Center",
  addressLocality: "New Delhi",
  postalCode: "110058",
  addressCountry: "IN",
};
const CORE_KEYWORDS = [
  "revenue orchestration",
  "telecom monetization",
  "digital commerce",
  "marketplace platform",
  "billing automation",
  "revenue operations",
  "partner onboarding",
  "lifecycle orchestration",
  "marketplace launch",
  "revenue recognition",
];

const DEFAULT_META_DESCRIPTION =
  "AppGallop delivers enterprise-grade revenue orchestration, telecom monetization, and digital lifecycle automation engineered for hyperscale growth";

type SeoConfig = {
  title: string;
  description: string;
  keywords?: string[];
};

const staticSeo: Record<string, SeoConfig> = {
  "/": {
    title: "AppGallop | Revenue Orchestration, Billing & Marketplace Platform",
    description: DEFAULT_META_DESCRIPTION,
    keywords: ["revenue orchestration platform", "billing automation", "digital commerce platform", "marketplace platform", "telecom monetization"],
  },
  "/platform": {
    title: "Revenue Orchestration Platform | AppGallop",
    description: "Learn how AppGallop bridges sales activity and financial accuracy with unified lifecycle-to-ledger automation for global enterprises.",
    keywords: ["revenue orchestration", "lifecycle orchestration", "revenue operations"],
  },
  "/platform/integrations": {
    title: "Revenue Operations Integrations | AppGallop",
    description: "Explore AppGallop's available API integrations across tax, payments, cloud, CRM, ITSM, banking, messaging, and enterprise operations.",
    keywords: ["revenue operations integrations", "billing automation integrations", "digital commerce APIs"],
  },
  "/platform/security": {
    title: "Enterprise Revenue Platform Security | AppGallop",
    description: "Review AppGallop's enterprise security posture for encrypted revenue operations, compliance-driven automation, and zero-trust digital commerce.",
    keywords: ["enterprise platform security", "revenue operations security", "compliance automation"],
  },
  "/platform/about": {
    title: "Revenue Orchestration Platform | AppGallop",
    description: "Learn how AppGallop bridges sales activity and financial accuracy with unified lifecycle-to-ledger automation for global enterprises.",
    keywords: ["revenue orchestration", "lifecycle orchestration", "digital commerce"],
  },
  "/platform/lead-to-order": {
    title: "Lead to Order Automation | AppGallop",
    description: "Explore AppGallop Lead to Order automation for lead intake, opportunity routing, CPQ, quote approval, partner negotiation, and order conversion.",
    keywords: ["lead to order automation", "CPQ automation", "partner onboarding"],
  },
  "/platform/order-to-cash": {
    title: "Order to Cash Billing Automation | AppGallop",
    description: "Explore AppGallop O2C Order to Cash automation for order validation, billing, invoicing, collections, reconciliation, and revenue reporting.",
    keywords: ["order to cash", "billing automation", "revenue recognition"],
  },
  "/platform/ticket-to-resolution": {
    title: "Ticket to Resolution Workflow Automation | AppGallop",
    description: "Explore AppGallop T2R Ticket to Resolution automation for ticket intake, classification, routing, SLA governance, resolution, and service intelligence.",
    keywords: ["ticket to resolution", "workflow automation", "service lifecycle orchestration"],
  },
  "/platform/system-resilience": {
    title: "System Resilience for Revenue Operations | AppGallop",
    description: "Explore AppGallop system resilience for commerce lifecycle automation, cloud service orchestration, revenue reconciliation, and enterprise-grade governance.",
    keywords: ["system resilience", "revenue operations", "lifecycle orchestration"],
  },
  "/platform/core-advantage": {
    title: "Core Advantage for Digital Commerce | AppGallop",
    description: "See how AppGallop's core operating model unifies cloud commerce, telecom billing, subscriptions, renewals, and partner ecosystems.",
    keywords: ["digital commerce", "telecom monetization", "subscription billing"],
  },
  "/services": {
    title: "Revenue Operations Services | AppGallop",
    description: "Explore AppGallop platform services for revenue assurance, process control, managed support desk operations, and consulting or custom development around marketplace, billing, CPQ, and lifecycle workflows.",
    keywords: ["AppGallop services", "revenue assurance", "process control", "managed support desk", "custom development", "marketplace operations"],
  },
  "/solutions": {
    title: "Revenue Orchestration Solutions | AppGallop",
    description: "Explore AppGallop solutions for customer experience orchestration, revenue engineering, digital lifecycle automation, and scalable commerce operations.",
    keywords: ["revenue orchestration solutions", "digital lifecycle automation", "revenue engineering"],
  },
  "/contact": {
    title: "Contact | AppGallop",
    description: "Connect with AppGallop to discuss enterprise revenue orchestration, telecom monetization, digital lifecycle automation, and hyperscale growth.",
    keywords: ["contact AppGallop", "revenue orchestration demo", "billing automation demo"],
  },
  "/case-studies": {
    title: "Case Studies | AppGallop",
    description: "Read AppGallop case studies showing how global cloud distributors scale private-label cloud commerce, billing automation, partner marketplaces, and revenue operations.",
    keywords: ["AppGallop case studies", "cloud commerce case study", "SoftwareOne marketplace", "cloud distribution platform"],
  },
  "/capabilities": {
    title: "Core Revenue Platform Capabilities | AppGallop",
    description: "Explore AppGallop capabilities for onboarding, CPQ, order orchestration, subscription billing, revenue compliance, and partner channel management.",
    keywords: ["revenue platform capabilities", "subscription billing", "partner management"],
  },
  "/system-resilience": {
    title: "System Resilience for Revenue Operations | AppGallop",
    description: "Explore AppGallop system resilience for lifecycle workflows, service orchestration, revenue reconciliation, and compliance automation.",
    keywords: ["system resilience", "revenue operations", "workflow automation"],
  },
  "/core-advantage": {
    title: "Core Advantage for Digital Commerce | AppGallop",
    description: "Explore AppGallop's core advantage across cloud commerce, telecom monetization, billing automation, renewals, and partner operations.",
    keywords: ["digital commerce", "marketplace platform", "billing automation"],
  },
};

const dynamicSeo: Record<string, Record<string, SeoConfig>> = {
  services: {
    "revenue-assurance": {
      title: "Revenue Assurance Services | AppGallop",
      description: "Protect revenue, reduce leakage, validate billing records, automate reconciliation, and strengthen financial governance across digital service operations.",
      keywords: ["revenue assurance", "billing validation", "settlement reconciliation", "revenue leakage detection"],
    },
    "process-control": {
      title: "Process Control Services | AppGallop",
      description: "Standardize and govern operational workflows with policy enforcement, SLA management, approval automation, exception handling, and execution visibility.",
      keywords: ["process control", "workflow orchestration", "SLA management", "operational governance"],
    },
    "managed-support-desk": {
      title: "Managed Support Desk Services | AppGallop",
      description: "Deliver structured support operations for telecom, cloud marketplace, distributor, and digital service businesses with SLA governance and intelligent ticket routing.",
      keywords: ["managed support desk", "SLA governance", "ticket routing", "digital operations support"],
    },
    "consulting-custom-development": {
      title: "Consulting & Custom Development Services | AppGallop",
      description: "Modernize digital operations with transformation consulting, custom platform development, systems integration, process automation, and cloud modernization.",
      keywords: ["custom platform development", "digital transformation consulting", "systems integration", "cloud modernization"],
    },
  },
  "case-studies": {
    softwareone: {
      title: "SoftwareOne Cloud Commerce Case Study | AppGallop",
      description: "See how AppGallop supported SoftwareOne with private-label cloud commerce, multi-currency marketplace operations, partner channels, compliance, and cloud resource management.",
      keywords: ["SoftwareOne case study", "cloud commerce", "private-label marketplace"],
    },
    "jio-platforms": {
      title: "Jio OMNI Services Integration Case Study | AppGallop",
      description: "Explore AppGallop's Jio case study for OMNI services integration with legacy OSS/BSS, CRM, ERP, billing, support, accounting, and telco operations.",
      keywords: ["Jio case study", "OSS BSS integration", "telco digital services"],
    },
    "tata-tele-nxtgen": {
      title: "Tata Tele NXTGEN Case Study | AppGallop",
      description: "Read how AppGallop helped Tata Tele NXTGEN build a telco-to-techco digital services platform across marketplace, billing, UCaaS, CCaaS, and partner ecosystems.",
      keywords: ["Tata Tele NXTGEN case study", "telco to techco", "UCaaS billing", "CCaaS billing"],
    },
    redington: {
      title: "Redington Lead-to-Cash Case Study | AppGallop",
      description: "See how AppGallop helped Redington modernize distributor commerce with governed CPQ, SAP integration, partner self-service, approvals, payments, and invoice visibility.",
      keywords: ["Redington case study", "lead to cash", "distributor CPQ", "SAP integration"],
    },
  },
  products: {
    marketplace: {
      title: "Digital Commerce & Monetization Platform | AppGallop",
      description: "AppGallop orchestrates digital commerce, monetization, partner ecosystems, real-time provisioning, billing, collections, and renewals from one enterprise platform.",
      keywords: ["digital commerce platform", "monetization platform", "partner onboarding", "billing automation", "revenue lifecycle"],
    },
    revsimply: {
      title: "CPQ Revenue Orchestration Platform | AppGallop",
      description: "AppGallop CPQ by AppGallop accelerates quote-to-cash with AI-powered configuration, pricing automation, contract mediation, and margin governance.",
      keywords: ["CPQ automation", "revenue orchestration", "quote to cash", "revenue operations"],
    },
    rms: {
      title: "Renewal Management & Revenue Automation | AppGallop",
      description: "AppGallop RMS by AppGallop automates renewals, predicts churn, and delivers predictable recurring revenue growth with intelligent orchestration.",
      keywords: ["renewal management", "revenue automation", "revenue operations", "subscription billing"],
    },
    "uc-billing": {
      title: "Telecom Billing Automation & Mediation | AppGallop",
      description: "AppGallop UC Billing & Mediation powers carrier-grade rating, CDR mediation, invoice automation, and global communications monetization.",
      keywords: ["telecom billing automation", "telecom monetization", "UC billing", "CDR mediation"],
    },
  },
  solutions: {
    "cx-orchestration": {
      title: "CX Orchestration for Digital Commerce | AppGallop",
      description: "AppGallop CX Orchestration synchronizes customer journeys, identity, service automation, and commercial workflows for enterprise retention.",
      keywords: ["CX orchestration", "digital commerce", "customer lifecycle orchestration"],
    },
    "revenue-engineering": {
      title: "Revenue Engineering & Recognition Automation | AppGallop",
      description: "AppGallop Revenue Engineering protects pricing, discounts, contract logic, revenue recognition, and financial compliance at scale.",
      keywords: ["revenue engineering", "revenue recognition", "revenue operations"],
    },
    "digital-lifecycle": {
      title: "Digital Lifecycle Automation | AppGallop",
      description: "AppGallop Digital Lifecycle automation bridges legacy infrastructure, cloud provisioning, partner ecosystems, and zero-downtime service growth.",
      keywords: ["digital lifecycle automation", "lifecycle orchestration", "cloud provisioning"],
    },
  },
  industries: {
    "telco-to-techco": {
      title: "Telco Digital Monetization | AppGallop",
      description: "AppGallop helps telcos monetize cloud marketplaces, UCaaS, CCaaS, CPaaS, AI services, and partner ecosystems through one digital revenue platform.",
      keywords: ["telco digital monetization", "UCaaS monetization", "CCaaS monetization", "CPaaS monetization", "AI marketplace"],
    },
    "cloud-distribution": {
      title: "Cloud Distribution Marketplace Platform | AppGallop",
      description: "AppGallop unifies cloud marketplace, CSP billing, multi-tier channel automation, and Microsoft or AWS distribution operations for modern cloud distributors.",
      keywords: ["cloud distribution", "cloud marketplace", "CSP billing", "multi-tier channel automation", "Microsoft AWS distribution"],
    },
    "saas-isv": {
      title: "ISV Monetization Platform | AppGallop",
      description: "AppGallop helps software vendors manage subscription billing, license management, marketplace syndication, and partner distribution from one platform.",
      keywords: ["ISV monetization", "subscription billing", "license management", "marketplace syndication", "partner distribution"],
    },
    utilities: {
      title: "Energy & Utilities | AppGallop",
      description: "AppGallop supports utilities with smart meter mediation, dynamic rate logic, consumption billing, and digital customer engagement.",
      keywords: ["utility billing", "consumption billing", "digital customer engagement"],
    },
  },
};

const formatSlugTitle = (slug: string) =>
  slug
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getSeoForPath = (pathname: string) => {
  if (staticSeo[pathname]) return staticSeo[pathname];

  const [section, slug] = pathname.replace(/^\/+/, "").split("/");
  const sectionSeo = dynamicSeo[section]?.[slug];
  if (sectionSeo) return sectionSeo;

  if (section && slug) {
    return {
      title: `${formatSlugTitle(slug)} | AppGallop`,
      description: DEFAULT_META_DESCRIPTION,
    };
  }

  return staticSeo["/"];
};

const getCanonicalPath = (pathname: string) => {
  if (pathname === "/platform") return "/platform/about";
  if (pathname === "/platform/core-capabilities") return "/platform/lead-to-order";
  return pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
};

const getCanonicalUrl = (pathname: string) =>
  `${SITE_URL}${getCanonicalPath(pathname) === "/" ? "" : getCanonicalPath(pathname)}`;

const setMetaTag = (selector: string, createAttributes: Record<string, string>, content: string) => {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);

  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(createAttributes).forEach(([key, value]) => {
      tag?.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const setLinkTag = (selector: string, createAttributes: Record<string, string>, href: string) => {
  let tag = document.head.querySelector<HTMLLinkElement>(selector);

  if (!tag) {
    tag = document.createElement("link");
    Object.entries(createAttributes).forEach(([key, value]) => {
      tag?.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }

  tag.setAttribute("href", href);
};

const setJsonLdScript = (id: string, schema: Record<string, any>) => {
  let script = document.getElementById(id) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schema);
};

const getBreadcrumbItems = (pathname: string) => {
  const canonicalPath = getCanonicalPath(pathname);
  const segments = canonicalPath.split("/").filter(Boolean);
  const items = [{ name: "Home", url: SITE_URL }];

  segments.forEach((_, index) => {
    const path = `/${segments.slice(0, index + 1).join("/")}`;
    const seo = getSeoForPath(path);
    const name = seo === staticSeo["/"] && !staticSeo[path]
      ? formatSlugTitle(segments[index])
      : seo.title.replace(/\s*\|\s*AppGallop$/, "");

    items.push({
      name,
      url: `${SITE_URL}${path}`,
    });
  });

  return items;
};

const getProductSchemaData = (pathname: string, seo: SeoConfig) => {
  const [, section, slug] = pathname.match(/^\/(products)\/([^/]+)$/) || [];
  if (section !== "products" || !slug) return null;

  const data = productData?.[slug];
  const name = data?.title || seo.title.replace(/\s*\|\s*AppGallop$/, "");

  return {
    slug,
    name,
    description: data?.desc || seo.description,
    category: slug === "uc-billing"
      ? "Telecom billing automation"
      : slug === "marketplace"
        ? "Digital commerce marketplace platform"
        : slug === "rms"
          ? "Revenue management software"
          : "CPQ revenue orchestration software",
  };
};

const buildStructuredData = (pathname: string, seo: SeoConfig) => {
  const canonicalUrl = getCanonicalUrl(pathname);
  const breadcrumbItems = getBreadcrumbItems(pathname);
  const product = getProductSchemaData(getCanonicalPath(pathname), seo);

  const graph: Record<string, any>[] = [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: SITE_NAME,
      legalName: "AppGallop",
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: LOGO_URL,
      },
      sameAs: SOCIAL_PROFILE_URLS,
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: CONTACT_PHONE,
          email: CONTACT_EMAIL,
          contactType: "sales",
          areaServed: "Worldwide",
          availableLanguage: ["en"],
        },
      ],
      address: {
        "@type": "PostalAddress",
        ...OFFICE_ADDRESS,
      },
    },
    {
      "@type": "ProfessionalService",
      "@id": `${SITE_URL}/#local-business`,
      name: SITE_NAME,
      url: SITE_URL,
      image: LOGO_URL,
      logo: LOGO_URL,
      telephone: CONTACT_PHONE,
      email: CONTACT_EMAIL,
      address: {
        "@type": "PostalAddress",
        ...OFFICE_ADDRESS,
      },
      parentOrganization: {
        "@id": `${SITE_URL}/#organization`,
      },
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: SITE_NAME,
      url: SITE_URL,
      publisher: {
        "@id": `${SITE_URL}/#organization`,
      },
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: seo.title,
      description: seo.description,
      isPartOf: {
        "@id": `${SITE_URL}/#website`,
      },
      publisher: {
        "@id": `${SITE_URL}/#organization`,
      },
      breadcrumb: {
        "@id": `${canonicalUrl}#breadcrumb`,
      },
      about: CORE_KEYWORDS.map((name) => ({ "@type": "Thing", name })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${canonicalUrl}#breadcrumb`,
      itemListElement: breadcrumbItems.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    },
  ];

  if (product) {
    graph.push(
      {
        "@type": "SoftwareApplication",
        "@id": `${canonicalUrl}#software-application`,
        name: product.name,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: canonicalUrl,
        description: product.description,
        offers: {
          "@type": "Offer",
          url: canonicalUrl,
          availability: "https://schema.org/InStock",
        },
        publisher: {
          "@id": `${SITE_URL}/#organization`,
        },
      },
      {
        "@type": "Product",
        "@id": `${canonicalUrl}#product`,
        name: product.name,
        brand: {
          "@id": `${SITE_URL}/#organization`,
        },
        category: product.category,
        url: canonicalUrl,
        description: product.description,
      }
    );
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
};

function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getSeoForPath(pathname);
    const canonicalUrl = getCanonicalUrl(pathname);
    const keywords = [...CORE_KEYWORDS, ...(seo.keywords || [])]
      .filter((keyword, index, list) => list.indexOf(keyword) === index)
      .join(", ");

    document.title = seo.title;
    setMetaTag('meta[name="description"]', { name: "description" }, seo.description);
    setMetaTag('meta[name="keywords"]', { name: "keywords" }, keywords);
    setMetaTag('meta[property="og:title"]', { property: "og:title" }, seo.title);
    setMetaTag('meta[property="og:description"]', { property: "og:description" }, seo.description);
    setMetaTag('meta[property="og:url"]', { property: "og:url" }, canonicalUrl);
    setMetaTag('meta[name="twitter:title"]', { name: "twitter:title" }, seo.title);
    setMetaTag('meta[name="twitter:description"]', { name: "twitter:description" }, seo.description);
    setLinkTag('link[rel="canonical"]', { rel: "canonical" }, canonicalUrl);
    setJsonLdScript("appgallop-route-schema", buildStructuredData(pathname, seo));
  }, [pathname]);

  return null;
}

function AnalyticsManager() {
  useEffect(() => {
    const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const facebookPixelId = import.meta.env.VITE_FACEBOOK_PIXEL_ID;

    if (gaMeasurementId && !document.getElementById("ga4-script")) {
      const gaScript = document.createElement("script");
      gaScript.id = "ga4-script";
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaMeasurementId)}`;
      document.head.appendChild(gaScript);

      (window as any).dataLayer = (window as any).dataLayer || [];
      const gtag = (...args: any[]) => (window as any).dataLayer.push(args);
      gtag("js", new Date());
      gtag("config", gaMeasurementId);
    }

    if (clarityProjectId && !(window as any).clarity) {
      (function(c: any, l: Document, a: string, r: string, i: string) {
        c[a] = c[a] || function(...args: any[]) {
          (c[a].q = c[a].q || []).push(args);
        };
        const script = l.createElement(r) as HTMLScriptElement;
        script.async = true;
        script.src = `https://www.clarity.ms/tag/${encodeURIComponent(i)}`;
        script.id = "clarity-script";
        const firstScript = l.getElementsByTagName(r)[0];
        firstScript.parentNode?.insertBefore(script, firstScript);
      })(window, document, "clarity", "script", clarityProjectId);
    }

    if (facebookPixelId && !(window as any).fbq) {
      (function(f: any, b: Document, e: string, v: string, n?: any, t?: HTMLScriptElement, s?: Element) {
        if (f.fbq) return;
        n = f.fbq = function(...args: any[]) {
          n.callMethod ? n.callMethod(...args) : n.queue.push(args);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e) as HTMLScriptElement;
        t.async = true;
        t.src = v;
        t.id = "facebook-pixel-script";
        s = b.getElementsByTagName(e)[0];
        s.parentNode?.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

      (window as any).fbq("init", facebookPixelId);
      (window as any).fbq("track", "PageView");
    }
  }, []);

  return null;
}

const MouseInteractiveExperienceContext = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseX = useSpring(x, { stiffness: 40, damping: 20 });
  const mouseY = useSpring(y, { stiffness: 40, damping: 20 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      x.set((e.clientX - centerX) * 0.2); // Slower, more subtle movement
      y.set((e.clientY - centerY) * 0.2);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [x, y]);

  return (
    <div ref={ref} className="relative w-full h-full">
      <motion.div
        style={{ x: mouseX, y: mouseY }}
        className="absolute -inset-20 bg-[#ff5d22]/15 blur-[140px] rounded-full opacity-50 animate-pulse pointer-events-none"
      />
      <div className="relative z-10 w-full h-full text-left">
        {children}
      </div>
    </div>
  );
};

const MockDashboard = ({ className = "" }: { className?: string }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className={`glass-card-dark rounded-2xl w-full aspect-video overflow-hidden relative shadow-2xl border border-white/5 ${className}`}
  >
    <div className="absolute top-0 left-0 right-0 h-10 border-b border-slate-800 flex items-center px-4 gap-2 bg-slate-900/50 backdrop-blur-md z-10">
      <div className="w-2.5 h-2.5 rounded-full bg-slate-500/50" />
      <div className="w-2.5 h-2.5 rounded-full bg-blue-500/50" />
      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/50" />
      <div className="flex-1 flex justify-center">
        <div className="h-4 w-32 bg-white/5 rounded" />
      </div>
    </div>
    <div className="pt-14 px-6 grid grid-cols-12 gap-6 h-full bg-[#050505]">
      <div className="col-span-3 space-y-4 pt-4 border-r border-white/5">
        {[1, 2, 3, 4].map(i => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 + 0.5 }}
            className="h-8 bg-white/5 rounded-lg w-full flex items-center px-3 gap-2 hover:bg-white/10 transition-colors cursor-pointer"
          >
            <div className="w-3 h-3 bg-[#2563EB]/40 rounded-sm" />
            <div className="h-2 w-16 bg-white/10 rounded" />
          </motion.div>
        ))}
      </div>
      <div className="col-span-9 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="h-24 bg-[#2563EB]/10 rounded-xl border border-[#2563EB]/20 p-4 relative overflow-hidden"
        >
          <div className="text-[10px] uppercase font-bold text-blue-300">Live Lifecycle Orchestration</div>
          <div className="text-3xl font-bold text-white mt-1">
            <LiveCounter value={1284} />
          </div>
          <div className="h-1.5 w-full bg-black rounded-full mt-3 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "66%" }}
              transition={{ duration: 1.5, delay: 1 }}
              className="h-full bg-[#2563EB] rounded-full shadow-[0_0_10px_rgba(37,99,235,0.35)]"
            />
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="flex-1 bg-white/5 rounded-2xl p-6 h-[200px] relative overflow-hidden group"
        >
          <div className="absolute inset-0 flex items-end">
            <svg viewBox="0 0 400 100" className="w-full h-32 text-[#2563EB]/20 fill-current transition-transform duration-500 group-hover:scale-105">
              <motion.path
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 2, delay: 1.2 }}
                d="M0 80 Q 25 20, 50 60 T 100 40 T 150 70 T 200 30" fill="none" stroke="#2563EB" strokeWidth="2"
              />
              <path d="M0 100 V 40 Q 50 10 100 50 T 200 40 T 300 60 T 400 30 V 100 Z" opacity="0.1" />
            </svg>
          </div>
          <div className="relative z-10 grid grid-cols-2 gap-4 h-full">
            <div className="h-full border-r border-white/5 pr-4 flex flex-col justify-center">
              <span className="text-[10px] text-slate-400 uppercase font-bold">Uptime</span>
              <span className="text-2xl font-bold text-emerald-400">99.999%</span>
            </div>
            <div className="h-full flex flex-col justify-center">
              <span className="text-[10px] text-slate-400 uppercase font-bold">API Requests</span>
              <span className="text-2xl font-bold text-white">4.2M</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </motion.div>
);

const ExperienceConsole = () => (
  <div className="relative w-full min-h-[430px] rounded-[2.5rem] overflow-hidden border border-white/5 bg-[#07080d] sm:min-h-[390px] lg:min-h-[370px] xl:min-h-[390px]">
    <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_20%_10%,rgba(227,30,36,0.12),transparent_45%)]" />
    <div className="relative z-10 h-full flex flex-col">
      <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/5 bg-white/[0.02]">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-white/50">Unified Admin Console</div>
          <div className="text-sm font-bold text-white">Revenue and Lifecycle Control</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-1 rounded-full">Live</span>
          <span className="text-[9px] font-bold text-white/50">Sync 6s</span>
        </div>
      </div>

      <div className="flex-1 p-4 grid grid-cols-1 gap-3 sm:grid-cols-12">
        <div className="sm:col-span-7 flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Active Tenants", value: "128" },
              { label: "Billing Runs", value: "24/hr" },
              { label: "Revenue Integrity", value: "99.999%" },
            ].map((item) => (
              <div key={item.label} className="rounded-xl border border-white/5 bg-white/[0.03] p-3 min-h-[82px]">
                <div className="text-[9px] uppercase tracking-widest text-white/40 font-bold">{item.label}</div>
                <div className="text-lg font-black text-white mt-1">{item.value}</div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Lifecycle Flow</span>
              <span className="text-[10px] font-bold text-[#93BD57]">Real-Time Orchestration</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {["COM", "SOM", "TOM", "ACTIVE"].map((step, i) => (
                <div key={step} className="relative">
                  <div className="h-9 rounded-xl bg-[#93BD57]/10 border border-[#93BD57]/30 flex items-center justify-center text-[10px] font-black text-[#93BD57]">
                    {step}
                  </div>
                  {i < 3 && (
                    <div className="absolute top-1/2 left-[75%] w-full h-px bg-gradient-to-r from-[#93BD57]/50 to-transparent" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="sm:col-span-5 flex flex-col gap-3">
          <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-4">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-3">Automations</div>
            <div className="space-y-2">
              {[
                "Quote approved and synced",
                "CDR batch rated and closed",
                "Invoice run completed",
                "Revenue posted to GL",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-[11px] text-white/70 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1 rounded-2xl border border-white/5 bg-white/[0.03] p-4">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-3">Billing Health</div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-black text-white">4.2M</div>
                <div className="text-[10px] text-white/50 font-bold">Events Rated</div>
              </div>
              <div className="h-10 w-10 rounded-xl bg-[#93BD57]/15 border border-[#93BD57]/30 flex items-center justify-center text-[#93BD57] font-black text-[10px]">
                0.0%
              </div>
            </div>
            <div className="mt-3 h-1.5 w-full bg-black rounded-full overflow-hidden">
              <div className="h-full w-[86%] bg-[#93BD57] rounded-full shadow-[0_0_10px_rgba(227,30,36,0.4)]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const IndustryTabs = () => {
  const [activeTab, setActiveTab] = useState(0);

  const industries = [
    {
      id: 0,
      label: 'Telco Digital Monetization',
      icon: Globe,
      title: 'Monetize cloud, communications, AI, and partner services.',
      desc: 'Launch cloud marketplace, UCaaS, CCaaS, CPaaS, AI marketplace, and partner ecosystem offerings through one AppGallop revenue platform.',
      metric: '6x faster marketplace launch',
      cta: 'Explore Telco Monetization',
      path: '/industries/telco-to-techco',
      image: telcototechoImage
    },
    {
      id: 1,
      label: 'Cloud Distribution',
      icon: ShoppingCart,
      title: 'Modernize cloud distribution for multi-tier channels.',
      desc: 'Unify cloud marketplace, CSP billing, channel automation, and Microsoft or AWS distribution workflows for distributor-scale operations.',
      metric: 'Automated lead-to-ledger',
      cta: 'Explore Cloud Distribution',
      path: '/industries/cloud-distribution',
      image: marketplaceHomepageImage
    },
    {
      id: 2,
      label: 'ISV Monetization',
      icon: Cloud,
      title: 'Scale software revenue across billing, licensing, and channels.',
      desc: 'Manage subscription billing, license operations, marketplace syndication, and partner distribution through one AppGallop operating layer.',
      metric: 'Zero revenue leakage in billing',
      cta: 'Explore ISV Monetization',
      path: '/industries/saas-isv',
      image: isvImage
    }
  ];

  return (
    <div className="w-full">
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {industries.map((industry, i) => (
          <motion.button
            key={industry.id}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab(i)}
            className={`px-5 py-2.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${activeTab === i
              ? 'bg-[#ff5d22] text-white shadow-xl shadow-[#ff5d22]/20'
              : 'bg-slate-100 text-black/40 hover:bg-slate-200 hover:text-black hover:shadow-md'
              }`}
          >
            <div className="flex items-center gap-2">
              <industry.icon size={14} strokeWidth={2.5} />
              {industry.label}
            </div>
          </motion.button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="grid lg:grid-cols-2 gap-6 items-center bg-slate-50 rounded-[2.5rem] p-6 lg:p-8 overflow-hidden relative"
        >
          <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#ff5d22]/5 to-transparent pointer-events-none" />

          <div className="relative z-10">
            <h3 className="text-xl md:text-2xl font-black tracking-tight text-black mb-3 leading-tight">
              {industries[activeTab].title}
            </h3>
            <p className="text-black/50 text-sm font-medium mb-4 leading-relaxed">
              {industries[activeTab].desc}
            </p>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center text-[#ff5d22]">
                <LineChart size={24} />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-black/40 mb-1">Key Impact</div>
                <div className="text-base font-black text-black">{industries[activeTab].metric}</div>
              </div>
            </div>

            <Link to={industries[activeTab].path}>
              <Button variant="primary" className="!px-7 !py-3 text-[9px] font-black uppercase tracking-widest flex items-center gap-3">
                {industries[activeTab].cta} <ArrowRight size={14} strokeWidth={3} />
              </Button>
            </Link>
          </div>

          <div className="relative h-[260px] rounded-[2rem] overflow-hidden shadow-2xl group">
            <img
              src={industries[activeTab].image}
              className="w-full h-full object-fill transition-transform duration-[2s] group-hover:scale-110"
              alt={industries[activeTab].label}
              referrerPolicy="no-referrer"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const ProductEcosystem = () => {
  const [activeTab, setActiveTab] = useState(0);

  const products = [
    {
      id: 0,
      label: 'Commerce Layer',
      title: 'Go-to-market and customer engagement.',
      desc: 'The front-end layer that handles customer acquisition, marketplace operations, and service provisioning. This layer manages the entire customer journey from discovery to purchase.',
      features: [
        'Multi-channel Marketplace Engine',
        'Subscription & Usage Management',
        'Real-time Service Provisioning',
        'Customer Self-Service Portal'
      ],
      subHeadings: [
        { title: 'Marketplace Operations', desc: 'Multi-vendor digital storefronts with automated catalog management' },
        { title: 'Customer Engagement', desc: 'Self-service portals and subscription management interfaces' },
        { title: 'Service Provisioning', desc: 'Automated deployment and configuration of digital services' }
      ],
      image: marketplaceHomepageImage // More relevant for commerce layer
    },
    {
      id: 1,
      label: 'Monetization Layer',
      title: 'Revenue engine and financial control.',
      desc: 'The financial brain that handles pricing, billing, revenue recognition, and margin management. This layer ensures accurate revenue capture and financial compliance.',
      features: [
        'Dynamic Pricing & Quoting Engine',
        'Usage-based Billing & Rating',
        'Revenue Recognition & GL Sync',
        'Margin Control & Governance'
      ],
      subHeadings: [
        { title: 'Pricing Intelligence', desc: 'AI-powered pricing optimization and quote generation' },
        { title: 'Billing Automation', desc: 'Carrier-grade rating and mediation for complex billing scenarios' },
        { title: 'Financial Control', desc: 'Revenue recognition, tax compliance, and margin protection' }
      ],
      image: cpqHomepageImage // Relevant for monetization/billing
    },
    {
      id: 2,
      label: 'Operations Layer',
      title: 'Service delivery and lifecycle management.',
      desc: 'The operational backbone that manages service delivery, support operations, and customer lifecycle. This layer ensures service quality and operational excellence.',
      features: [
        'Service Delivery Automation',
        'Support & Incident Management',
        'Lifecycle Operations & MACD',
        'Performance & SLA Monitoring'
      ],
      subHeadings: [
        { title: 'Service Operations', desc: 'Automated service delivery and lifecycle management' },
        { title: 'Support Systems', desc: 'Integrated ticketing, escalation, and resolution workflows' },
        { title: 'Operational Intelligence', desc: 'Real-time monitoring, analytics, and performance optimization' }
      ],
      image: operationalLayerImage // Can represent operations/systems
    }
  ];

  return (
    <section className="bg-white py-10 md:py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-6 grid items-start gap-4 lg:grid-cols-12 lg:gap-x-7">
          <div className="max-w-3xl lg:col-span-6">
            <div className="mb-3 h-1 w-10 bg-[#ff5d22]" />
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-black leading-[1.05]">
              The Operating Brain. <br />
              <span className="text-[#ff5d22]">AI-powered Platform Layers.</span>
            </h2>
          </div>

          <div className="lg:col-span-6">
            <div className="ml-auto flex w-full max-w-[560px] flex-wrap items-center justify-start gap-2 rounded-lg border border-black/15 bg-slate-50 p-1 shadow-inner lg:justify-center">
              {products.map((p, i) => (
                <motion.button
                  key={p.id}
                  whileHover={{ scale: 1.03, y: -1 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setActiveTab(i)}
                  className={`min-h-9 flex-1 rounded-md px-3 py-2 text-[8px] font-black uppercase tracking-widest transition-all ${activeTab === i
                    ? 'bg-white text-[#ff5d22] shadow-sm border border-black/10'
                    : 'text-black/70 hover:text-black hover:bg-black/5'
                    }`}
                >
                  {p.label}
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid items-start gap-4 lg:grid-cols-12 lg:gap-x-7 lg:gap-y-4">
          <div className="rounded-lg border border-black/6 bg-slate-50 p-5 lg:col-span-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-2xl font-black tracking-tight text-black mb-4 leading-tight">
                  {products[activeTab].title}
                </h3>
                <p className="text-black/50 text-sm font-semibold leading-relaxed mb-5">
                  {products[activeTab].desc}
                </p>

                {/* Key capabilities */}
                {/* <div className="mb-8">
                  <h5 className="text-sm font-black uppercase tracking-widest text-black/40 mb-4">Key Capabilities</h5>
                  <div className="grid grid-cols-1 gap-3">
                    {products[activeTab].features.map((feat, i) => (
                      <div key={i} className="flex items-center gap-3 group">
                        <div className="w-5 h-5 rounded-full bg-[#ff5d22]/10 flex items-center justify-center text-[#ff5d22] group-hover:bg-[#ff5d22] group-hover:text-white transition-colors">
                          <Check size={10} strokeWidth={3} />
                        </div>
                        <span className="text-xs font-black text-black/70 group-hover:text-black transition-colors">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div> */}

              </motion.div>
            </AnimatePresence>
          </div>

          <div className="lg:col-span-6 lg:-mt-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, rotateY: 10, scale: 0.95 }}
                animate={{ opacity: 1, rotateY: 0, scale: 1 }}
                exit={{ opacity: 0, rotateY: -10, scale: 0.95 }}
                transition={{ duration: 0.6 }}
                className="relative ml-auto overflow-hidden rounded-[1.5rem] aspect-[16/8.8] w-full max-w-[560px] shadow-xl perspective-[2000px]"
              >
                <img
                  src={products[activeTab].image}
                  className="w-full h-full object-fill"
                  alt={products[activeTab].label}
                  referrerPolicy="no-referrer"
                />
                <div className={`absolute inset-0 transition-all duration-500 ${activeTab === 0 ? 'bg-gradient-to-tr from-blue-500/20 via-transparent to-transparent' :
                  activeTab === 1 ? 'bg-gradient-to-tr from-green-500/20 via-transparent to-transparent' :
                    'bg-gradient-to-tr from-purple-500/20 via-transparent to-transparent'
                  }`} />

                {/* Dynamic floating UI Elements over image */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                  className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-xl p-3 rounded-xl shadow-xl border border-white/20"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#ff5d22] shadow-lg flex items-center justify-center text-white">
                      {activeTab === 0 && <ShoppingCart size={17} />}
                      {activeTab === 1 && <CreditCard size={17} />}
                      {activeTab === 2 && <Settings size={17} />}
                    </div>
                    <div>
                      <div className="text-[7px] font-black uppercase tracking-widest text-[#ff5d22]">
                        {activeTab === 0 && 'Commerce Active'}
                        {activeTab === 1 && 'Revenue Engine'}
                        {activeTab === 2 && 'Operations Live'}
                      </div>
                      <div className="text-xs font-black text-black">
                        {activeTab === 0 && 'Marketplace Online'}
                        {activeTab === 1 && 'Billing Processing'}
                        {activeTab === 2 && 'Service Delivery'}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>

          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={`subheadings-${activeTab}`}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, delay: 0.08 }}
              className="grid gap-3 sm:grid-cols-3 lg:col-span-12"
            >
              {products[activeTab].subHeadings.map((subHeading, i) => (
                <div key={subHeading.title} className="group rounded-lg border border-black/6 bg-slate-50 p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#ff5d22]/25 hover:bg-white hover:shadow-lg">
                  <div className="mb-2 flex h-7 w-7 items-center justify-center rounded-lg bg-[#ff5d22]/10 text-[11px] font-black text-[#ff5d22]">
                    0{i + 1}
                  </div>
                  <h4 className="text-[18px] font-black leading-snug text-black transition-colors group-hover:text-[#ff5d22]">
                    {subHeading.title}
                  </h4>
                  <p className="mt-1.5 text-[16px] font-semibold leading-relaxed text-black/55">
                    {subHeading.desc}
                  </p>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const FeaturesList = [
  {
    title: 'Adaptive Onboarding',
    slug: 'adaptive-onboarding',
    desc: 'Seamlessly onboard customers & partners through KYC verification, and regional mapping for both B2B and B2C segments.',
    icon: Users,
    color: 'bg-[#ff5d22]/5 text-[#ff5d22]'
  },
  {
    title: 'AI-Powered Quote-to-Cash',
    slug: 'ai-powered-quote-to-cash',
    desc: 'Commercially valid and technically feasible quotes with centralized discount governance.',
    icon: Layers,
    color: 'bg-[#ff5d22]/5 text-[#ff5d22]'
  },
  {
    title: 'Digital Order Orchestration',
    slug: 'digital-order-orchestration',
    desc: 'End-to-end automation of the customer lifecycle from Order to Revenue execution.',
    icon: Activity,
    color: 'bg-[#ff5d22]/5 text-[#ff5d22]'
  },
  {
    title: 'Subscription & Billing Automation',
    slug: 'subscription-billing-automation',
    desc: 'Automate recurring billing, usage tracking, invoicing, and revenue workflows across telecom and digital service offerings.',
    icon: Zap,
    color: 'bg-[#ff5d22]/5 text-[#ff5d22]'
  },
  {
    title: 'Revenue & Financial Compliance',
    slug: 'revenue-financial-compliance',
    desc: 'GL mapping, revenue recognition, and TDS/Tax compliance built into every transaction.',
    icon: ShieldCheck,
    color: 'bg-[#ff5d22]/5 text-[#ff5d22]'
  },
  {
    title: 'Partner & Channel Management',
    slug: 'partner-channel-management',
    desc: 'Comprehensive PRM with lifecycle management, commissioning, and deal-flow visibility.',
    icon: Globe,
    color: 'bg-[#ff5d22]/5 text-[#ff5d22]'
  }
];

const TestimonialsList = [
  {
    quote:
      "AppGallop enabled us to unify our direct and indirect cloud commerce operations through a single scalable platform. From partner management to marketplace orchestration and ISV integrations, the team delivered a flexible ecosystem tailored to our multi-channel business model.",
    author: "Jim Bowman",
    role: "Chairman, Velosio",
    avatar: testimonial1
  },
  {
    quote:
      "AppGallop played a key role in scaling our global partner engagement platform across more than 30 countries. Their ability to execute complex integrations, adapt to regional business requirements, and deliver consistently at enterprise scale has been exceptional.",
    author: "Mike Gersten",
    role: "Board Member, SoftwareOne",
    avatar: testimonial3
  },
  {
    quote:
      "The AppGallop platform has helped us streamline high-volume Azure transactions, subscription operations, and customer lifecycle workflows with reliability and precision. What stands out most is the responsiveness of the team and their deep understanding of cloud commerce operations.",
    author: "Tapas G.",
    role: "Head Of Projects, Embee Software",
    avatar: testimonial4
  },
  {
    quote:
      "AppGallop successfully extended its platform capabilities beyond traditional CSP distribution into telecom-grade commerce workflows. Their team delivered specialized integrations across telecom services and cloud provisioning frameworks while maintaining operational scalability.",
    author: "Prayank Chandorkar",
    role: "Tech Lead, JIO Platforms Ltd.",
    avatar: testimonial2
  },
  // {
  //   quote:
  //     "AppGallop helped us strengthen telecom-grade digital commerce operations through scalable workflows, deep integrations, and reliable platform orchestration powering SmartFlo UCaaS/CCaaS services, including advanced CDR processing and enterprise billing operations.",
  //   author: "Abhishek Nawlakhe",
  //   role: "General Manager, Tata Tele Business",
  //   avatar: testimonial5
  // }
];

const TrustedByCarousel = ({ logos }: { logos: Array<{ name: string; logo: string }> }) => {
  return (
    <div className="flex flex-col">
      <p className="text-[9px] font-black uppercase tracking-[0.25em] text-black/35 mb-3">
        Trusted by
      </p>
      <div className="marquee-viewport relative w-full overflow-hidden">

        {/* Left Fade */}
        <div className="absolute left-0 top-0 h-full w-20 bg-gradient-to-r from-white via-white/90 to-transparent z-10 pointer-events-none" />

        {/* Right Fade */}
        <div className="absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-white via-white/90 to-transparent z-10 pointer-events-none" />

        <div className="marquee-track flex items-center gap-4">

          {[...logos, ...logos].map(({ name, logo }, index) => (
            <div
              key={`${name}-${index}`}
              title={name}
              className="
          flex-shrink-0
          w-44
          h-20
          flex
          items-center
          justify-center
          rounded-xl
          border
          border-black/10
          bg-white
          transition-all
          duration-300
          hover:border-slate-300
          hover:shadow-md
        "
            >
              <img
                src={logo}
                alt={name}
                className="
            max-w-[132px]
            max-h-[50px]
            w-auto
            h-auto
            object-contain
            select-none
          "
                draggable={false}
              />
            </div>
          ))}

        </div>
      </div>
    </div>
  );
};

const LogoCloud = () => {
  const brands = [
    { name: 'Embee Software', logo: embeeAvatar },
    { name: 'Jio Telecom', logo: jioAvatar },
    { name: 'Compuage', logo: compuageLogo },    
    { name: 'SoftwareOne', logo: softwareOneAvatar },
    { name: 'Redington', logo: redLogo },
    { name: 'Velosio', logo: velosioAvatar }
  ];

  return (
    <div className="py-6 border-y border-black/5 overflow-hidden bg-slate-50/70 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-4">
        <p className="text-center text-[10px] font-black uppercase tracking-[0.34em] text-black/35">
          Empowering Global Digital Distribution & Monetization
        </p>
      </div>

      <div className="marquee-viewport flex relative overflow-hidden group py-2">
        <div className="absolute inset-y-0 left-0 w-24 md:w-48 bg-gradient-to-r from-slate-50 via-slate-50/90 to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 md:w-48 bg-gradient-to-l from-slate-50 via-slate-50/90 to-transparent z-10 pointer-events-none" />

        <motion.div
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="logo-cloud-track flex gap-8 md:gap-10 items-center whitespace-nowrap min-w-max px-6 md:px-10"
        >
          {[...brands, ...brands].map((brand, i) => (
            <div
              key={`${brand.name}-${i}`}
              className="h-24 w-56 shrink-0 rounded-2xl bg-white border border-black/5 shadow-sm flex items-center justify-center cursor-pointer opacity-75 grayscale-[35%] hover:grayscale-0 hover:opacity-100 transition-all duration-700 hover:scale-105 hover:shadow-xl hover:border-slate-300"
              title={brand.name}
            >
              <img
                src={brand.logo}
                alt={`${brand.name} logo`}
                className="h-14 w-40 object-contain"
              />
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

const FAQItem = ({ faq, isOpen, onClick, index }: { faq: any, isOpen: boolean, onClick: () => void, index: number }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      viewport={{ once: true }}
      className={`group rounded-[2rem] border transition-all duration-[0.6s] overflow-hidden ${isOpen ? 'bg-black border-none ring-1 ring-[#ff5d22]/30 shadow-2xl scale-[1.02]' : 'bg-white border-black/5 hover:border-[#ff5d22]/20'
        }`}
    >
      <button
        onClick={onClick}
        className="w-full p-6 md:p-8 flex items-center justify-between text-left outline-none"
      >
        <span className={`text-base md:text-lg font-black tracking-tight transition-colors duration-500 ${isOpen ? 'text-white' : 'text-black'}`}>
          {faq.q}
        </span>
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-500 ${isOpen ? 'bg-[#ff5d22] text-white rotate-180' : 'bg-slate-100 text-black/20 group-hover:text-[#ff5d22]'
          }`}>
          <ChevronDown size={20} strokeWidth={3} />
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="px-6 md:px-8 pb-8 text-white/70 font-bold text-sm md:text-base leading-relaxed max-w-2xl">
              {faq.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [
    { q: "How secure is the multi-tenant architecture?", a: "Every instance is logically isolated with zero-trust networking. We employ AES-256 encryption at rest and TLS 1.3 in transit, with dedicated SOC-2 compliant clusters for tier-1 carriers." },
    { q: "Does AppGallop support multi-currency billing and taxation?", a: "Yes, our engine handles 160+ currencies with real-time exchange rate normalization. We integrate with major tax engines like Avalara and Vertex for global automated compliance." },
    { q: "What is the typical deployment timeline for legacy migration?", a: "Standard enterprise deployments range from 8 to 14 weeks. Our 'Transition-without-Downtime' protocol ensures legacy stacks remain active while the AppGallop Neural Network synchronizes data in the background." },
    { q: "Can we integrate existing ERP systems (SAP, Oracle, NetSuite)?", a: "Absolutely. AppGallop features a pre-built 'Finance Lattice' that maps digital transactions directly to your GL in major ERP systems with automated reconciliation." },
    { q: "How does the platform handle high-volume CDR mediation?", a: "Our MediateX engine is built on a high-concurrency event-driven architecture, capable of rating and billing millions of records per second with 99.9999% fidelity." }
  ];

  return (
    <section className="py-8 bg-slate-50/50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <NetworkLattice />
      </div>
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-black/5 to-transparent" />

      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        <SectionHeader
          pill="Platform Intelligence"
          title="Predictable Answers."
          subtitle="Clarifying the operational depth of the AppGallop ecosystem for enterprise requirements."
          align="center"
        />

        <div className="mt-10 space-y-4">
          {faqs.map((faq, i) => (
            <FAQItem
              key={i}
              faq={faq}
              index={i}
              isOpen={openIndex === i}
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyAppGallopSection = () => {
    //   <section className="relative overflow-hidden bg-white py-16 md:py-20" id="why-appgallop">
    //   {/* Background Grid */}
    //   <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
    //     <div
    //       className="absolute inset-0"
    //       style={{
    //         backgroundImage: `
    //       linear-gradient(to right, #000 1px, transparent 1px),
    //       linear-gradient(to bottom, #000 1px, transparent 1px)
    //     `,
    //         backgroundSize: "60px 60px",
    //       }}
    //     />
    //   </div>

    //   {/* Animated Connection Lines */}
    //   <div className="absolute inset-0 overflow-hidden pointer-events-none">
    //     <svg
    //       className="absolute inset-0 w-full h-full"
    //       viewBox="0 0 1440 900"
    //       fill="none"
    //       preserveAspectRatio="none"
    //     >
    //       <motion.path
    //         d="M0 220C220 120 320 320 520 240C760 140 900 360 1200 200C1320 140 1440 240 1440 240"
    //         stroke="rgba(227,30,36,0.08)"
    //         strokeWidth="2"
    //         strokeDasharray="8 12"
    //         initial={{ pathLength: 0 }}
    //         whileInView={{ pathLength: 1 }}
    //         transition={{ duration: 3, ease: "easeInOut" }}
    //       />

    //       <motion.path
    //         d="M0 620C200 720 360 500 600 620C820 720 980 480 1440 700"
    //         stroke="rgba(0,0,0,0.05)"
    //         strokeWidth="2"
    //         strokeDasharray="10 16"
    //         initial={{ pathLength: 0 }}
    //         whileInView={{ pathLength: 1 }}
    //         transition={{ duration: 3.5, ease: "easeInOut" }}
    //       />
    //     </svg>
    //   </div>

    //   <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
    //     <SectionHeader
    //       pill="Core Advantage"
    //       title="AppGallop Marketplace Suite."
    //       subtitle="AppGallop unifies partner onboarding, CPQ, product bundling, provisioning, billing, collections, and business operations into a single automation-first platform, helping cloud businesses scale faster, reduce operational overhead, and accelerate revenue realization."
    //     />

    //     {/* TOP CARDS */}
    //     <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
    //       {[
    //         {
    //           title: "Commerce Control Center",
    //           slug: "cloud-channel-commerce",
    //           desc: "Centralize catalogs, reseller ecosystems, subscription operations, and assisted sales journeys through one operational layer with scalable partner commerce automation.",
    //           tags: ["Marketplace", "Partner Ops", "Catalogs"],
    //           icon: LayoutGrid,
    //         },
    //         {
    //           title: "Financial Operations Core",
    //           slug: "financial-operations-core",
    //           desc: "Automate invoicing, settlements, taxation, collections, and ledger synchronization with enterprise-grade financial workflows and compliance-driven revenue governance capabilities.",
    //           tags: ["Billing", "FinOps", "Compliance"],
    //           icon: ShieldCheck,
    //         },
    //         {
    //           title: "Intelligence & Growth Engine",
    //           slug: "intelligence-growth-engine",
    //           desc: "Enable renewals, AI-driven recommendations, ecosystem analytics, and operational visibility with real-time intelligence for proactive business decision-making.",
    //           tags: ["Analytics", "AI Insights", "Renewals"],
    //           icon: Globe,
    //         },
    //       ].map((item, i) => (
    //         <motion.div
    //           key={i}
    //           initial={{ opacity: 0, y: 30 }}
    //           whileInView={{ opacity: 1, y: 0 }}
    //           viewport={{ once: true }}
    //           transition={{ duration: 0.7, delay: i * 0.1 }}
    //           whileHover={{ y: -6 }}
    //           className="group relative rounded-[2rem] border border-black/5 bg-white/70 backdrop-blur-xl p-5 overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_60px_rgba(227,30,36,0.12)] transition-all duration-500"
    //         >
    //           {/* Glow */}
    //           <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
    //             <div className="absolute -top-24 right-0 w-44 h-44 bg-[#ff5d22]/10 blur-3xl rounded-full" />
    //           </div>

    //           {/* Floating Line */}
    //           <motion.div
    //             animate={{ x: ["-100%", "200%"] }}
    //             transition={{
    //               repeat: Infinity,
    //               duration: 4,
    //               ease: "linear",
    //             }}
    //             className="absolute top-0 left-0 w-32 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/50 to-transparent"
    //           />

    //           <div className="relative z-10">
    //             <div className="w-11 h-11 rounded-2xl bg-[#ff5d22] text-white flex items-center justify-center shadow-lg mb-5 group-hover:rotate-[360deg] transition-transform duration-700">
    //               <item.icon size={18} />
    //             </div>

    //             <h3 className="text-[17px] leading-tight font-black tracking-tight text-black mb-2">
    //               {item.title}
    //             </h3>

    //             <p className="text-[11px] leading-relaxed font-semibold text-black/55 mb-4">
    //               {item.desc}
    //             </p>

    //             <div className="flex flex-wrap gap-1.5 mb-4">
    //               {item.tags.map((tag, idx) => (
    //                 <span
    //                   key={idx}
    //                   className="text-[9px] uppercase tracking-[0.15em] font-black px-2.5 py-1 rounded-full border border-[#ff5d22]/15 text-[#ff5d22] bg-[#ff5d22]/[0.03]"
    //                 >
    //                   {tag}
    //                 </span>
    //               ))}
    //             </div>

    //             <Link
    //               to={`/platform/core-advantage#${item.slug}`}
    //               className="inline-flex items-center gap-2 text-[9px] font-black uppercase tracking-[0.25em] text-[#ff5d22] hover:gap-3 transition-all duration-200"
    //             >
    //               Explore
    //               <ArrowRight size={11} />
    //             </Link>
    //           </div>
    //         </motion.div>
    //       ))}
    //     </div>

    //   </div>
    // </section>
};

const WorkflowIntelligenceSection = () => {
  const steps = [
    {
      title: "L2O",
      label: "Lead to Order",
      desc: "Convert qualified leads into structured, order-ready transactions with CPQ, validation, entitlement checks, pricing governance, approval controls, provisioning triggers, service activation, and partner fulfilment orchestration."
    },
    {
      title: "O2C",
      label: "Order to Cash",
      desc: "Streamline billing, invoicing, taxation, collections, payment reconciliation, settlements, revenue workflows, and financial synchronization with enterprise-grade cash flow visibility and compliance-ready controls."
    },
    {
      title: "T2R",
      label: "Ticket to Resolution",
      desc: "Capture, classify, prioritize, route, and resolve service incidents through AI-assisted intake, ITIL-aligned automation, SLA governance, escalations, agentic workflows, audit trails, and continuous resolution visibility."
    }
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [isWorkflowMediaPlaying, setIsWorkflowMediaPlaying] = useState(false);
  const [isWorkflowInView, setIsWorkflowInView] = useState(false);
  const workflowCarouselRef = useRef<HTMLDivElement | null>(null);
  const workflowMediaRef = useRef<HTMLIFrameElement | null>(null);
  const hasWorkflowAutoplayedRef = useRef(false);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setIsWorkflowInView(true);
      hasWorkflowAutoplayedRef.current = true;
      setIsWorkflowMediaPlaying(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isSectionApproaching = entry.isIntersecting;

          if (entry.target === workflowCarouselRef.current) {
            setIsWorkflowInView(isSectionApproaching);

            if (isSectionApproaching && !hasWorkflowAutoplayedRef.current) {
              hasWorkflowAutoplayedRef.current = true;
              setIsWorkflowMediaPlaying(true);
            }
          }
        });
      },
      { rootMargin: "55% 0px", threshold: 0 }
    );

    const workflowElement = workflowCarouselRef.current;

    if (workflowElement) observer.observe(workflowElement);

    return () => {
      if (workflowElement) observer.unobserve(workflowElement);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isWorkflowInView) return undefined;
    let transitionDelay: number | undefined;
    const interval = window.setInterval(() => {
      transitionDelay = window.setTimeout(() => {
        setActiveStep((prev) => (prev + 1) % steps.length);
      }, 300);
    }, 11000);
    return () => {
      window.clearInterval(interval);
      if (transitionDelay) window.clearTimeout(transitionDelay);
    };
  }, [isWorkflowInView, steps.length]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaWindow = workflowMediaRef.current?.contentWindow as
      | (Window & { __appgallopWorkflowPlayback?: { set: (isPlaying: boolean) => void } })
      | null
      | undefined;
    const payload = {
      type: "appgallop-workflow-media",
      action: isWorkflowMediaPlaying ? "play" : "pause",
    };

    mediaWindow?.__appgallopWorkflowPlayback?.set(isWorkflowMediaPlaying);
    mediaWindow?.postMessage(payload, window.location.origin);
  }, [isWorkflowMediaPlaying]);

  return (
    <section ref={workflowCarouselRef} id="solutions" className="relative overflow-hidden bg-slate-950 py-12 text-white sm:py-14 md:py-18 lg:py-20">
      <div className="absolute inset-0 opacity-10">
        <NetworkLattice />
      </div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,93,34,0.18),transparent_38%),radial-gradient(circle_at_bottom_left,rgba(14,165,233,0.1),transparent_34%)]" />
      <motion.div
        animate={{ backgroundPosition: ["0% 0%", "100% 100%"] }}
        transition={{ repeat: Infinity, duration: 18, ease: "linear" }}
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: `
            linear-gradient(
              45deg,
              rgba(255,255,255,0.22) 25%,
              transparent 25%,
              transparent 50%,
              rgba(255,255,255,0.22) 50%,
              rgba(255,255,255,0.22) 75%,
              transparent 75%,
              transparent
            )
          `,
          backgroundSize: "42px 42px",
        }}
      />

      <div className="relative z-20 mx-auto max-w-[1500px] px-4 sm:px-6 md:px-10 xl:px-12">
        <div className="grid grid-cols-1 gap-8 sm:gap-10 lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)] lg:items-center xl:gap-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="min-w-0"
          >
            <SectionHeader
              align="left"
              pill="Workflow Intelligence"
              title={<span className="text-white">Revenue and service workflows at enterprise scale.</span>}
              subtitle={<span className="text-white/70">From partner onboarding and product configuration to invoicing, settlements, service tickets, and resolution visibility, every operational stage flows through one connected ecosystem.</span>}
              className="max-w-none [&>h2]:!text-[2.25rem] [&>h2]:!leading-[1.02] sm:[&>h2]:!text-5xl lg:[&>h2]:!text-[3.4rem] xl:[&>h2]:!text-6xl [&>p]:max-w-2xl"
            />

            <div className="mt-6 max-w-3xl lg:max-w-none">
              <AnimatePresence mode="wait">
                <motion.div
                  key={steps[activeStep].title}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.5 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-xl sm:p-5"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <motion.div
                      animate={{
                        boxShadow: [
                          "0 0 0 0 rgba(255,93,34,0.38)",
                          "0 0 0 12px rgba(255,93,34,0)",
                        ],
                      }}
                      transition={{ repeat: Infinity, duration: 2.5 }}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#ff5d22]/20 bg-[#ff5d22]/15 text-[10px] font-black tracking-widest text-[#ff5d22] sm:h-12 sm:w-12"
                    >
                      {steps[activeStep].title}
                    </motion.div>

                    <div>
                      <div className="mb-1 text-[10px] font-black uppercase tracking-[0.25em] text-white">
                        {steps[activeStep].label}
                      </div>
                      <div className="text-[12px] font-semibold leading-relaxed text-white/65">
                        {steps[activeStep].desc}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="mt-4 flex items-center gap-2">
                {steps.map((step, i) => (
                  <button
                    key={step.title}
                    type="button"
                    onClick={() => setActiveStep(i)}
                    aria-label={`Show ${step.label}`}
                    className={`h-1 rounded-full transition-all duration-500 ${
                      activeStep === i ? "w-8 bg-[#ff5d22]" : "w-2 bg-white/20"
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="relative min-w-0 lg:scale-[1.03] lg:origin-center xl:scale-[1.06]"
          >
            <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0B1220] shadow-2xl sm:rounded-[2rem] lg:rounded-[2.5rem]">
              <motion.div
                animate={{ x: ["-100%", "100%"] }}
                transition={{ repeat: Infinity, duration: 5, ease: "linear" }}
                className="absolute left-0 top-0 h-px w-40 bg-gradient-to-r from-transparent via-[#ff5d22] to-transparent"
              />

              <div className="relative aspect-[16/10] min-h-[250px] overflow-hidden bg-[#0B1220] sm:aspect-[16/9] sm:min-h-[340px] lg:min-h-[430px] xl:min-h-[500px]">
                <iframe
                  ref={workflowMediaRef}
                  src="/lead-to-order.html"
                  title="Lead to Order"
                  className="absolute inset-0 z-0 h-full w-full scale-[1.08] border-0 sm:scale-[1.06] lg:scale-[1.1]"
                  loading="lazy"
                  scrolling="no"
                  allow="autoplay"
                  onLoad={() => {
                    if (typeof window === "undefined") return;
                    const mediaWindow = workflowMediaRef.current?.contentWindow as
                      | (Window & { __appgallopWorkflowPlayback?: { set: (isPlaying: boolean) => void } })
                      | null
                      | undefined;
                    const payload = {
                      type: "appgallop-workflow-media",
                      action: isWorkflowMediaPlaying ? "play" : "pause",
                    };

                    mediaWindow?.__appgallopWorkflowPlayback?.set(isWorkflowMediaPlaying);
                    mediaWindow?.postMessage(payload, window.location.origin);
                  }}
                />

                <div className="pointer-events-none absolute right-4 top-4 h-20 w-20 rounded-full bg-[#ff5d22]/20 blur-3xl" />
              </div>
            </div>

            <div className="pointer-events-none absolute right-4 top-4 z-[100] flex items-center gap-2">
              <motion.button
                data-testid="workflow-media-toggle"
                type="button"
                whileTap={{ scale: 0.92 }}
                whileHover={{ scale: 1.04 }}
                onClick={() => setIsWorkflowMediaPlaying((prev) => !prev)}
                className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-[#0b1220]/90 text-white shadow-[0_10px_30px_rgba(0,0,0,0.45)] backdrop-blur-md transition-colors hover:bg-[#ff5d22] hover:text-white"
                aria-label={isWorkflowMediaPlaying ? "Pause workflow media" : "Play workflow media"}
                title={isWorkflowMediaPlaying ? "Pause" : "Play"}
              >
                {isWorkflowMediaPlaying ? <Pause size={14} /> : <Play size={14} />}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// --- Pages ---

type HeroTheme = {
  accentText: string;
  accentBg: string;
  accentBorder: string;
  glowBg: string;
  imageOverlay: string;
};

const heroThemes: Record<string, HeroTheme> = {
  marketplace: {
    accentText: "text-[#ff5d22]",
    accentBg: "bg-[#ff5d22]/15",
    accentBorder: "border-[#ff5d22]/30",
    glowBg: "bg-[#ff5d22]/30",
    imageOverlay: "from-[#ff5d22]/30 via-black/60 to-black",
  },
  revsimply: {
    accentText: "text-[#ff5d22]",
    accentBg: "bg-[#ff5d22]/15",
    accentBorder: "border-[#ff5d22]/30",
    glowBg: "bg-[#ff5d22]/30",
    imageOverlay: "from-[#ff5d22]/30 via-black/60 to-black",
  },
  cpq: {
    accentText: "text-[#ff5d22]",
    accentBg: "bg-[#ff5d22]/15",
    accentBorder: "border-[#ff5d22]/30",
    glowBg: "bg-[#ff5d22]/30",
    imageOverlay: "from-[#ff5d22]/30 via-black/60 to-black",
  },
  rms: {
    accentText: "text-[#ff5d22]",
    accentBg: "bg-[#ff5d22]/15",
    accentBorder: "border-[#ff5d22]/30",
    glowBg: "bg-[#ff5d22]/30",
    imageOverlay: "from-[#ff5d22]/30 via-black/60 to-black",
  },
  "uc-billing": {
    accentText: "text-[#ff5d22]",
    accentBg: "bg-[#ff5d22]/15",
    accentBorder: "border-[#ff5d22]/30",
    glowBg: "bg-[#ff5d22]/30",
    imageOverlay: "from-[#ff5d22]/30 via-black/60 to-black",
  },
};

const productData: Record<string, any> = {
  marketplace: {
    title: "Digital Commerce & Monetization Platform",
    subtitle: "The operating layer for unified digital service commerce.",
    desc: "AppGallop helps service providers commercialize, sell, provision, bill, collect, recognize, and renew digital services across direct, partner, and multi-tier channels from one enterprise platform.",
    hero: {
      pill: "Digital Commerce & Monetization",
      title: "Commercialize digital services",
      tagline: "from catalog to renewals",
      description: "Empower teams to manage catalogs, pricing, campaigns, onboarding, provisioning, billing, collections, and renewals through one unified operational platform.",
      primaryCta: { label: "Request Platform Demo", href: "/contact" },
      secondaryCta: { label: "View Integrations", href: "/platform/integrations" },
    },
    metrics: [
      { label: "Partner Reach", value: "50K+", desc: "Direct integration with the world's leading digital service distributors." },
      { label: "Provisioning Speed", value: "Sub-Second", desc: "Automated service activation across multi-cloud environments." },
      { label: "Transaction Scale", value: "$150M+", desc: "Yearly volume managed through our proprietary commerce lattice." }
    ],
    modules: [
      { title: "Universal Catalog", desc: "Consolidate products from any vendor into a unified, branded digital storefront.", icon: LayoutGrid },
      { title: "Partner PRM", desc: "Orchestrate multi-tier resale channels with automated commission and payout logic.", icon: Users },
      { title: "Global Settlement", desc: "Automated multi-currency financial reconciliation and tax governance.", icon: Coins }
    ],
    capabilities: [
      "Multi-Tier Reseller Orchestration",
      "Automated Vendor Catalog Sync",
      "Global Taxation & Compliance Logic",
      "Real-time Provisioning Mediation"
    ],
    image: marketplaceHomepageImage
  },
  revsimply: {
    title: "AppGallop CPQ",
    subtitle: "AI-Powered Quote-to-Cash Revolution",
    desc: "Transform your revenue operations with intelligent CPQ automation, predictive analytics, and seamless deal orchestration. Built for enterprise scale with neural pricing optimization and real-time margin intelligence.",
    hero: {
      pill: "CPQ & Quoting",
      title: "AppGallop CPQ",
      tagline: "AI-Powered Quote-to-Cash Revolution",
      description: "Transform complex sales processes with intelligent automation, predictive pricing, and seamless deal orchestration designed for enterprise scale.",
      primaryCta: { label: "Book CPQ Demo", href: "/contact" },
      secondaryCta: { label: "Explore AI Features", href: "/solutions/revenue-engineering" },
    },
    metrics: [
      { label: "Quote Generation Speed", value: "10x Faster", desc: "AI-powered configuration reduces quote time from hours to minutes with intelligent product bundling." },
      { label: "Revenue Leakage Prevention", value: "Zero Tolerance", desc: "Real-time margin validation and automated compliance ensure every quote is profitable and compliant." },
      { label: "Approval Cycle Reduction", value: "-75%", desc: "Smart approval workflows with risk-based routing eliminate bottlenecks and accelerate deal closure." }
    ],
    modules: [
      { title: "Neural CPQ Engine", desc: "AI-driven product configuration with intelligent bundling, real-time pricing optimization, and margin validation.", icon: Zap },
      { title: "Dynamic Pricing Intelligence", desc: "Machine learning algorithms optimize pricing strategies, identify upsell opportunities, and protect deal margins in real time.", icon: Bot },
      { title: "Automated Revenue Recognition", desc: "FASB/IFRS compliant revenue scheduling with automated journal entries and real-time financial reporting.", icon: FileCheck },
      { title: "Smart Contract Orchestration", desc: "Intelligent contract generation with automated billing setup, compliance validation, and e-signature integration.", icon: FileText },
      { title: "Integration Ecosystem", desc: "Seamless connectivity with CRM, ERP, and financial systems through API-first architecture and real-time synchronization.", icon: Network }
    ],
    capabilities: [
      "AI-Powered Product Configuration & Bundling",
      "Real-Time Margin Analysis & Optimization",
      "Automated Compliance & Revenue Recognition",
      "Smart Approval Workflows & Risk Assessment",
      "Enterprise CRM & ERP Integration",
      "Advanced Analytics & Revenue Intelligence",
      "Multi-Currency & Global Tax Calculation",
      "Contract-to-Ledger Synchronization"
    ],
    image: cpqHomepageImage
  },
  "uc-billing": {
    title: "UC Billing & Mediation",
    subtitle: "Breaking the bottlenecks between legacy infrastructure and cloud-native scale.",
    desc: "A unified telecom commerce platform designed to help communication service providers launch faster, scale globally, and deliver seamless customer experiences. We handle the extreme complexity of UCaaS, CCaaS, and CPaaS environments with carrier-grade integrity.",
    hero: {
      pill: "UC Billing & Mediation",
      title: "All in One Platform",
      tagline: "Breaking the bottlenecks between legacy infrastructure and cloud-native scale.",
      description: "A unified telecom commerce platform designed to help communication service providers launch faster, scale globally, and deliver seamless customer experiences.",
      primaryCta: { label: "Schedule Billing Workshop", href: "/contact" },
      secondaryCta: { label: "View Integrations", href: "/platform/integrations" },
    },
    metrics: [
      { label: "Mediation Volume", value: "Millions/Day", desc: "Successfully processing extreme transaction volumes with zero record loss." },
      { label: "Billing Accuracy", value: "99.999%", desc: "Eliminating reconciliation gaps in high-volume usage environments." },
      { label: "Compliance Scope", value: "160+ Countries", desc: "Native tax and regulatory governance at global scale." }
    ],
    modules: [
      { title: "Atomic Mediation", desc: "High-concurrency ingestion engine for massive CDR and usage payloads.", icon: Cpu },
      { title: "Rating Intelligence", desc: "Nested pricing models that adapt to complex international roaming and usage.", icon: Database },
      { title: "Invoice Automation", desc: "Dynamic, multi-language invoicing at planetary scale with zero manual touch.", icon: MessageSquare }
    ],
    capabilities: [
      "Carrier-Grade CDR Mediation Layer",
      "Hyperscale Rating & Rating-Logic",
      "Automated Multi-Region Fiscal Sync",
      "Real-time Usage Visibility Portals"
    ],
    image: unifiedBillingHomepageImage
  },
  rms: {
    title: "RevSimply RMS",
    subtitle: "Your Renewal Command Center",
    desc: "A modern revenue orchestration platform designed to help enterprises automate renewals, predict churn, and unlock predictable recurring growth with AI-powered intelligence.",
    hero: {
      pill: "Renewal Management",
      title: "RevSimply RMS",
      tagline: "Your Renewal Command Center",
      description: "Automate renewals, predict churn before it happens, and drive predictable recurring revenue with intelligent lifecycle orchestration built for enterprise scale.",
      primaryCta: { label: "Book RMS Demo", href: "/contact" },
      secondaryCta: { label: "Explore Platform", href: "/products/marketplace" },
    },
    metrics: [
      { label: "Renewal Rate Improvement", value: "+35%", desc: "Proactive customer management and automated outreach drive higher renewal rates." },
      { label: "Process Efficiency", value: "85% Faster", desc: "Automated workflows eliminate manual renewal processing and reduce operational overhead." },
      { label: "Forecast Accuracy", value: "+40%", desc: "Predictive analytics deliver accurate revenue forecasting and churn early-warning signals." }
    ],
    modules: [
      { title: "Renewal Intelligence Hub", desc: "Complete bird's-eye view of your renewal pipeline with health scoring, risk flags, and timeline tracking.", icon: BarChart3 },
      { title: "AI-Powered Churn Prediction", desc: "Machine learning identifies at-risk accounts early and recommends targeted intervention strategies.", icon: Bot },
      { title: "Automated Renewal Workflows", desc: "Streamlined processes for renewal notifications, approval chains, and contract generation with zero manual touch.", icon: RefreshCw }
    ],
    capabilities: [
      "Centralized Renewal Visibility & Tracking",
      "AI-Driven Churn Prediction & Early Warning",
      "Smart Workflow Automation & Notifications",
      "Real-Time Customer Health Monitoring",
      "Predictive Revenue Forecasting",
      "CRM & Customer Success Integration",
      "Revenue Leakage Prevention",
      "Enterprise-Grade Security & Compliance"
    ],
    image: revenueEngineImage
  }
};

const ProductHero = ({ data, theme }: { data: any; theme: HeroTheme }) => {
  const kpis = (data.metrics || []).slice(0, 3);

  return (
    <section className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-slate-50 via-white to-slate-50 text-black px-6 py-10 md:px-10 md:py-12 border border-black/8 shadow-lg">
      <div className="absolute inset-0 pointer-events-none">
        <div className={`hidden sm:block absolute -top-12 -right-4 sm:h-[160px] sm:w-[160px] md:h-[240px] md:w-[240px] rounded-full blur-[80px] ${theme.glowBg} opacity-60`} />
        <div className="hidden sm:block absolute -bottom-16 left-[-6%] sm:h-[180px] sm:w-[180px] md:h-[280px] md:w-[280px] rounded-full blur-[100px] bg-[#ff5d22]/8" />
        <div className="absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,rgba(0,0,0,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.08)_1px,transparent_1px)] bg-[size:32px_32px]" />

        {/* Floating geometric shapes for visual interest */}
        <div className="absolute top-1/4 right-1/4 w-16 h-16 rounded-full bg-[#ff5d22]/5 blur-sm animate-pulse" />
        <div className="absolute bottom-1/3 left-1/5 w-12 h-12 rounded-lg bg-[#ff5d22]/8 blur-sm animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative z-10 grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-center">
        <div className="space-y-6">
          <div className="space-y-4">
            <div className={`inline-flex items-center gap-2.5 rounded-full px-4 py-2 text-[10px] font-black uppercase tracking-[0.15em] border-2 ${theme.accentBg} ${theme.accentText} ${theme.accentBorder} shadow-sm`}>
              <span className={`h-1.5 w-1.5 rounded-full ${theme.accentText.replace("text-", "bg-")} animate-pulse`} />
              {data.hero?.pill || "AppGallop Platform"}
            </div>

            <div className="space-y-3">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[0.9] text-black">
                {data.hero?.title || data.title}
                <span className={`block ${theme.accentText} mt-1`}>
                  {data.hero?.tagline || data.subtitle}
                </span>
              </h1>
              <p className="text-black/65 text-sm md:text-base font-medium leading-relaxed max-w-xl">
                {data.hero?.description || data.desc}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to={data.hero?.primaryCta?.href || "/contact"}>
              <Button variant="primary" className="!px-8 !py-3 !text-[10px] !tracking-[0.15em] !font-black shadow-lg hover:shadow-xl transition-all duration-300">
                {data.hero?.primaryCta?.label || "Request a Demo"} <ArrowRight size={13} />
              </Button>
            </Link>
            <Link to={data.hero?.secondaryCta?.href || "/platform/integrations"}>
            </Link>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 pt-4">
            {kpis.map((kpi: any, i: number) => (
              <div key={i} className="group rounded-2xl border border-black/8 bg-white/80 backdrop-blur-sm p-4 hover:shadow-md hover:border-[#ff5d22]/20 transition-all duration-300 cursor-pointer">
                <div className="text-xl font-black text-[#000] group-hover:scale-105 transition-transform duration-300">{kpi.value}</div>
                <div className="text-[9px] font-black uppercase tracking-widest text-black/50 mt-1.5">{kpi.label}</div>
                <div className="text-[10px] font-medium text-black/40 mt-1 leading-relaxed">{kpi.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative group">
          <div className="relative rounded-[2.5rem] overflow-hidden border border-black/8 bg-white/90 backdrop-blur-sm shadow-xl group-hover:shadow-2xl transition-all duration-500">
            <img
              src={data.image}
              className="w-full h-full object-fill aspect-[4/3] group-hover:scale-105 transition-transform duration-700"
              alt={data.title}
              referrerPolicy="no-referrer"
            />
            <div className={`absolute inset-0 bg-gradient-to-tr ${theme.imageOverlay} from-black/10 via-transparent to-[#ff5d22]/10`} />
          </div>

          {/* Enhanced floating cards with better positioning */}
          <div className="absolute -bottom-6 left-6 right-6 rounded-2xl border border-black/10 bg-white/95 backdrop-blur-xl px-5 py-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-black/50 font-black mb-1">Live Signal</div>
                <div className="flex items-center gap-2 text-[11px] font-semibold text-black/80">
                  <span className={`h-2 w-2 rounded-full ${theme.accentBg} animate-pulse`} />
                  Orchestration mesh active
                </div>
              </div>
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-[0.15em] text-black/50 font-black">Status</div>
                <div className="text-[11px] font-black text-emerald-600">Online</div>
              </div>
            </div>
          </div>

          <div className="absolute -top-8 right-8 rounded-2xl border border-black/10 bg-white/95 backdrop-blur-xl px-5 py-4 shadow-lg">
            <div className="text-center">
              <div className="text-[9px] uppercase tracking-[0.2em] text-black/50 font-black mb-1">Stack Focus</div>
              <div className={`text-sm font-black ${theme.accentText} !text-[#ff5d22]`}>
                {data.hero?.pill || data.title}
              </div>
            </div>
          </div>

          {/* Additional visual enhancement - floating metrics */}
          <div className="absolute top-1/4 -left-4 rounded-xl border border-black/8 bg-white/90 backdrop-blur-xl px-3 py-2 shadow-md">
            <div className="text-[8px] uppercase tracking-widest text-black/40 font-black">Performance</div>
            <div className="text-xs font-black text-[#2563EB]">99.9%</div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HomePage = ({ onDemoRequest }: { onDemoRequest: () => void }) => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeBottleneck, setActiveBottleneck] = useState(0);
  const containerRef = useRef(null);
  const { scrollY } = useScroll();

  const yParallax = useTransform(scrollY, [2000, 4000], [0, 50]);
  const statsBgParallax = useTransform(scrollY, [3500, 5500], [0, 50]);

  const activeBottleneckData = bottleneckSolutions[activeBottleneck];
  const ActiveBottleneckIcon = activeBottleneckData.icon;

  return (
    <div ref={containerRef}>
      {/* Hero Section */}
// ─── Hero Section ─────────────────────────────────────────────────────────────
      <section className="relative overflow-hidden bg-white pt-24 pb-14 md:pt-28 md:pb-18">

        {/* Background Animations */}
        <div className="absolute inset-0 overflow-hidden">
          <NetworkLattice />

          <div className="absolute inset-0">
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 1200 800"
              preserveAspectRatio="xMidYMid slice"
            >
              <defs>
                <linearGradient id="heroGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#0F172A" stopOpacity="0.03" />
                  <stop offset="100%" stopColor="#334155" stopOpacity="0.08" />
                </linearGradient>

                <radialGradient id="heroGrad2" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#334155" stopOpacity="0.05" />
                  <stop offset="100%" stopColor="#334155" stopOpacity="0" />
                </radialGradient>
              </defs>

              <motion.path
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 3, ease: "easeInOut" }}
                d="M0,400 Q300,200 600,400 T1200,400"
                stroke="url(#heroGrad1)"
                strokeWidth="2"
                fill="none"
              />

              <motion.circle
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 2, delay: 1 }}
                cx="600"
                cy="400"
                r="200"
                fill="url(#heroGrad2)"
              />
            </svg>
          </div>

          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-black/10 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-[minmax(0,1.02fr)_minmax(360px,0.98fr)] gap-10 xl:gap-14 items-center min-w-0">

            {/* ───────────────── LEFT ───────────────── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="min-w-0 w-full max-w-[calc(100vw-2rem)] sm:max-w-none"
              style={{ maxWidth: "min(100%, calc(100vw - 2rem))" }}
            >
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/5 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-[#ff5d22]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#ff5d22]" />
                Enterprise Revenue Platform
              </div>

              <h1 className="mb-5 max-w-4xl break-words text-[2.1rem] sm:text-[2.75rem] md:text-[3.35rem] lg:text-[4rem] font-black leading-[1.08] md:leading-[1.02] tracking-tight text-black">
                Monetize{" "}
                <span className="text-[#ff5d22]">Cloud, Telecom & AI Services</span>{" "}
                at Scale
              </h1>

              <p className="mb-7 max-w-2xl text-base font-semibold leading-relaxed text-[#334155]">
                Automate catalog, provisioning, subscriptions, billing, partner ecosystems and revenue operations from a single platform.
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {[
                  "Multi-cloud orchestration",
                  "AI automation",
                  "Global compliance",
                ].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-[#F8FAFC] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-[#334155]"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#ff5d22]/50" />
                    {tag}
                  </span>
                ))}
              </div>

              {/* Trusted By */}
              <TrustedByCarousel
                logos={[
                  { name: "TataTele", logo: tatalogo },
                  { name: "Jio Telecom", logo: jioAvatar },
                  { name: "SoftwareOne", logo: softwareOneAvatar },
                  { name: "Embee Software", logo: embeeAvatar },
                  { name: "Compuage", logo: compuageLogo },
                  { name: "Redington", logo: redLogo },
                  { name: "Velosio", logo: velosioAvatar },
                ]}
              />

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="flex flex-col sm:flex-row flex-wrap gap-3 items-stretch sm:items-center mt-5"
              >
                <Button
                  variant="primary"
                  onClick={onDemoRequest}
                  className="w-full sm:w-auto !px-6 !py-2 !text-[11px] font-black uppercase tracking-wider"
                >
                  Request Demo
                </Button>

                <Link to="/products/marketplace" className="w-full sm:w-auto">
                  <Button
                    variant="outline"
                    className="hero-platform-button w-full sm:w-auto !px-6 !py-2 !text-[11px] font-black uppercase tracking-wider !border-black/20 hover:!border-[#ff5d22] hover:!text-[#ff5d22]"
                  >
                    ▷ &nbsp;
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            {/* ───────────────── RIGHT ───────────────── */}
            <div className="relative pt-8 md:pt-10 pr-0 md:pr-6 pl-0 sm:pl-2 pb-2">

              {/* Active Partners */}
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{
                  duration: 4.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="hidden sm:block absolute z-30 w-44 rounded-lg border border-black/8 bg-white/90 px-3 py-2.5 shadow-lg backdrop-blur-xl"
                style={{
                  top: "0px",
                  right: "0px",
                }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-5 h-5 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                    <Users size={11} className="text-[#0F172A]" />
                  </div>

                  <span className="text-[9px] font-bold text-black/50">
                    Active Partners
                  </span>
                </div>

                <div className="text-[18px] font-black text-black leading-none">
                  1,284
                </div>

                <span className="inline-block mt-1 text-[8px] bg-emerald-50 text-emerald-700 rounded-full px-1.5 py-px font-bold">
                  +12% this month
                </span>
              </motion.div>

              {/* Revenue Growth */}
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{
                  delay: 0.5,
                  duration: 4.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="hidden sm:block absolute z-30 w-44 rounded-lg border border-black/8 bg-white/90 px-3 py-2.5 shadow-lg backdrop-blur-xl"
                style={{
                  top: "42%",
                  left: "-12px",
                  willChange: "transform",
                }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-5 h-5 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                    <BarChart3 size={11} className="text-[#2563EB]" />
                  </div>

                  <span className="text-[9px] font-bold text-black/50">
                    Revenue Growth
                  </span>
                </div>

                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1">
                    <div className="w-14 h-14 rounded-full border-2 border-blue-200 relative flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full border-[3px] border-[#2563EB] border-t-transparent animate-spin" />
                      <div className="absolute text-[15px] font-black text-[#2563EB]">24%</div>
                    </div>
                    <div className="text-[10px] font-semibold text-black/60">Growth Rate</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] font-black text-[#2563EB]">+$2.4M</div>
                    <div className="text-[7px] text-black/40">This Month</div>
                  </div>
                </div>

                <span className="text-[8px] text-black/40 font-medium">
                  Last 8 weeks -{" "}
                  <span className="text-emerald-600 font-bold">
                    +24.8%
                  </span>
                </span>
              </motion.div>

              {/* SLA Compliance */}
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{
                  duration: 4.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="hidden sm:block absolute z-30 w-32 rounded-lg border border-black/8 bg-white/90 px-2.5 py-2 shadow-lg backdrop-blur-xl"
                style={{
                  top: "45%",
                  right: "0px",
                  willChange: "transform",
                }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-5 h-5 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                    <Zap size={11} className="text-[#0F172A]" />
                  </div>

                  <span className="text-[9px] font-bold text-black/50">
                    SLA Compliance
                  </span>
                </div>

                <div className="text-[13px] font-black text-black leading-none">
                  98.6%
                </div>

                <span className="inline-block mt-1 text-[7px] bg-emerald-50 text-emerald-700 rounded-full px-1.5 py-px font-bold">
                  Zero-touch
                </span>
              </motion.div>          

              {/* Main Dashboard */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.7 }}
                className="relative z-10 overflow-hidden rounded-lg border border-black/8 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.12)]"
              >

                {/* Browser chrome */}
                <div className="flex items-center gap-1.5 px-3 py-2 border-b border-black/6 bg-slate-50">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/70" />

                  <div className="flex-1 mx-2 px-2 py-0.5 rounded bg-white border border-black/6 text-[9px] text-black/35 font-mono">
                    app.appgallop.com / dashboard
                  </div>

                  <RefreshCcw size={10} className="text-black/30" />
                </div>

                {/* Nav + content */}
                <div className="grid grid-cols-1 sm:grid-cols-[88px_1fr] sm:divide-x divide-black/6">

                  {/* Sidebar */}
                  <div className="hidden sm:flex flex-col gap-0.5 p-2">
                    {[
                      { icon: LayoutGrid, label: "Overview", active: true },
                      { icon: Users, label: "Partners" },
                      { icon: FileText, label: "Billing" },
                      { icon: BarChart3, label: "Analytics" },
                      { icon: Settings, label: "Settings" },
                    ].map(({ icon: Icon, label, active }) => (
                      <div
                        key={label}
                        className={`flex items-center gap-1.5 rounded px-2 py-1 text-[9px] font-semibold ${
                          active ? "bg-blue-50 text-[#2563EB]" : "text-black/40"
                        }`}
                      >
                        <Icon size={11} />
                        {label}
                      </div>
                    ))}
                  </div>

                  {/* Content */}
                  <div className="p-2.5 space-y-2">

                    {/* KPIs */}
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        {
                          val: "$12.4M",
                          label: "MRR",
                          badge: "+24.8%",
                          color: "text-[#2563EB]"
                        },
                        {
                          val: "1230",
                          label: "Invoices Generated",
                          badge: "+17% MOM",
                          color: "text-emerald-700"
                        },
                        {
                          val: "$3.2M",
                          label: "Renewal Pipeline",
                          badge: "+5% vs last month",
                          color: "text-[#334155]"
                        },
                      ].map(({ val, label, badge, color }) => (
                        <div key={label} className="bg-slate-50 rounded-lg p-2">
                          <div className={`text-[13px] font-black ${color}`}>
                            {val}
                          </div>

                          <div className="text-[7px] uppercase tracking-widest text-black/40 mt-0.5">
                            {label}
                          </div>

                          <span className="inline-block mt-0.5 text-[7px] bg-emerald-50 text-emerald-700 rounded-full px-1.5 py-px font-bold">
                            {badge}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Mini chart */}
                    <div className="bg-slate-50 rounded-lg p-2">
                      <div className="text-[7px] uppercase tracking-widest text-black/35 mb-1">
                        Revenue pipeline - last 8 weeks
                      </div>

                      <div className="flex items-end gap-0.5 h-6">
                        {[38, 52, 44, 61, 55, 72, 84, 100].map((h, i) => (
                          <div
                            key={i}
                            className={`flex-1 rounded-sm ${i >= 6
                              ? "bg-[#2563EB]"
                              : "bg-blue-200"
                              }`}
                            style={{
                              height: `${h}%`,
                              transition: "none",
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Orders */}
                    <div className="space-y-1">
                      {[
                        {
                          id: "SKU-M365",
                          name: "Microsoft 365 Business Premium",
                          status: "Provisioned",
                          cls: "text-emerald-700 bg-emerald-50",
                        },
                        {
                          id: "SKU-AZ-EA",
                          name: "Azure Enterprise Agreement",
                          status: "Processing",
                          cls: "text-amber-700 bg-amber-50",
                        },
                      ].map(({ id, name, status, cls }) => (
                        <div
                          key={id}
                          className="flex items-center justify-between px-2 py-1 bg-slate-50 rounded text-[9px] gap-2"
                        >
                          <span className="text-black/35 font-mono shrink-0">
                            {id}
                          </span>

                          <span className="text-black/70 flex-1 truncate">
                            {name}
                          </span>

                          <span
                            className={`px-1.5 py-px rounded-full font-bold text-[8px] shrink-0 ${cls}`}
                          >
                            {status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 border-t border-black/6 bg-white">
                  <div className="flex items-center gap-1.5 text-[9px] text-black/45">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Orchestration mesh active
                  </div>

                  <span className="text-[8px] text-black/30">
                    Live - 128 services online
                  </span>
                </div>
              </motion.div>

              {/* Stats Grid - positioned below dashboard */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="mt-4 grid grid-cols-2 gap-3"
              >
                {[
                  { val: "1M+", label: "Transactions processed monthly" },
                  { val: "$150M+", label: "Revenue managed annually" },
                  { val: "50K+", label: "Channel partners activated" },
                  { val: "24x7", label: "Automated revenue operations" },
                ].map(({ val, label }, i) => (
                  <div
                    key={label}
                    className="rounded-lg border border-black/6 bg-white px-4 py-3 shadow-sm transition-colors hover:bg-slate-50"
                  >
                    <div className="text-lg font-black text-[#000] tracking-tight">
                      {val}
                    </div>
                    <div className="text-[9px] text-black/45 font-medium mt-1 leading-tight">
                      {label}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>

        </div>
      </section>

      <section className="relative overflow-hidden bg-slate-950 py-8 text-white md:py-10">
  {/* Animated Background */}
  <div className="pointer-events-none absolute inset-0">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,93,34,0.12),transparent_30%),radial-gradient(circle_at_15%_85%,rgba(14,165,233,0.08),transparent_32%)]" />
    
    <motion.div
      animate={{ x: ["-20%", "20%", "-20%"] }}
      transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      className="absolute inset-y-0 left-0 w-1/2 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.04),transparent)]"
    />

    <div className="absolute inset-0 opacity-[0.055] bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:44px_44px]" />
  </div>

  <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
    <div className="grid gap-5 rounded-xl border border-white/10 bg-white/[0.045] p-4 shadow-xl shadow-black/30 backdrop-blur-xl md:p-5 lg:grid-cols-[0.7fr_1.3fr] lg:items-center">
      <div>
        <div className="inline-flex items-center rounded-full border border-[#ff5d22]/25 bg-[#ff5d22]/10 px-3 py-1 text-[8px] font-black uppercase tracking-[0.18em] text-[#ff8a5c]">
          Trust Signals
        </div>

        <h2 className="mt-3 text-xl font-black tracking-tight text-white md:text-2xl">
          Validated for enterprise confidence.
        </h2>

        <p className="mt-2 max-w-xl text-xs font-bold leading-relaxed text-white/60">
          Independent certifications, compliance standards, and industry validations that reinforce trust across procurement, security, governance, and financial operations.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          { name: "SOC 1 Type II", logo: soc1Type2Logo },
          { name: "SOC 2 Type II", logo: soc2Type2Logo },
          { name: "IDC", logo: idcLogo },
          { name: "ISO", logo: isoLogo }
        ].map((item) => (
          <div
            key={item.name}
            className="group rounded-lg border border-white/10 bg-white px-3 py-3 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-[#ff5d22]/35 hover:shadow-lg hover:shadow-[#ff5d22]/10"
          >
            <div className="flex h-11 items-center justify-center rounded-md bg-slate-50">
              <img
                src={item.logo}
                alt={item.name}
                className="max-h-12 w-auto object-contain"
              />
            </div>

            <div className="mt-2 text-center text-[9px] font-black uppercase tracking-[0.14em] leading-relaxed text-black/45 group-hover:text-[#ff5d22]">
              {item.name}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
</section>

      <section id="operational-transformation" className="relative overflow-hidden bg-white py-16 text-slate-950 md:py-20">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_82%_18%,rgba(255,93,34,0.10),transparent_30%),radial-gradient(circle_at_16%_82%,rgba(14,165,233,0.08),transparent_26%)]" />
          <div className="absolute inset-0 opacity-[0.34] bg-[linear-gradient(to_right,rgba(15,23,42,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.05)_1px,transparent_1px)] bg-[size:44px_44px]" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeader
            align="left"
            pill="Operational Transformation"
            title={<>Still Losing Revenue to <span className="text-[#ff5d22]">Disconnected Cloud Operations?</span></>}
            subtitle="Disconnected partner onboarding, quoting, product bundling, provisioning, billing, and collections create slow handoffs, manual rework, missed charges, delayed cash flow, and revenue leakage that becomes harder to control as cloud operations scale."
            className="mb-10 [&>span]:!mb-3 [&>h2]:!mb-3 [&>h2]:!text-3xl md:[&>h2]:!text-4xl [&>p]:!text-sm [&>p]:!max-w-2xl"
          />

          <div className="grid gap-6 lg:grid-cols-[minmax(300px,0.72fr)_minmax(0,1.28fr)] lg:items-start lg:gap-8">
            <motion.div
              initial={{ opacity: 1, x: 0 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: 0.06 }}
              className="rounded-lg border border-black/8 bg-white p-4 shadow-sm"
            >
              <div className="border-b border-black/10 pb-3">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#ff5d22]">Common growth blockers</p>
                <h3 className="mt-1 hidden text-lg font-black tracking-tight text-slate-950 lg:block">Pick the area slowing the team down</h3>
              </div>

              <div className="space-y-2">
                {bottleneckSolutions.map((item, index) => {
                  const Icon = item.icon;
                  const isActive = index === activeBottleneck;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => setActiveBottleneck(index)}
                      className={`group grid w-full grid-cols-[34px_minmax(0,1fr)] items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-all lg:grid-cols-[34px_minmax(0,1fr)_16px] ${
                        isActive
                          ? "border-[#ff5d22]/45 bg-[#ff5d22]/10 text-slate-950 shadow-[0_16px_32px_rgba(255,93,34,0.12)]"
                          : "border-black/6 bg-slate-50 text-slate-700 hover:border-sky-300/55 hover:bg-white"
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 items-center justify-center rounded-lg border ${
                          isActive
                            ? "border-[#ff5d22]/35 bg-[#ff5d22]/12 text-[#ff5d22]"
                            : "border-black/8 bg-white text-slate-500"
                        }`}
                      >
                        <Icon size={16} />
                      </span>
                      <span>
                        <span className="block text-[11.5px] font-black leading-snug text-inherit">{item.title}</span>                        
                      </span>
                      <ChevronRight
                        size={16}
                        className={`mt-2 hidden transition-transform lg:block ${
                          isActive ? "text-[#ff5d22]" : "text-slate-300 group-hover:text-sky-500"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeBottleneckData.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.34 }}
                className="hidden flex-col rounded-lg border border-black/8 bg-white p-5 shadow-sm lg:flex lg:p-6"
              >
                <div className="flex items-start justify-between gap-4 border-b border-black/8 pb-4">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">{activeBottleneckData.problemLabel}</p>
                    <h3 className="mt-2 text-xl font-black leading-tight tracking-tight text-slate-950">
                      {activeBottleneckData.title}
                    </h3>
                  </div>
                  <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#ff5d22]/25 bg-[#ff5d22]/10 text-[#ff5d22] sm:flex">
                    <ActiveBottleneckIcon size={18} />
                  </div>
                </div>

                <div className="mt-5 rounded-lg border border-black/8 bg-slate-50 p-4">
                  <div className="grid gap-4 md:grid-cols-[1fr_74px_1fr_74px_1fr] md:items-center">
                    <div className="rounded-lg border border-black/8 bg-white p-5 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                          <ActiveBottleneckIcon size={18} />
                        </div>
                        <div>
                          <div className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">Problem</div>
                          <div className="mt-1 text-[12px] font-black leading-snug text-slate-950">{activeBottleneckData.problemLabel}</div>
                        </div>
                      </div>
                    </div>

                    <div className="hidden items-center justify-center md:flex">
                      <div className="h-px w-full bg-gradient-to-r from-slate-200 via-[#ff5d22] to-slate-200" />
                      <ArrowRight size={18} className="-ml-3 text-[#ff5d22]" />
                    </div>

                    <div className="rounded-lg border border-[#ff5d22]/25 bg-[#ff5d22]/10 p-4 shadow-sm">
                      <div className="text-center">
                        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-lg bg-white text-[#ff5d22] shadow-sm">
                          <Sparkles size={19} />
                        </div>
                        <div className="mt-2 text-[9px] font-black uppercase tracking-[0.14em] text-[#ff5d22]">AppGallop</div>
                        <div className="mt-1 text-[12px] font-black leading-snug text-slate-950">Organize the flow</div>
                      </div>
                    </div>

                    <div className="hidden items-center justify-center md:flex">
                      <div className="h-px w-full bg-gradient-to-r from-slate-200 via-[#ff5d22] to-slate-200" />
                      <ArrowRight size={18} className="-ml-3 text-[#ff5d22]" />
                    </div>

                    <div className="rounded-lg border border-black/8 bg-white p-4 shadow-sm">
                      <div className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">Outcome</div>
                      <div className="mt-2 text-[12px] font-black leading-snug text-slate-950">{activeBottleneckData.plainOutcome}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-black/8 bg-white p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">Working flow</div>
                    <div className="text-[9px] font-black uppercase tracking-[0.16em] text-[#ff5d22]">3 steps</div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {activeBottleneckData.flow.map((step, index) => (
                      <div key={step} className="relative rounded-lg border border-black/8 bg-slate-50 px-3 py-3">
                        {index < activeBottleneckData.flow.length - 1 && (
                          <div className="absolute right-[-14px] top-1/2 z-10 hidden h-px w-7 bg-[#ff5d22]/35 sm:block" />
                        )}
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#ff5d22] text-[10px] font-black text-white">
                            0{index + 1}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-[11px] font-black text-slate-950">{step}</div>
                            <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400">
                              {index === 0 ? "Start" : index === 1 ? "Validate" : "Complete"}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  {activeBottleneckData.solutions.map((solution, index) => (
                    <motion.div
                      key={solution.title}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.04 + index * 0.05 }}
                      className="group min-h-[150px] rounded-lg border border-black/8 bg-slate-50 p-4 transition-all hover:-translate-y-0.5 hover:border-[#ff5d22]/35 hover:bg-white hover:shadow-lg"
                    >
                      <div>
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-black/8 bg-white text-[#ff5d22] shadow-sm transition-all group-hover:border-[#ff5d22]/30 group-hover:bg-[#ff5d22]/10">
                            <solution.icon size={17} />
                          </div>
                          <div className="rounded-full border border-black/8 bg-white px-2 py-1 text-[8px] font-black uppercase tracking-[0.12em] text-slate-400">
                            {solution.tag}
                          </div>
                        </div>
                        <h4 className="mt-4 text-[12.5px] font-black leading-snug text-slate-950">{solution.title}</h4>
                        <p className="mt-2 text-[10.5px] font-semibold leading-relaxed text-slate-500">{solution.shortDesc || solution.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>
      
<section id="features" className="relative overflow-hidden bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            pill="Core Capabilities"
            title="Appgallop Commerce Hub"
            subtitle="Unify distribution, billing, renewals, and revenue operations on a single platform engineered for modern digital commerce."
          />

          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {FeaturesList.map((f, i) => (
              <TiltCard key={i} delay={i * 0.1}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, rotateX: 20 }}
                  whileInView={{ opacity: 1, scale: 1, rotateX: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className="group relative flex h-full flex-col justify-between overflow-hidden rounded-lg border border-black/6 bg-slate-50 p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-[#ff5d22]/20 hover:bg-white hover:shadow-lg"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#ff5d22]/10 to-transparent blur-[40px] rounded-full translate-x-10 -translate-y-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-2xl bg-white shadow-lg flex items-center justify-center text-[#ff5d22] mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 border border-black/5">
                      <f.icon size={24} strokeWidth={2.5} />
                    </div>
                    <h3 className="mb-3 text-lg font-black tracking-tight text-black transition-colors group-hover:text-[#ff5d22]">{f.title}</h3>
                    <p className="text-sm font-bold leading-relaxed text-black/52">{f.desc}</p>
                  </div>

                  <Link to={`/capabilities#${f.slug}`} className="mt-6 flex items-center justify-between relative z-10">
                    <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-[#ff5d22]">
                      Explore <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                    <div className="text-[32px] font-black text-black/5 select-none transition-all duration-700 group-hover:text-[#ff5d22]/10 group-hover:-translate-y-2">
                      0{i + 1}
                    </div>
                  </Link>

                  <div className="absolute bottom-0 left-0 w-full h-1 bg-[#ff5d22] scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-700" />
                </motion.div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>
      

      {/* Platform Resilience - Additional Features */}
      {/* <section className="relative overflow-hidden bg-slate-50 py-16 md:py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(239,68,68,0.03),transparent_50%)]" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <SectionHeader
            pill="System Resilience"
            title="Engineered for 100% durability."
            subtitle="Designed for resilient cloud-native operations"
            align="left"
          />

          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              { title: "Lifecycle Engine", slug: "lifecycle-engine", desc: "Automate L2O, O2Q and T2R workflows through a scalable BPM and event orchestration architecture.", icon: Activity },
              { title: "Service Orchestration", slug: "service-orchestration", desc: "Enable API-driven provisioning, workflow execution, and lifecycle orchestration across distributed service environments.", icon: Zap },
              { title: "Financial Reconciliation Layer", slug: "financial-reconciliation-layer", desc: "Built-in GL mapping ensuring 100% data integrity.", icon: Shield }
            ].map((feature, i) => (
              <TiltCard key={i} delay={i * 0.1}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.1 }}
                  className="group relative h-full overflow-hidden rounded-lg border border-black/6 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-[#ff5d22]/20 hover:shadow-lg"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#ff5d22]/5 to-transparent rounded-full translate-x-10 -translate-y-10" />

                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-slate-50 border border-black/5 shadow-inner flex items-center justify-center text-[#ff5d22] mb-6 group-hover:scale-110 transition-all duration-500">
                      <feature.icon size={24} strokeWidth={2} />
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-base font-black tracking-tight text-black flex items-center gap-3">
                        <span className="text-[#ff5d22]/20 font-mono text-xs">0{i + 1}</span>
                        {feature.title}
                      </h4>
                      <p className="text-black/50 leading-relaxed text-[11px] font-semibold opacity-80">
                        {feature.desc}
                      </p>
                    </div>

                    <Link to={`/system-resilience#${feature.slug}`} className="mt-6 flex items-center gap-2 text-[8px] font-black uppercase tracking-[0.2em] text-[#ff5d22] group-hover:translate-x-2 transition-all duration-300">
                      View Spec <ArrowRight size={10} />
                    </Link>
                  </div>
                </motion.div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section> */}


      {/* Platform Resilience - Parallax Enhanced */}
      <section className="relative overflow-hidden bg-slate-950 py-16 md:py-20" id="resilience">
        <div className="absolute inset-0 opacity-10">
          <NetworkLattice />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <SectionHeader
                align="left"
                pill="Enterprise DNA"
                title={<span className="text-white">Performance without compromise.</span>}
                subtitle={<span className="text-white/70">AppGallop removes cloud monetization chaos with one unified engine for partner onboarding, lead-to-cash, provisioning, and billing at telco scale.</span>}
              />
              <div className="mt-6 space-y-4">
                {[
                  { text: "Zero-touch partner onboarding with role-based access and self-serve workflows", icon: Activity },
                  { text: "Lead-to-cash automation from quoting to provisioning to renewal", icon: Zap },
                  { text: "Unified billing across usage, recurring, and bundled plans with full visibility", icon: Globe },
                  { text: "Audit-ready compliance, margin control, and enterprise-grade security", icon: Shield }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    whileHover={{ scale: 1.05 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-4 group cursor-pointer"
                  >
                    <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#ff5d22] group-hover:bg-[#ff5d22] group-hover:text-white transition-all duration-300">
                      <item.icon size={16} />
                    </div>
                    <span className="text-white/60 text-xs font-bold tracking-tight group-hover:text-white transition-colors">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <div className="relative">
              {/* Parallax Illustration Elements */}
              <motion.div
                style={{ y: yParallax }}
                className="relative rounded-[2.5rem] overflow-hidden shadow-2xl"
              >
                <img
                  src={enterpriseDnaImage}
                  alt="Enterprise DNA"
                  className="w-full h-full object-fill"
                  loading="lazy"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        className="relative overflow-hidden bg-[#f8fafc] py-16 md:py-20"
      >
        <motion.div
          className="absolute inset-0 opacity-90"
          style={{
            backgroundImage:
              "linear-gradient(115deg, rgba(227,30,36,0.09), transparent 28%, rgba(14,165,233,0.07) 52%, transparent 74%, rgba(15,23,42,0.05)), repeating-linear-gradient(90deg, rgba(15,23,42,0.045) 0 1px, transparent 1px 88px)",
            backgroundSize: "180% 180%, 88px 88px"
          }}
          animate={{
            backgroundPosition: ["0% 50%, 0 0", "100% 50%, 44px 0", "0% 50%, 0 0"]
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.78),rgba(255,255,255,0.95))]" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          <div className="space-y-4">
            <div className="flex justify-center">
              <SectionHeader
                pill="Success Stories"
                title="Trusted by sector leaders."
                subtitle="Enterprise teams use AppGallop to unify commerce, lifecycle operations, and revenue control."
                align="center"
                className="max-w-2xl [&>span]:!mb-3 [&>h2]:!mb-3"
              />
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="w-full"
              >
                <div className="grid md:grid-cols-3 gap-3 md:gap-4 items-stretch">
                  {[-1, 0, 1].map((offset) => {
                    const testimonialIndex = (activeTestimonial + offset + TestimonialsList.length) % TestimonialsList.length;
                    const testimonial = TestimonialsList[testimonialIndex];
                    const isActive = offset === 0;

                    return (
                      <motion.article
                        key={`${testimonial.author}-${testimonialIndex}`}
                        whileHover={{ y: -4 }}
                        className={`relative min-h-[260px] rounded-lg border bg-white p-5 text-center transition-all duration-300 ${isActive
                          ? "border-[#ff5d22]/20 shadow-[0_24px_70px_-45px_rgba(15,23,42,0.6)]"
                          : "border-black/10 shadow-[0_18px_55px_-45px_rgba(15,23,42,0.5)]"
                          }`}
                      >
                        <div className="absolute left-3 top-3 h-6 w-6 rounded-full border border-black/10 bg-white text-[#ff5d22] flex items-center justify-center">
                          <Quote size={11} />
                        </div>
                        <div className="absolute right-3 top-3 flex items-center gap-0.5 text-[#ff5d22]">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={9} fill="currentColor" strokeWidth={0} />
                          ))}
                        </div>

                        <div className="mx-auto mb-3 h-16 w-16 rounded-full border border-black/10 bg-slate-50 shadow-sm overflow-hidden flex items-center justify-center">
                          <img
                            src={testimonial.avatar}
                            alt={testimonial.author}
                            className="h-full w-full rounded-full object-fill"
                            referrerPolicy="no-referrer"
                          />
                        </div>

                        <h4 className="text-sm font-black tracking-tight text-black">{testimonial.author}</h4>
                        <p className="mt-1 text-[9px] font-black uppercase tracking-[0.16em] text-[#ff5d22] leading-relaxed">
                          {testimonial.role}
                        </p>

                        <blockquote className="mt-3 text-[11px] md:text-[12px] font-medium leading-relaxed text-black/60 text-justify">
                          "{testimonial.quote}"
                        </blockquote>
                      </motion.article>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setActiveTestimonial((prev) => (prev - 1 + TestimonialsList.length) % TestimonialsList.length)}
                  className="h-9 w-9 rounded-full border border-black/10 bg-white text-black/65 hover:text-black hover:border-[#ff5d22]/40 hover:bg-[#ff5d22]/5 transition-colors flex items-center justify-center shadow-sm"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft size={15} />
                </button>
                <button
                  onClick={() => setActiveTestimonial((prev) => (prev + 1) % TestimonialsList.length)}
                  className="h-9 w-9 rounded-full border border-black/10 bg-white text-black/65 hover:text-black hover:border-[#ff5d22]/40 hover:bg-[#ff5d22]/5 transition-colors flex items-center justify-center shadow-sm"
                  aria-label="Next testimonial"
                >
                  <ChevronRight size={15} />
                </button>
              </div>

              <div className="flex items-center gap-2">
                {TestimonialsList.map((item, index) => (
                  <button
                    key={item.author}
                    onClick={() => setActiveTestimonial(index)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${activeTestimonial === index ? "w-9 bg-[#ff5d22]" : "w-4 bg-black/15 hover:bg-black/30"
                      }`}
                    aria-label={`View testimonial from ${item.author}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="industries" className="relative overflow-hidden bg-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 relative z-10">
          <SectionHeader
            pill="Sector Solutions"
            title="Tailored for your Industry."
            subtitle="Specialized orchestration workflows designed for the specific needs of modern global enterprises."
          />

          <div className="mt-4">
            <IndustryTabs />
          </div>
        </div>
      </section>

      {/* Integrations Grid - The Open Ecosystem */}
      <section className="relative overflow-hidden bg-slate-50 py-16 md:py-20">
        <div className="max-w-8xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/20 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#ff5d22]" />
                <span className="text-[9px] font-black uppercase tracking-widest text-[#ff5d22]">Ecosystem</span>
              </div>
              <h2 className="mb-6 text-3xl md:text-4xl font-black tracking-tight text-black">One Platform. <span className="text-[#ff5d22]">Connected Everywhere.</span></h2>
              <p className="mb-8 text-sm font-semibold leading-relaxed text-black/50">
                Bring together cloud providers, marketplaces, support systems, communication channels, and business applications through a unified integration layer designed for enterprise scale.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Link to="/platform/integrations">
                  <Button variant="primary" className="!px-6 !py-3 text-[10px] uppercase tracking-widest font-black">Explore APIs</Button>
                </Link>                
              </div>
            </div>

            <div className="md:w-2/3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {[
                { name: 'Microsoft', logo: microsoftLogo, color: '#0078D4', label: 'Azure + 365' },
                { name: 'Google', logo: googleLogo, color: '#4285F4', label: 'Cloud + Workspace' },
                { name: 'AWS', logo: awsLogo, color: '#FF9900', label: 'Marketplace' },
                { name: 'ServiceNow', logo: serviceNowLogo, color: '#4A7C59', label: 'ITSM sync' },
                { name: 'Avalara', logo: avalaraLogo, color: '#C05621', label: 'Tax engine' },
                { name: 'Acronis', logo: acronisLogo, color: '#1D4ED8', label: 'Cyber backup' },
                { name: 'WABA', logo: wabaLogo, color: '#15803D', label: 'WhatsApp API' },
                { name: 'Freshdesk', logo: freshdeskLogo, color: '#0F766E', label: 'Support desk' }
              ].map((brand, i) => (
                <motion.div
                  key={brand.name}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.04, duration: 0.45, ease: "easeOut" }}
                  whileHover={{ y: -6, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="aspect-square bg-white border border-black/5 rounded-2xl flex flex-col items-center justify-center p-5 transition-all shadow-sm hover:shadow-lg hover:border-black/10 group relative overflow-hidden cursor-default"
                >
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: `radial-gradient(circle at 50% 0%, ${brand.color}12, transparent 56%)` }}
                  />
                  <div
                    className="absolute -right-10 -top-10 h-24 w-24 rounded-full blur-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"
                    style={{ backgroundColor: brand.color }}
                  />
                  <motion.div
                    className="relative w-14 h-14 rounded-2xl bg-slate-50 shadow-sm border border-black/5 flex items-center justify-center mb-4 text-slate-800 group-hover:bg-white group-hover:shadow-md transition-all duration-300"
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 3 + i * 0.2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <img
                      src={brand.logo}
                      alt={`${brand.name} logo`}
                      className="max-h-8 max-w-[78%] object-contain transition-all duration-300"
                      loading="lazy"
                    />
                  </motion.div>
                  <span className="relative text-[10px] font-black uppercase tracking-widest text-black transition-colors text-center">{brand.name}</span>
                  <span className="relative mt-2 text-[8px] font-black uppercase tracking-[0.18em] text-black/35 group-hover:text-black/60 transition-colors text-center">{brand.label}</span>
                  <span
                    className="absolute bottom-0 left-0 h-0.5 w-full origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                    style={{ backgroundColor: brand.color }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Global Scale & Reliability Stats */}
      <section className="relative overflow-hidden bg-black py-14 text-white md:py-16">
        <motion.div
          style={{ y: statsBgParallax }}
          className="hidden md:block absolute bottom-0 left-0 md:w-[300px] md:h-[300px] lg:w-[600px] lg:h-[600px] bg-[#ff5d22]/10 blur-[120px] rounded-full translate-y-1/2 -translate-x-1/4"
        />
        <div className="absolute inset-0 z-0 opacity-20">
          <NetworkLattice />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black z-10" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-20">
          <div className="grid gap-4 md:grid-cols-4">
            {[
              { value: "1B+", label: "Revenue Processed", sub: "Growth Engine", progress: 74 },
              { value: "30+", label: "Countries Supported", sub: "Deep Localization", progress: 62 },
              { value: "99.999%", label: "Orchestration Uptime", sub: "Enterprise SLA", progress: 92 },
              { value: "150M+", label: "Transactions Processed", sub: "Volume scaled", progress: 81 }
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4 }}
                transition={{ delay: i * 0.1 }}
                className="group relative rounded-lg border border-white/10 bg-white/5 p-5 backdrop-blur-sm transition-colors hover:border-white/20 hover:bg-white/10"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-4xl font-black tracking-tighter text-white">{stat.value}</div>
                    <div className="mt-2 text-sm font-black uppercase tracking-widest leading-none">{stat.label}</div>
                    <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-white/60">{stat.sub}</div>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.2em] text-white/40">
                    <span>Momentum</span>
                    <span>{stat.progress}%</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${stat.progress}%` }}
                      transition={{ duration: 1.2, delay: i * 0.15 }}
                      className="h-full rounded-full bg-[#93BD57]"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-lg bg-[linear-gradient(135deg,rgba(227,30,36,0.7),rgba(15,23,42,0.6),rgba(227,30,36,0.25))] p-[1px] shadow-2xl"
          >
            <div className="relative overflow-hidden rounded-lg bg-slate-950/95 p-8 md:p-10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(227,30,36,0.14),transparent_45%)]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(255,255,255,0.06),transparent_55%)]" />
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#ff5d22]/15 blur-[90px] rounded-full" />
              <div className="relative z-10 space-y-6 max-w-2xl mx-auto">
                <div className="mx-auto h-px w-24 bg-gradient-to-r from-transparent via-white/50 to-transparent" />
                <h2 className="text-3xl md:text-5xl font-black tracking-tighter text-white">
                  Ready to <span className="text-[#ff5d22]">Gallop?</span>
                </h2>
                <p className="text-white/80 text-sm md:text-base font-bold">
                  Launch a modern revenue stack that unifies quoting, provisioning, billing, and finance
                  without the operational drag.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2 text-[9px] font-black uppercase tracking-[0.2em] text-white/60">
                  <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">Lifecycle Orchestration</span>
                  <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">Enterprise Billing</span>
                  <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">Ledger-Ready Controls</span>
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
                  <Link to="/contact">
                    <Button variant="primary" style={{ backgroundColor: '#ff5d22' }} className="!px-9 !py-4 text-[10px] uppercase tracking-widest font-black shadow-xl shrink-0">
                      Request a Demo
                    </Button>
                  </Link>
                  <Link to="/case-studies">
                    <Button variant="ghost" className="!px-9 !py-4 text-[10px] uppercase tracking-widest hover:text-white font-black text-white/70 border border-white/40 hover:border-white transition-all">
                      View Case Studies
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

const bottleneckSolutions = [
  {
    id: "marketplace-launch",
    icon: ShoppingCart,
    problemLabel: "Marketplace launch",
    title: "Marketplace launches keep taking longer than planned.",
    summary: "Catalog setup, pricing, approvals, and provisioning often move in separate tracks.",
    response: "AppGallop brings the launch steps into one guided flow, so teams can prepare offers, check readiness, and go live without chasing updates across email, spreadsheets, and tickets.",
    plainOutcome: "Launch offers with a clear checklist.",
    flow: ["Build offer", "Check readiness", "Go live"],
    solutions: [
      {
        icon: Layers,
        tag: "Catalog",
        title: "Offer setup",
        shortDesc: "Plans, bundles, pricing.",
        desc: "Create product plans, bundles, pricing, and terms in one place before launch.",
      },
      {
        icon: Check,
        tag: "Readiness",
        title: "Launch checks",
        shortDesc: "Missing items flagged early.",
        desc: "Spot missing approvals, pricing gaps, or setup issues before customers see the offer.",
      },
      {
        icon: Zap,
        tag: "Activation",
        title: "Faster go-live",
        shortDesc: "Approved offers move live.",
        desc: "Move approved products into selling and provisioning flows without repeated manual handoffs.",
      },
    ],
  },
  {
    id: "partner-onboarding",
    icon: Users,
    problemLabel: "Partner onboarding",
    title: "New partners wait too long before they can start selling.",
    summary: "Approvals, agreements, pricing access, and user setup can drag across multiple teams.",
    response: "AppGallop gives partner onboarding a simple path from signup to selling, with clear owner actions and fewer manual checkpoints.",
    plainOutcome: "Partners move from signup to selling faster.",
    flow: ["Invite partner", "Approve access", "Start selling"],
    solutions: [
      {
        icon: FileCheck,
        tag: "Intake",
        title: "Clean partner intake",
        shortDesc: "Details and documents in one path.",
        desc: "Collect business details, documents, tax information, and approval inputs through a standard path.",
      },
      {
        icon: ShieldCheck,
        tag: "Approval",
        title: "Controlled approvals",
        shortDesc: "Legal and finance stay visible.",
        desc: "Make legal, finance, and channel approvals visible instead of buried in email threads.",
      },
      {
        icon: Network,
        tag: "Portal",
        title: "Ready-to-sell access",
        shortDesc: "Catalog and pricing access.",
        desc: "Give approved partners the right catalog, price lists, and customer actions without extra setup work.",
      },
    ],
  },
  {
    id: "disconnected-systems",
    icon: LayoutGrid,
    problemLabel: "System sprawl",
    title: "Teams keep working from different versions of the truth.",
    summary: "Sales, operations, finance, and support often see different customer or order details.",
    response: "AppGallop connects the important records and handoffs so teams can follow the same customer, product, order, and billing story.",
    plainOutcome: "Everyone follows the same customer story.",
    flow: ["Connect records", "Sync handoffs", "Track work"],
    solutions: [
      {
        icon: Database,
        tag: "Data",
        title: "Shared operating view",
        shortDesc: "One view of key records.",
        desc: "Keep customer, product, subscription, and order details aligned across teams.",
      },
      {
        icon: RefreshCcw,
        tag: "Sync",
        title: "Cleaner handoffs",
        shortDesc: "Owner and next step are clear.",
        desc: "Pass work from sales to operations to finance with clear status, owner, and next step.",
      },
      {
        icon: Eye,
        tag: "Audit",
        title: "Traceable changes",
        shortDesc: "Changes are easy to verify.",
        desc: "See who changed what, when it changed, and why it matters for billing or service delivery.",
      },
    ],
  },
  {
    id: "bundled-offers",
    icon: Layers,
    problemLabel: "Offer bundling",
    title: "Good bundle ideas stay stuck on a whiteboard.",
    summary: "Combining cloud, SaaS, telecom, support, and service plans can become too complex to price and bill.",
    response: "AppGallop helps teams turn bundle ideas into sellable packages with pricing, eligibility, billing, and fulfillment rules already connected.",
    plainOutcome: "Create bundles without rebuilding operations.",
    flow: ["Design bundle", "Price safely", "Sell and bill"],
    solutions: [
      {
        icon: Settings,
        tag: "Builder",
        title: "Bundle builder",
        shortDesc: "Products and services grouped.",
        desc: "Group products, services, add-ons, and support options into clear packages.",
      },
      {
        icon: Coins,
        tag: "Margin",
        title: "Pricing guardrails",
        shortDesc: "Discounts stay controlled.",
        desc: "Protect margins with discount rules, approval paths, and deal checks.",
      },
      {
        icon: CreditCard,
        tag: "Billing",
        title: "Bundle billing",
        shortDesc: "Mixed offers bill cleanly.",
        desc: "Bill mixed products and services without confusing invoices or manual calculations.",
      },
    ],
  },
  {
    id: "renewal-risk",
    icon: RefreshCw,
    problemLabel: "Renewals",
    title: "Renewals slip because teams react too late.",
    summary: "Upcoming renewals, contract changes, and customer risks are easy to miss when they are tracked manually.",
    response: "AppGallop gives teams a clear renewal calendar, risk signals, and action owners so revenue is protected before the deadline arrives.",
    plainOutcome: "Renewals become routine, not last-minute.",
    flow: ["Track renewal", "Spot risk", "Take action"],
    solutions: [
      {
        icon: Clock,
        tag: "Calendar",
        title: "Renewal timeline",
        shortDesc: "Dates and tasks visible early.",
        desc: "See upcoming renewals, contract dates, owner tasks, and customer actions early.",
      },
      {
        icon: LineChart,
        tag: "Risk",
        title: "Customer health signals",
        shortDesc: "Risk appears before renewal.",
        desc: "Flag churn risk using usage, payment, support, and engagement signals.",
      },
      {
        icon: MessageSquare,
        tag: "Action",
        title: "Guided follow-up",
        shortDesc: "Teams know what to do next.",
        desc: "Help account teams know who to contact, what to offer, and when to escalate.",
      },
    ],
  },
  {
    id: "commission-clarity",
    icon: Eye,
    problemLabel: "Partner commissions",
    title: "Commission questions slow partner trust.",
    summary: "Partners and finance teams need to understand how payouts were calculated.",
    response: "AppGallop keeps commission rules, deal data, adjustments, and payout status connected so every payout is easier to explain.",
    plainOutcome: "Payouts become easier to explain.",
    flow: ["Set rules", "Calculate payout", "Explain result"],
    solutions: [
      {
        icon: FileText,
        tag: "Rules",
        title: "Commission policy setup",
        shortDesc: "Tiers and incentives defined.",
        desc: "Manage tiers, margins, incentives, and exceptions through a consistent rule structure.",
      },
      {
        icon: BarChart3,
        tag: "Payout",
        title: "Payout calculation",
        shortDesc: "Approved transactions calculate payout.",
        desc: "Calculate commissions from approved transactions without spreadsheet-heavy reconciliation.",
      },
      {
        icon: Eye,
        tag: "Clarity",
        title: "Partner visibility",
        shortDesc: "Status and adjustments visible.",
        desc: "Show payout status, adjustments, and revenue contribution in a way partners can understand.",
      },
    ],
  },
  {
    id: "billing-leakage",
    icon: CreditCard,
    problemLabel: "Billing control",
    title: "Small billing gaps quietly become revenue leakage.",
    summary: "Usage, pricing, taxes, credits, and invoice timing can create hidden losses when they are not checked together.",
    response: "AppGallop helps teams catch billing exceptions earlier and keep revenue events aligned with what customers actually bought and used.",
    plainOutcome: "Invoices become easier to trust.",
    flow: ["Capture usage", "Check invoice", "Reconcile"],
    solutions: [
      {
        icon: Activity,
        tag: "Usage",
        title: "Usage and entitlement checks",
        shortDesc: "Usage matches entitlement.",
        desc: "Compare what customers used with what they are allowed to use and what should be billed.",
      },
      {
        icon: FileCheck,
        tag: "Invoice",
        title: "Invoice exception review",
        shortDesc: "Billing errors flagged early.",
        desc: "Catch pricing, tax, credit, and subscription mismatches before disputes start.",
      },
      {
        icon: Coins,
        tag: "Finance",
        title: "Revenue reconciliation",
        shortDesc: "Orders, invoices, payments aligned.",
        desc: "Help finance match orders, invoices, payments, and adjustments with less manual effort.",
      },
    ],
  },
  {
    id: "support-noise",
    icon: LifeBuoy,
    problemLabel: "Support operations",
    title: "Support teams get buried in repeat questions.",
    summary: "Tickets take longer when the team has to search for customer, product, billing, and order context.",
    response: "AppGallop gives support teams cleaner intake, routing, and context so issues reach the right person with the right information.",
    plainOutcome: "Support gets context before work starts.",
    flow: ["Capture context", "Route issue", "Resolve faster"],
    solutions: [
      {
        icon: MessageSquare,
        tag: "Intake",
        title: "Better ticket intake",
        shortDesc: "Right details captured first.",
        desc: "Collect the customer, product, subscription, billing, and service details before work begins.",
      },
      {
        icon: Network,
        tag: "Routing",
        title: "Smarter routing",
        shortDesc: "Issues reach the right queue.",
        desc: "Send issues to the right queue based on product area, severity, SLA, and account impact.",
      },
      {
        icon: BarChart3,
        tag: "Insight",
        title: "Support trend view",
        shortDesc: "Repeat issues surface quickly.",
        desc: "See repeat issues, aging tickets, escalations, and bottlenecks before they grow.",
      },
    ],
  },
];

const serviceGroups = [
  {
    title: "Revenue Assurance",
    path: "/services/revenue-assurance",
    theme: "Reconcile - Validate - Assure",
    icon: Coins,
    stat: "Financial Operations (FinOps)",
    signal: "Billing and collection controls",
    description:
      "For cloud distributors and telecom providers, FinOps is not just cloud cost visibility. It is the operating discipline that keeps billed revenue, collections, OEM payables, partner commissions, subscriptions, and customer balances aligned across every transaction cycle.",
    handles: [
      "Collections vs billed reconciliation across invoices, credit cards, ACH, and offline payments",
      "Three-way reconciliation across OEM records, platform subscriptions, and customer billing",
      "Pre-bill and post-bill checks for quantity, status, dates, pricing, and tax alignment",
      "AP vs AR pre-audit, revenue leakage detection, and billing dispute coordination",
      "Transfer pricing, commission calculation, disbursement support, and profitability reporting",
    ],
    why:
      "Disconnected billing and payment systems create leakage, disputes, and delayed cash visibility. We help finance teams trust the numbers before, during, and after every bill run.",
  },
  {
    title: "Process Control",
    path: "/services/process-control",
    theme: "Map - Govern - Improve",
    icon: Activity,
    stat: "Business Operations (BizOps)",
    signal: "Partner and order visibility",
    description:
      "BizOps helps revenue, product, finance, partner, and support teams operate from one process map. We document handoffs, reduce dependency on manual follow-ups, and build practical operating rhythms for cloud and digital service growth.",
    handles: [
      "Customer and partner data mapping, audit, cleanup, and migration readiness",
      "Process mapping, documentation, best-practice implementation, and operating playbooks",
      "Order management, renewal tracking, auto-renewal monitoring, cancellations, and amendments",
      "Customer segmentation, product performance reviews, forecasting, and budget vs actual analysis",
      "Partner, ISV, and marketplace program coordination across business and operations teams",
    ],
    why:
      "Growth stalls when teams run on disconnected spreadsheets, inboxes, and tribal knowledge. BizOps gives the business a repeatable operating model instead of a manual escalation loop.",
  },
  {
    title: "Managed Support Desk",
    path: "/services/managed-support-desk",
    theme: "Resolve - Escalate - Learn",
    icon: LifeBuoy,
    stat: "L1/L2 coverage",
    signal: "SLA-led ticket handling",
    description:
      "Our support desk is designed for marketplace, cloud subscription, billing, and partner operations. We manage first-line and second-line resolution, route specialized issues, and coordinate escalations to platform, OEM, and third-party teams.",
    handles: [
      "L1 and L2 support for marketplace, subscription, billing, and customer lifecycle queries",
      "Ticket triage, SLA monitoring, queue hygiene, and resolution reporting",
      "Escalation coordination with L3 teams, OEMs, cloud providers, and platform specialists",
      "Billing dispute, licensing query, renewal issue, and provisioning support workflows",
      "Knowledge base development, self-service enablement, and recurring issue analysis",
    ],
    why:
      "Support overhead can slow customer experience and distract internal teams. A managed desk gives customers a clearer path to resolution while your business keeps moving.",
  },
  {
    title: "Consulting & Custom Development",
    path: "/services/consulting-custom-development",
    theme: "Design - Integrate - Extend",
    icon: Code,
    stat: "Faster GTM",
    signal: "Platform extensions",
    description:
      "When distributors and telecom companies need to move beyond out-of-box operations, AppGallop supports the transformation work: cloud strategy, marketplace extension, UI modernization, API integration, automation, and product enablement.",
    handles: [
      "Cloud ecosystem consulting, Microsoft alignment, GTM planning, and market expansion support",
      "Lift-and-shift migration services with customer and partner data audit",
      "API integrations, automation flows, marketplace extensions, and hub UI/UX modernization",
      "DevOps support, integration governance, and release coordination",
      "SaaS and digital service enablement across CPQ, chatbot, UCaaS/CCaaS, and product bundling",
    ],
    why:
      "Cloud commerce programs need more than a platform launch. We help extend the operating layer so new services, partners, integrations, and customer experiences can go live faster.",
  },
];

const deskWorkflowDetails = [
  // FinOps
  [
    {
      title: "Cost Discovery Audits",
      desc: "Audit public cloud registries & operational ledgers to map unallocated compute or redundant tool subscriptions.",
      metric: "100% visible",
      action: "Run deep resource crawl"
    },
    {
      title: "Automated Tagging & Budgeting",
      desc: "Apply dynamic cost-center tags & threshold alert limits at the resource-level dynamically to prevent spillages.",
      metric: "0% overrun",
      action: "Configure threshold rules"
    },
    {
      title: "Consolidated Ledger Reports",
      desc: "Generate continuous visual spend variance projections, department cost allocations, and direct ROI charts.",
      metric: "Real-time updates",
      action: "Analyze weekly variation"
    },
    {
      title: "Continuous Optimizations",
      desc: "Auto-downgrade dormant servers, adjust license volume, and commit reserved savings agreements.",
      metric: "22% saved",
      action: "Commit reserved savings"
    }
  ],
  // BizOps
  [
    {
      title: "Operational Heatmap Audit",
      desc: "Graph and track cross-departmental handoff latencies, identify meeting overlaps, and list communication silos.",
      metric: "Heatmap ready",
      action: "Map company-wide handoffs"
    },
    {
      title: "Standardized SOP Orchestration",
      desc: "Deploy automated task pipelines for onboarding, launch-flows, and approval workflows in one hub.",
      metric: "100% streamlined",
      action: "Activate automated triggers"
    },
    {
      title: "Custom SLA & OKR Dashboards",
      desc: "Synthesize team-level OKRs into real-time visual progress lanes with clear priority milestones.",
      metric: "Clear goals",
      action: "Generate dashboard export"
    },
    {
      title: "Feedback-Driven Optimization",
      desc: "Refine recurring processes, deprecate redundant meetings, and structure collaborative files to reduce drag.",
      metric: "10x faster execution",
      action: "Initiate workflow retro"
    }
  ],
  // Support Desk
  [
    {
      title: "Ticket & Inbound Diagnostics",
      desc: "Map ticket incoming paths, categorize main support issues, and structure logical resolution queues.",
      metric: "Intelligent triage",
      action: "Start traffic route rules"
    },
    {
      title: "Multi-Channel SLA Response",
      desc: "Trigger high-speed ticket resolutions via email, chat, or integrated Slack streams backed by active team monitoring.",
      metric: "Average resolution <15m",
      action: "Review urgent queue"
    },
    {
      title: "Customer Satisfaction Scoring",
      desc: "Track client happiness levels, agent resolve ratios, and response speed directly in centralized logs.",
      metric: "98% CSAT score",
      action: "Open satisfaction report"
    },
    {
      title: "Knowledge Base Synthesizer",
      desc: "Convert resolved issues into searchable customer manuals, self-service aids, and training playbooks.",
      metric: "70% self-serve",
      action: "Compile resolution tips"
    }
  ],
  // Order Management
  [
    {
      title: "Incoming Order Mapping",
      desc: "Scan all incoming client logs, purchase approvals, price validations, and credit holds across channels.",
      metric: "No validation delays",
      action: "Verify pipeline ingress"
    },
    {
      title: "Automated Line Fulfillment",
      desc: "Integrate logistics channels, issue drop-ship orders, and confirm correct items in the operational queue.",
      metric: "Instant sync",
      action: "Coordinate vendor cargo"
    },
    {
      title: "Live Delivery Traceability",
      desc: "Expose real-time order tracking data, courier handshakes, and return-rate logs in unified screens.",
      metric: "Accurate tracking",
      action: "Check fulfillment route"
    },
    {
      title: "Fulfillment Pipeline Optimization",
      desc: "Identify error patterns, resolve logistics vendor bottlenecks, and speed up validation models continuously.",
      metric: "99.9% accurate",
      action: "Run logistics audit"
    }
  ]
];

const pipelineDetails = [
  // FinOps (index 0)
  [
    {
      label: "Ingest Signal",
      title: "Cost anomalies detected in cloud registry",
      desc: "Instantaneous scan triggered by real-time budget threshold deviations beyond target limits (+14.6%).",
      metric: "Active Spike Alert",
      badge: "Real-time Detection",
      terminalLines: [
        "INGRESS_ALERT: Cloud cost threshold warning breached (+14.6% Delta)",
        "DETERMINING_SOURCE: Sifting cloud catalog registers (AWS, GCP, Azure)",
        "IDENTIFIED: Compute engine 'staging-unbound-k8s' scale-out mismatch",
        "STATUS_DISPATCH: Alert propagated to FinOps triage node"
      ]
    },
    {
      label: "Triage & Route",
      title: "Automated alert classification for target resource",
      desc: "Parsing cloud tenant metadata to route the cost anomalies to our cloud optimization pipeline.",
      metric: "0.2s dispatch latency",
      badge: "Intelligent Triage",
      terminalLines: [
        "DISPATCH_CYCLE: Parsing metadata attributes",
        "RESOURCE_RESOLVE: Matching tags: {ENV: 'staging', APP: 'k8s', COST_CENTER: 'marketing'}",
        "LEDGER_MAP: Allocating chargebacks over departmental budget balance",
        "HANDOVER: Dispatching playbook request to Auto-scale-down queue"
      ]
    },
    {
      label: "Process Execution",
      title: "Automated auto-scaling correction rules deployed",
      desc: "AppGallop scale-down automation scales back or shuts down redundant containers in non-production environments.",
      metric: "4 mins resolve time",
      badge: "Auto-Remediation",
      terminalLines: [
        "EXECUTING_SOP: Running dormant resource crawl on AWS Cluster-04B",
        "SCALE_DOWN: Terminating 3 staging sub-nodes (idle since weekend)",
        "REALLOCATE: Adjusting cluster size variables inside target registry",
        "RE-VERIFICATION: Deployed size limits verified against cost controls"
      ]
    },
    {
      label: "Continuous Verification",
      title: "Confirm outcome & report saved run-rate",
      desc: "Continuous scan confirms target anomalies resolved and locks in real-time savings run-rate.",
      metric: "+$18,000 /mo savings",
      badge: "Outcome Locked",
      terminalLines: [
        "VERIFICATION_ENGINE: Cost limits normal (99.2% alignment)",
        "OPTIMIZATION_RATIO: Residual waste meter minimized to 0.8%",
        "SAVINGS_AUDIT: Recorded monthly runoff down $18,000/mo",
        "LEDGER_POST: Cost charts synchronized. Status: Green"
      ]
    }
  ],
  // BizOps (index 1)
  [
    {
      label: "Ingest Signal",
      title: "Departmental handoff lag alert",
      desc: "Workflow delay telemetry identifies process congestion or overlapping meeting structures.",
      metric: "Friction Point Spotted",
      badge: "Anomaly Spotted",
      terminalLines: [
        "LATENCY_DETECTOR: Multi-dept feedback handoff queue lag exceeds 3.5h",
        "CONGESTION_AUDIT: High meeting overlap ratio (42.5%) spotted",
        "IDENTIFIED: Engineering to Product rollout approval gate blocking",
        "STATUS_DISPATCH: Transitioning telemetry signal to BizOps dashboard"
      ]
    },
    {
      label: "Triage & Route",
      title: "Matching KPI milestones & routing process",
      desc: "Assigning process bottleneck tracking IDs and determining target playbooks to restore team rhythm.",
      metric: "SOP triage active",
      badge: "Clarity Established",
      terminalLines: [
        "DISPATCH_CYCLE: Evaluating team OKR milestone trajectories",
        "SOCIOMETRY_MAP: Process mapping handoff latency profiles",
        "RESOLVE: Selected SOP Standard Playbook #42 (Product Rollout Gate)",
        "HANDOVER: Activating automation trigger sequence"
      ]
    },
    {
      label: "Process Execution",
      title: "Playbook #42 deployment & handoff automation",
      desc: "Standardized approval forms are deployed, redundant sync loops are pruned, and collaboration channels are synced.",
      metric: "1.25x speed-up deployed",
      badge: "Standardization Active",
      terminalLines: [
        "EXECUTING_SOP: Deploying Playbook #42 task triggers in unified hub",
        "AUTOMATION: Generating Slack check-ins with automated state gates",
        "MEETING_PRUNE: Cancelling redundant weekly status syncs",
        "RE-VERIFICATION: Workflow execution pathways confirmed online"
      ]
    },
    {
      label: "Continuous Verification",
      title: "Continuous tracking & process retro verification",
      desc: "KPI logs verify throughput acceleration and cross-team interaction friction declines to baseline.",
      metric: "98.2% Standardized",
      badge: "Efficiency Secured",
      terminalLines: [
        "VERIFICATION_ENGINE: Dept handoff latency reduced from 3.5h to 11m",
        "OKR_PROGRESS: Handoff velocity stable",
        "RETRO_AUDIT: Operational audit logs complete. Friction minimized.",
        "STATUS: System execution optimized. Baseline healthy."
      ]
    }
  ],
  // Support Desk (index 2)
  [
    {
      label: "Ingest Signal",
      title: "P0 ticket ingress across support stream",
      desc: "Customer ticket raised on high-severity channels triggers instant system-focused warning.",
      metric: "Crit-1 Incident Tracked",
      badge: "Urgent Alert",
      terminalLines: [
        "INBOUND_INQUIRY: Ingress point 'api-gateway' triggers ticket",
        "DESCRIPTON: 'Web checkout checkout payload fails intermittently'",
        "IMPACT: Category: P0 Critical - Revenue Interruption",
        "STATUS_DISPATCH: Pushing alerts to instant Slack dispatcher"
      ]
    },
    {
      label: "Triage & Route",
      title: "Intelligent triage & on-call routing",
      desc: "Automated categorizer assigns ticket routing, maps key SLA schedules, and flags support leads.",
      metric: "SLA schedule locked",
      badge: "Intelligent Dispatch",
      terminalLines: [
        "DISPATCH_CYCLE: Parsing ticket content patterns",
        "SLA_SCHEDULING: Target resolution countdown initialized (15 min SLA)",
        "RESOLVE: Routing ticket to Tier-2 Checkout Systems Lead on-call",
        "HANDOVER: Dispatching pager notification to response channel"
      ]
    },
    {
      label: "Process Execution",
      title: "On-call response & resolution pipeline",
      desc: "Assigned response engineer isolates checkout payload error and implements targeted patch.",
      metric: "11m resolution time",
      badge: "Remediation Active",
      terminalLines: [
        "EXECUTING_SOP: Team assigned, Slack war-room initialized",
        "RESOLUTION: checkout-api Docker container image hot-fixed",
        "VERIFICATION: Payload response status returns 200 OK",
        "USER_REPLY: Resolved message submitted to client. Monitoring stream."
      ]
    },
    {
      label: "Continuous Verification",
      title: "SLA status reporting & satisfaction survey",
      desc: "Ensuring customer satisfaction scoring (CSAT) is generated and incident summary is compiled.",
      metric: "100% CSAT scorecard",
      badge: "Satisfaction Checked",
      terminalLines: [
        "VERIFICATION_ENGINE: SLA clock terminated. Total lapse: 11 mins",
        "CSAT_ENGINE: survey-prompt sent; client score: Excellent (5/5)",
        "SLA_AUDIT: Pending escalations: 0, backlog clear",
        "STATUS: SLA healthy. Resolution confirmed"
      ]
    }
  ],
  // Order Management Desk (index 3)
  [
    {
      label: "Ingest Signal",
      title: "1,450 lines batch purchase alert",
      desc: "Multi-channel purchase approvals and incoming catalog order files parsed into staging staging.",
      metric: "High Ingress Volume",
      badge: "Logistics Sync",
      terminalLines: [
        "ORDER_INGEST: XML/JSON payload received from major distributor",
        "LINE_COUNT: 1,450 distinct SKU lookup lines parsed",
        "CREDIT_CHECK: Checking customer ledger status dynamically",
        "STATUS_DISPATCH: Sync request dispatched to validation pipeline"
      ]
    },
    {
      label: "Triage & Route",
      title: "Credit verify & exception classification",
      desc: "Scanning line item rules, mapping correct warehouses, and verifying against stock levels.",
      metric: "Zero validation delay",
      badge: "Credit Verified",
      terminalLines: [
        "DISPATCH_CYCLE: Validating unit prices against distributor catalogs",
        "ERROR_CHECK: Zero pricing discrepancy found",
        "WAREHOUSE_MAP: Selected East Coast Hub & dropship vendor logs",
        "HANDOVER: Preparing courier handshake payloads"
      ]
    },
    {
      label: "Process Execution",
      title: "Courier handshake API dispatch",
      desc: "AppGallop integration triggers automated warehouse requests, logs tracking IDs, and handles customs documentation.",
      metric: "Instant order sync",
      badge: "Logistics Automated",
      terminalLines: [
        "EXECUTING_SOP: Synchronizing order details via standard APIs",
        "VENDOR_CARGO: Automated dispatch of drop-ship fulfillment requests",
        "TRACKING_ESTABLISH: Courier payload logged. Tracking active.",
        "COMPLIANCE: Generating compliance tags for express shipping"
      ]
    },
    {
      label: "Continuous Verification",
      title: "Real-time delivery tracing & accuracy logs",
      desc: "Continuous order status tracker confirms perfect delivery and updates ERP inventory ledgers.",
      metric: "99.98% accurate fulfillment",
      badge: "Delivered & Logged",
      terminalLines: [
        "VERIFICATION_ENGINE: Order line fulfillment status: Deployed",
        "ACCURACY_AUDIT: No item omissions, correct delivery verified",
        "ERP_UPDATE: Stock counts synchronized across system profiles",
        "STATUS: Handover completed. End-to-end trace archived"
      ]
    }
  ]
];

const serviceDetailPages = {
  "revenue-assurance": {
    eyebrow: "Financial Operations Integrity",
    title: "Revenue Assurance for Digital Service Providers",
    subtitle: "Protect revenue, eliminate leakage, and maintain financial accuracy across every operational transaction.",
    description:
      "AppGallop Revenue Assurance extends the platform's billing, marketplace, partner settlement, and revenue orchestration layers with controls that identify leakage, reconcile transactions, validate billing records, and strengthen financial governance across the complete digital service lifecycle.",
    icon: Coins,
    highlights: [
      "Revenue Leakage Detection",
      "Billing Validation & Controls",
      "Settlement Reconciliation",
      "Financial Audit Visibility",
    ],
    challengeTitle: "Revenue Loss Often Hides in Operational Complexity.",
    challengeIntro:
      "Manual reconciliation, disconnected systems, billing inconsistencies, and partner disputes create significant financial exposure.",
    challenges: [
      "Unreconciled transactions",
      "Billing inaccuracies",
      "Missing usage records",
      "Delayed settlements",
      "Partner disputes",
      "Limited financial visibility",
    ],
    capabilities: [
      { title: "Automated Reconciliation", desc: "Validate transactions across AppGallop marketplace orders, billing events, ERP records, partner ledgers, and payment systems." },
      { title: "Revenue Leakage Detection", desc: "Continuously identify missed charges, incomplete provisioning records, duplicate transactions, subscription gaps, and revenue inconsistencies." },
      { title: "Settlement Validation", desc: "Ensure OEM, reseller, and partner settlements reflect contractual obligations, consumption records, discount rules, and service lifecycle status." },
      { title: "Financial Audit Trail", desc: "Maintain complete traceability across AppGallop order, billing, collection, credit note, settlement, and reconciliation events." },
      { title: "Exception Management", desc: "Prioritize discrepancies and route exceptions into governed AppGallop workflows for rapid resolution." },
      { title: "Operational Reporting", desc: "Deliver real-time insight into collections, reconciliation status, partner exposure, and financial performance." },
    ],
    outcomes: [
      "Reduced revenue leakage",
      "Faster financial reconciliation cycles",
      "Improved settlement accuracy",
      "Stronger audit readiness",
      "Increased operational transparency",
      "Higher profitability",
    ],
    ctaTitle: "Ensure Every Transaction Contributes to Revenue.",
    ctaText:
      "Strengthen financial controls, reduce leakage, and improve operational confidence with AppGallop Revenue Assurance.",
  },
  "process-control": {
    eyebrow: "Process Control",
    title: "Govern Operational Workflows with Confidence.",
    subtitle: "Standardize, monitor, and optimize operational execution across every customer and service process.",
    description:
      "AppGallop Process Control turns fragmented operating procedures into governed platform workflows across lead-to-order, order-to-cash, ticket-to-resolution, partner operations, approvals, and exception management.",
    icon: Activity,
    highlights: [
      "Workflow Orchestration",
      "Policy Enforcement",
      "SLA Management",
      "Operational Visibility",
    ],
    challengeTitle: "Operational Inconsistency Creates Business Risk.",
    challengeIntro:
      "When processes depend on manual coordination, organizations face delays, errors, compliance gaps, and reduced customer satisfaction.",
    challenges: [
      "Manual task execution",
      "Inconsistent workflows",
      "Limited operational visibility",
      "SLA breaches",
      "Approval delays",
      "Cross-functional coordination gaps",
    ],
    capabilities: [
      { title: "Workflow Orchestration", desc: "Automate operational journeys across AppGallop L2O, O2C, T2R, marketplace, billing, and partner lifecycle workflows." },
      { title: "Rules & Policy Enforcement", desc: "Apply pricing, approval, entitlement, SLA, credit, and governance rules consistently inside platform workflows." },
      { title: "SLA Management", desc: "Track process timelines, monitor ownership, and prevent SLA violations across service activation, billing, support, and escalation paths." },
      { title: "Exception Handling", desc: "Identify operational deviations in orders, invoices, tickets, partner requests, and settlements, then trigger escalation workflows automatically." },
      { title: "Approval Automation", desc: "Streamline commercial, discount, payment-term, operational, finance, and support approvals." },
      { title: "Operational Visibility", desc: "Provide end-to-end visibility into workflow status, bottlenecks, ownership, and execution performance." },
    ],
    outcomes: [
      "Faster process execution",
      "Reduced operational risk",
      "Improved SLA adherence",
      "Higher productivity",
      "Stronger governance",
      "Better customer experience",
    ],
    ctaTitle: "Create Consistent, Governed, and Scalable Operations.",
    ctaText:
      "Transform fragmented operational activities into intelligent, automated workflows.",
  },
  "managed-support-desk": {
    eyebrow: "Lifecycle Operations Support",
    title: "Managed Support Desks Built for Digital Operations.",
    subtitle: "Deliver faster resolutions, stronger SLA performance, and exceptional customer experiences.",
    description:
      "AppGallop Managed Support Desk is designed around the platform's ticket-to-resolution layer, giving telecom providers, cloud marketplaces, distributors, and digital service businesses structured support operations with customer, product, entitlement, billing, and SLA context.",
    icon: LifeBuoy,
    highlights: [
      "Incident Management",
      "Intelligent Ticket Routing",
      "SLA Governance",
      "Knowledge Management",
    ],
    challengeTitle: "Support Operations Should Drive Growth, Not Friction.",
    challengeIntro:
      "Disconnected support processes often result in delayed responses, poor ownership, and inconsistent customer experiences.",
    challenges: [
      "Ticket ownership ambiguity",
      "SLA breaches",
      "Escalation inefficiencies",
      "Limited service visibility",
      "Fragmented communication",
      "Inconsistent customer experiences",
    ],
    capabilities: [
      { title: "Incident Management", desc: "Manage incidents across customer portals, email, service desk tools, marketplace operations, billing queues, and partner support channels." },
      { title: "Intelligent Ticket Routing", desc: "Automatically assign requests based on product, entitlement, severity, geography, customer tier, resolver expertise, or SLA risk." },
      { title: "SLA Governance", desc: "Track commitments across AppGallop T2R workflows and proactively manage escalations before breach." },
      { title: "Multi-Level Support", desc: "Coordinate L1, L2, L3, OEM, platform, finance, and operations teams through one resolution path." },
      { title: "Customer Communication", desc: "Maintain transparent updates throughout billing, provisioning, renewal, service assurance, and support lifecycles." },
      { title: "Knowledge Management", desc: "Turn resolved issues into structured knowledge assets that support self-service and faster future resolution." },
    ],
    outcomes: [
      "Faster issue resolution",
      "Improved SLA compliance",
      "Greater ownership accountability",
      "Enhanced customer satisfaction",
      "Reduced support costs",
      "Scalable support operations",
    ],
    ctaTitle: "Deliver Support Experiences That Build Customer Trust.",
    ctaText:
      "Empower teams with structured support operations and complete service visibility.",
  },
  "consulting-custom-development": {
    eyebrow: "Transformation & Platform Engineering",
    title: "Build, Modernize, and Scale Digital Operations.",
    subtitle: "Accelerate transformation initiatives with consulting expertise and custom platform engineering tailored to your business.",
    description:
      "AppGallop Consulting and Custom Development helps enterprises extend the AppGallop platform around their specific marketplace, CPQ, billing, provisioning, support, integration, and revenue operations requirements.",
    icon: Code,
    highlights: [
      "Digital Transformation Consulting",
      "Custom Platform Development",
      "Systems Integration",
      "Cloud Modernization",
    ],
    challengeTitle: "Legacy Systems Shouldn't Limit Innovation.",
    challengeIntro:
      "Modern digital businesses require flexible platforms, integrated ecosystems, and scalable architectures.",
    challenges: [
      "Legacy platform limitations",
      "Complex integrations",
      "Manual processes",
      "Slow innovation cycles",
      "Operational silos",
      "Limited scalability",
    ],
    capabilities: [
      { title: "Digital Transformation Consulting", desc: "Design AppGallop adoption, marketplace modernization, digital service monetization, and operating model strategies." },
      { title: "Custom Platform Development", desc: "Build tailored extensions around catalogs, partner portals, CPQ, billing, renewals, dashboards, and lifecycle workflows." },
      { title: "Systems Integration", desc: "Connect CRM, ERP, BSS, OSS, billing, support, payment, tax, OEM, and third-party platforms." },
      { title: "Process Automation", desc: "Automate repetitive AppGallop-adjacent operational tasks across approvals, onboarding, billing, reconciliation, and support." },
      { title: "Cloud Modernization", desc: "Modernize legacy commerce and operations environments while preserving critical enterprise systems." },
      { title: "API & Integration Services", desc: "Enable seamless interoperability across AppGallop APIs, enterprise systems, partner systems, and vendor ecosystems." },
    ],
    outcomes: [
      "Faster time-to-market",
      "Increased operational efficiency",
      "Modernized technology landscape",
      "Greater scalability",
      "Reduced technical debt",
      "Improved business agility",
    ],
    ctaTitle: "Transform Ideas into Scalable Digital Solutions.",
    ctaText:
      "Partner with AppGallop to design, build, and optimize platforms that support long-term growth.",
  },
} as const;

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const page = serviceId ? serviceDetailPages[serviceId as keyof typeof serviceDetailPages] : null;

  if (!page) {
    return <Navigate to="/services" replace />;
  }

  const Icon = page.icon;
  const otherServices = Object.entries(serviceDetailPages).filter(([slug]) => slug !== serviceId);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative isolate min-h-screen overflow-hidden bg-slate-50 pt-20 text-slate-950"
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.055)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.045)_1px,transparent_1px)] bg-[size:48px_48px]" />
        <div className="absolute right-[-14%] top-[-10%] h-[460px] w-[460px] rounded-full bg-[#ff5d22]/10 blur-[110px]" />
      </div>

      <section className="mx-auto max-w-7xl px-6 py-8 md:px-10 md:py-10">
        <div className="overflow-hidden rounded-[1.75rem] border border-black/6 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
          <div className="grid gap-0 lg:grid-cols-[1.04fr_0.96fr]">
            <div className="p-6 md:p-8">              

              <div className="inline-flex items-center gap-2 rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1 text-[8px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">
                {page.eyebrow}
              </div>
              <h1 className="mt-5 max-w-3xl text-3xl font-black leading-[0.98] tracking-tight text-slate-950 md:text-5xl">
                {page.title}
              </h1>
              <p className="mt-5 max-w-2xl text-base font-black leading-relaxed text-slate-700">
                {page.subtitle}
              </p>
              <p className="mt-4 max-w-2xl text-sm font-bold leading-relaxed text-slate-500">
                {page.description}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {page.highlights.map((highlight) => (
                  <span key={highlight} className="rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.12em] text-[#d9481e]">
                    {highlight}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative overflow-hidden bg-slate-950 p-6 text-white md:p-8">
              <div className="absolute right-0 top-0 h-72 w-72 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#ff5d22]/20 blur-[90px]" />
              <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:42px_42px]" />
              <div className="relative z-10 flex h-full min-h-[360px] flex-col justify-between">
                <div>
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#ff5d22] text-white shadow-xl shadow-[#ff5d22]/20">
                    <Icon size={24} />
                  </div>
                  <h2 className="mt-6 text-2xl font-black tracking-tight text-white">
                    {page.challengeTitle}
                  </h2>
                  <p className="mt-3 text-sm font-bold leading-relaxed text-white/62">
                    {page.challengeIntro}
                  </p>
                </div>

                <div className="mt-7 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {page.challenges.map((challenge) => (
                    <div key={challenge} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 text-[11px] font-bold leading-relaxed text-white/68">
                      {challenge}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-16 md:px-10 md:pb-24">
        <div className="grid gap-6 lg:grid-cols-[1fr_0.72fr]">
          <div className="rounded-[1.75rem] border border-black/6 bg-white p-6 shadow-[0_18px_55px_rgba(15,23,42,0.05)] md:p-7">
            <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">Core Capabilities</p>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {page.capabilities.map((capability) => (
                <div key={capability.title} className="rounded-2xl border border-black/6 bg-slate-50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#ff5d22]/10 text-[#ff5d22]">
                      <Check size={15} />
                    </div>
                    <div>
                      <h3 className="text-sm font-black leading-tight text-slate-950">{capability.title}</h3>
                      <p className="mt-2 text-xs font-bold leading-relaxed text-slate-500">{capability.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-black/6 bg-white p-6 shadow-[0_18px_55px_rgba(15,23,42,0.05)] md:p-7">
            <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">Business Outcomes</p>
            <div className="mt-5 space-y-3">
              {page.outcomes.map((outcome) => (
                <div key={outcome} className="flex items-start gap-3 rounded-2xl border border-black/6 bg-slate-50 p-3">
                  <Check size={15} className="mt-0.5 shrink-0 text-emerald-500" />
                  <p className="text-sm font-bold leading-relaxed text-slate-600">{outcome}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-[1.75rem] bg-slate-950 p-6 text-white shadow-[0_20px_70px_rgba(15,23,42,0.18)] md:p-8">
          <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff8a5c]">Next Step</p>
              <h2 className="mt-3 text-2xl font-black tracking-tight text-white md:text-4xl">{page.ctaTitle}</h2>
              <p className="mt-3 max-w-2xl text-sm font-bold leading-relaxed text-white/62">{page.ctaText}</p>
            </div>
            <Link to="/contact">
              <Button variant="primary" className="!bg-[#ff5d22] !text-white hover:!bg-white hover:!text-[#ff5d22]">
                Start the Conversation
                <ArrowRight size={14} />
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-8">
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">Explore More</p>
              <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-950">Other services</h3>
            </div>
            <Link to="/services" className="hidden text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 hover:text-[#ff5d22] sm:inline-flex">
              View all
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {otherServices.map(([slug, service]) => {
              const RelatedIcon = service.icon;
              return (
                <Link
                  key={slug}
                  to={`/services/${slug}`}
                  className="group rounded-[1.25rem] border border-black/8 bg-white p-5 shadow-[0_14px_45px_rgba(15,23,42,0.05)] transition-all hover:-translate-y-1 hover:border-[#ff5d22]/30"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#ff5d22]/10 text-[#ff5d22]">
                      <RelatedIcon size={18} />
                    </div>
                    <div>
                      <p className="text-base font-black leading-tight text-slate-950">{service.title}</p>
                      <p className="mt-1 text-xs font-bold leading-relaxed text-slate-500">{service.eyebrow}</p>
                    </div>
                  </div>
                  <div className="mt-5 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.14em] text-[#ff5d22]">
                    Open service
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </motion.main>
  );
};

const ServicesPage = () => {
  const serviceEntries = Object.entries(serviceDetailPages);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative isolate min-h-screen overflow-hidden bg-slate-50 pt-20 text-slate-950"
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(15,23,42,0.055)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.045)_1px,transparent_1px)] bg-[size:48px_48px]" />
        <div className="absolute right-[-14%] top-[-10%] h-[460px] w-[460px] rounded-full bg-[#ff5d22]/10 blur-[110px]" />
        <div className="absolute bottom-[12%] left-[-18%] h-[380px] w-[380px] rounded-full bg-slate-900/8 blur-[120px]" />
      </div>

      <section className="mx-auto max-w-7xl px-6 py-8 md:px-10 md:py-10">
        <div className="rounded-[1.75rem] border border-black/6 bg-white p-6 shadow-[0_18px_55px_rgba(15,23,42,0.06)] md:p-8">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1 text-[8px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">
                AppGallop Services
              </div>
              <h1 className="mt-5 max-w-3xl text-3xl font-black leading-[0.98] tracking-tight text-slate-950 md:text-5xl">
                Services built around the <span className="text-[#ff5d22]">AppGallop platform.</span>
              </h1>
            </div>
            <div>
              <p className="max-w-2xl text-sm font-bold leading-relaxed text-slate-500">
                Choose the service area you want to explore. Each page explains how AppGallop extends marketplace, CPQ, billing, partner operations, support, and revenue lifecycle workflows.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {['Marketplace operations', 'Billing controls', 'Workflow governance', 'Platform engineering'].map((tag) => (
                  <span key={tag} className="rounded-full border border-black/8 bg-slate-50 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.12em] text-slate-500">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-16 md:px-10 md:pb-24">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {serviceEntries.map(([slug, service], index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={slug}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/services/${slug}`}
                  className="group flex h-full flex-col justify-between overflow-hidden rounded-[1.5rem] border border-black/8 bg-white p-6 shadow-[0_18px_55px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-[#ff5d22]/35 hover:shadow-[0_24px_70px_rgba(15,23,42,0.1)]"
                >
                  <div>
                    <div className="flex items-start justify-between gap-5">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#ff5d22]/10 text-[#ff5d22]">
                        <Icon size={23} />
                      </div>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-slate-500">
                        Platform Service
                      </span>
                    </div>

                    <div className="mt-8">
                      <p className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">
                        {service.eyebrow}
                      </p>
                      <h2 className="mt-3 text-2xl font-black leading-tight tracking-tight text-slate-950 md:text-3xl">
                        {service.title}
                      </h2>
                      <p className="mt-4 line-clamp-3 text-sm font-bold leading-relaxed text-slate-500">
                        {service.description}
                      </p>
                    </div>
                  </div>

                  <div className="mt-7 flex items-center justify-between border-t border-black/6 pt-5">
                    <div className="flex flex-wrap gap-2">
                      {service.highlights.slice(0, 2).map((highlight) => (
                        <span key={highlight} className="rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.12em] text-[#d9481e]">
                          {highlight}
                        </span>
                      ))}
                    </div>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-950 text-white transition-all group-hover:bg-[#ff5d22]">
                      <ArrowRight size={17} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="pt-8">
          <ContactSection id="contact" />
        </div>
      </section>
    </motion.main>
  );
};

const LegacyServicesPage = () => {
  const [activeDesk, setActiveDesk] = useState(0);
  const [activeOpsStep, setActiveOpsStep] = useState(0);
  const [pipelineStep, setPipelineStep] = useState(0);

  // States for interactive sliders & toggles
  const [budgetSlider, setBudgetSlider] = useState(75000);
  const [autoOptimize, setAutoOptimize] = useState(true);

  const [bizOpsFriction, setBizOpsFriction] = useState(4);
  const [autoSOP, setAutoSOP] = useState(true);

  const [slaTarget, setSlaTarget] = useState(98);
  const [instantRoute, setInstantRoute] = useState(true);

  const [orderVolume, setOrderVolume] = useState(1450);
  const [apiHandshake, setApiHandshake] = useState(true);

  // Checked items for desk checklist
  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>({});

  const toggleCheck = (item: string) => {
    setCheckedItems(prev => ({ ...prev, [item]: !prev[item] }));
  };

  const stepsList = ["Discover", "Operate", "Report", "Improve"];
  const currentDesk = serviceGroups[activeDesk];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-16 pb-6 bg-white overflow-hidden"
    >
      {/* Services Hero Header */}
      <section className="relative overflow-hidden bg-white text-slate-950">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/50 to-transparent" />
        <div className="absolute inset-0 opacity-100 bg-[linear-gradient(rgba(0,0,0,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.04)_1px,transparent_1px)] bg-[size:42px_42px]" />
        <motion.div
          animate={{ x: ["-25%", "25%", "-25%"] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-y-0 w-1/2 bg-[linear-gradient(90deg,transparent,rgba(239,68,68,0.07),transparent)]"
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-8 items-center">
            <div>
              <p className="text-[#ff5d22] text-[10px] font-black uppercase tracking-[0.35em] mb-4">What we do</p>
              <h1 className="text-4xl md:text-6xl text-slate-950 tracking-tighter leading-[0.95] max-w-3xl">
                Services for <span className="text-[#ff5d22]">cloud and digital service operators</span>
              </h1>
              <p className="mt-5 text-slate-500 text-sm md:text-base font-bold leading-relaxed max-w-2xl">
                AppGallop helps cloud distributors and telecom companies run the operational work behind digital services: reconciliations, billing controls, partner settlement, support desk operations, process governance, integrations, and custom platform extensions.
              </p>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl">
                {serviceGroups.map((service, i) => (
                  <button
                    key={service.title}
                    onClick={() => setActiveDesk(i)}
                    className={`rounded-2xl border text-left p-3 transition-all duration-300 ${activeDesk === i
                      ? "border-[#2563EB] bg-[#2563EB]/8"
                      : "border-black/10 bg-black/[0.03] hover:border-black/20"
                      }`}
                  >
                    <service.icon size={16} className={`${activeDesk === i ? "text-[#2563EB]" : "text-slate-400"} mb-3`} />
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-800 leading-tight">{service.stat}</p>
                  </button>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, rotateX: 8, rotateY: -14, y: 18 }}
              animate={{ opacity: 1, rotateX: 0, rotateY: 0, y: 0 }}
              whileHover={{ rotateX: 5, rotateY: -7, y: -6 }}
              transition={{ type: "spring", stiffness: 130, damping: 18 }}
              className="relative [transform-style:preserve-3d]"
            >
              <div className="relative rounded-[2rem] border border-black/8 bg-white backdrop-blur-xl p-5 shadow-[0_8px_40px_rgba(0,0,0,0.10)]">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <p className="text-slate-400 text-[9px] font-black uppercase tracking-[0.24em]">Ops Control Room</p>
                    <h3 className="text-xl font-black tracking-tight text-slate-950 mt-1">Live service mesh</h3>
                  </div>
                  <motion.div
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                    className="h-10 w-10 rounded-2xl bg-[#2563EB] flex items-center justify-center shadow-xl shadow-[#2563EB]/20"
                  >
                    <Activity size={18} className="text-white" />
                  </motion.div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {serviceGroups.map((service, i) => (
                    <motion.button
                      key={service.title}
                      onClick={() => setActiveDesk(i)}
                      animate={{ y: i % 2 === 0 ? [0, -4, 0] : [0, 4, 0] }}
                      transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut" }}
                      className={`text-left rounded-2xl border p-4 transition-all duration-300 ${activeDesk === i
                        ? "bg-[#2563EB]/8 border-[#2563EB]/40"
                        : "bg-slate-50 border-black/8 hover:border-black/15"
                        }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-4">
                        <div className="flex items-center gap-2">
                          <service.icon size={15} className="text-[#2563EB]" />
                          <span className="text-slate-800 text-[9px] font-black uppercase tracking-widest">{service.stat}</span>
                        </div>
                        <span className={`h-1.5 w-1.5 rounded-full ${activeDesk === i ? "bg-[#2563EB] animate-pulse" : "bg-emerald-500"}`} />
                      </div>
                      <div className="h-1.5 rounded-full bg-black/8 overflow-hidden">
                        <motion.div
                          initial={{ width: "20%" }}
                          animate={{ width: activeDesk === i ? "100%" : `${72 + i * 6}%` }}
                          transition={{ duration: 0.8 }}
                          className={`h-full rounded-full ${activeDesk === i ? "bg-[#2563EB]" : "bg-black/20"}`}
                        />
                      </div>
                      <p className="mt-3 text-slate-500 text-[10px] font-bold leading-relaxed">{service.signal}</p>
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Interactive Operating Model Space - Made readable, aligned, and compact */}
      <section className="py-12 md:py-16 bg-slate-50/50 border-b border-slate-100 relative overflow-hidden">
        {/* Modern 3D Animated Background */}
        <NetworkLattice />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">

          {/* Headline Metadata */}
          <div className="mb-10 text-center max-w-3xl mx-auto">
            <p className="text-[#ff5d22] text-[10px] font-black uppercase tracking-[0.3em] mb-2.5">Operating Model</p>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter text-slate-900 leading-none">
              Managed service desks for recurring revenue operations
            </h2>
            <p className="text-slate-500 font-bold text-xs md:text-sm mt-3 leading-relaxed max-w-xl mx-auto">
              Each desk is built around the operating realities of cloud marketplaces, telecom digital services, partner channels, subscriptions, billing cycles, and customer support.
            </p>
          </div>

          {/* Grid: 2x2 Clean, Compact & Beautiful Reading Cards */}
          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            {serviceGroups.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white/95 backdrop-blur-md rounded-[1.75rem] border border-slate-200/80 p-6 md:p-8 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 relative overflow-hidden text-slate-800"
              >
                {/* Subtle Decorative Ambient Blur */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#ff5d22]/3 rounded-full blur-[40px] pointer-events-none" />

                <div>
                  {/* Card Header */}
                  <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#ff5d22]/5 border border-[#ff5d22]/10 text-[#ff5d22] flex items-center justify-center">
                        <service.icon size={18} />
                      </div>
                      <div>
                        <p className="text-[9px] font-mono font-black uppercase text-slate-400">Desk 0{i + 1}</p>
                        <h3 className="text-base font-black text-slate-950 tracking-tight leading-none mt-0.5">{service.title}</h3>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#ff5d22]/5 border border-[#ff5d22]/10 text-[9px] font-black uppercase tracking-widest text-[#ff5d22]">
                      {service.stat}
                    </span>
                  </div>

                  {/* Theme tagline */}
                  <div className="text-[10px] font-mono font-bold tracking-wider text-[#ff5d22] bg-slate-50/80 inline-block px-2.5 py-1 rounded-md border border-slate-100 mb-4">
                    {service.theme}
                  </div>

                  {/* Description */}
                  <p className="text-slate-600 text-xs font-medium leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Operational Handles List */}
                  <div>
                    <div className="text-[9px] font-black uppercase tracking-wider text-slate-400 mb-3 block">
                      Operational Scope
                    </div>
                    <div className="space-y-2.5">
                      {service.handles.map((item) => (
                        <div key={item} className="flex items-start gap-2.5 text-[11px] text-slate-700 font-medium leading-normal">
                          <Check size={12} className="text-emerald-500 shrink-0 mt-0.5" strokeWidth={3} />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link
                    to={service.path}
                    className="mt-6 inline-flex items-center gap-2 text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22] transition-all hover:gap-3"
                  >
                    Explore Service
                    <ArrowRight size={12} />
                  </Link>
                </div>

                {/* Footer Section: Why it matters */}
                <div className="mt-8 pt-4 border-t border-slate-700 bg-slate-900/90 -mx-6 -mb-6 px-6 py-4 rounded-b-[1.75rem] flex items-start gap-2.5">
                  <div className="min-w-[12px] mt-0.5 text-xs text-[#ff5d22] font-bold">ℹ</div>
                  <p className="text-[10px] text-slate-300 font-medium leading-relaxed text-left">
                    <span className="text-white font-black uppercase tracking-wider block mb-0.5">Why it matters</span>
                    {service.why}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* Structured Delivery System Banner */}
      {/* Unified Revenue & Service Orchestration Section */}
      <section className="relative py-14 md:py-20 overflow-hidden bg-[#050711] text-white border-t border-white/5">

        {/* Ambient Background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(227,30,36,0.12),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.08),transparent_35%)]" />

        <div className="absolute inset-0 opacity-[0.03] bg-[linear-gradient(rgba(255,255,255,1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,1)_1px,transparent_1px)] bg-[size:38px_38px]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">

          {/* Header */}
          <div className="max-w-3xl mb-10 md:mb-14">
            <p className="text-[#ff5d22] text-[10px] font-black uppercase tracking-[0.35em] mb-4">
              Unified Revenue Architecture
            </p>

            <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-[0.95] text-white/95">
              One service layer for{" "}
              <span className="text-[#ff5d22]">
                billing, reconciliation & customer operations
              </span>
            </h2>

            <p className="mt-5 text-sm md:text-base text-white/65 font-semibold leading-relaxed max-w-2xl">
              AppGallop brings FinOps, BizOps, support desk, consulting, and
              custom development together so providers can reduce leakage,
              improve customer experience, and scale cloud or digital services
              with stronger operational control.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid lg:grid-cols-12 gap-5 items-stretch">

            {/* Left Main Feature Card */}
            <div className="lg:col-span-7 rounded-[2rem] border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 md:p-7 relative overflow-hidden">

              {/* Glow */}
              <div className="absolute top-0 right-0 w-60 h-60 bg-[#ff5d22]/10 blur-[100px] rounded-full" />

              <div className="relative z-10">

                {/* Top */}
                <div className="flex items-start justify-between gap-4 mb-8">

                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] font-black text-[#ff5d22] mb-3">
                      Live Revenue Flow
                    </p>

                    <h3 className="text-2xl md:text-3xl font-black tracking-tight text-white leading-tight">
                      Real-time service execution across every operational touchpoint
                    </h3>
                  </div>

                  <div className="hidden md:flex h-12 w-12 rounded-2xl bg-[#ff5d22] items-center justify-center shadow-xl shadow-[#ff5d22]/20">
                    <Activity size={20} />
                  </div>
                </div>

                {/* Revenue Pipeline */}
                <div className="grid md:grid-cols-4 gap-3 mb-8">

                  {[
                    {
                      title: "Order Intake",
                      stat: "Partner Requests",
                    },
                    {
                      title: "Billing Checks",
                      stat: "Pre/Post Bill",
                    },
                    {
                      title: "Reconciliation",
                      stat: "3-Way Control",
                    },
                    {
                      title: "Settlement",
                      stat: "AP vs AR View",
                    },
                  ].map((item, i) => (
                    <motion.div
                      key={item.title}
                      animate={{ y: i % 2 === 0 ? [0, -3, 0] : [0, 3, 0] }}
                      transition={{
                        duration: 4 + i,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      className="rounded-2xl border border-white/10 bg-black/30 p-4"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-8 h-8 rounded-xl bg-[#ff5d22]/10 border border-[#ff5d22]/20 flex items-center justify-center text-[#ff5d22] text-[10px] font-black">
                          0{i + 1}
                        </div>

                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      </div>

                      <h4 className="text-sm font-black text-white">
                        {item.title}
                      </h4>

                      <p className="text-[10px] text-white/45 font-bold mt-2 uppercase tracking-wider">
                        {item.stat}
                      </p>
                    </motion.div>
                  ))}

                </div>

                {/* Bottom Description */}
                <div className="rounded-2xl border border-white/10 bg-black/20 p-5">

                  <div className="flex flex-wrap gap-2 mb-4">

                    {[
                      "Charging",
                      "Collections",
                      "Revenue Assurance",
                      "Partner Billing",
                      "Subscription Lifecycle",
                    ].map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[10px] uppercase tracking-wider font-black text-white/70"
                      >
                        {tag}
                      </span>
                    ))}

                  </div>

                  <p className="text-sm text-white/60 leading-relaxed font-medium">
                    Built to support distributors and service providers with modular
                    services that validate transactions, manage exceptions, support
                    customers, and keep finance, partner, and operations teams aligned.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Side Stack */}
            <div className="lg:col-span-5 grid gap-5">

              {/* AI Intelligence Card */}
              <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-[#ff5d22]/10 to-slate-900 p-6 relative overflow-hidden">

                <div className="absolute top-0 right-0 w-40 h-40 bg-[#ff5d22]/10 rounded-full blur-[80px]" />

                <div className="relative z-10">

                  <div className="flex items-center justify-between mb-6">

                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] font-black text-[#ff5d22] mb-2">
                        AI Revenue Intelligence
                      </p>

                      <h3 className="text-xl font-black text-white tracking-tight">
                        Reconciled data for better decisions
                      </h3>
                    </div>

                    <div className="w-10 h-10 rounded-2xl bg-[#ff5d22] flex items-center justify-center">
                      <Sparkles size={18} />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">

                    {[
                      { value: "3-Way", label: "Recon" },
                      { value: "24/7", label: "Monitoring" },
                      { value: "Live", label: "Dashboards" },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="rounded-xl border border-white/10 bg-black/20 p-3"
                      >
                        <div className="text-sm font-black text-white">
                          {item.value}
                        </div>

                        <div className="text-[9px] uppercase tracking-wider text-white/40 font-bold mt-1">
                          {item.label}
                        </div>
                      </div>
                    ))}

                  </div>
                </div>
              </div>

              {/* Operational Assurance Card */}
              <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6">

                <div className="flex items-center justify-between mb-6">

                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] font-black text-emerald-400 mb-2">
                      Operational Assurance
                    </p>

                    <h3 className="text-xl font-black text-white tracking-tight">
                      Transparent execution visibility
                    </h3>
                  </div>

                  <ShieldCheck size={20} className="text-emerald-400" />
                </div>

                <div className="space-y-4">

                  {[
                    "Automated charging validation workflows",
                    "Collections against invoice visibility",
                    "Partner settlement and commission support",
                    "Centralized revenue assurance reporting",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-start gap-3 text-sm text-white/65 font-medium"
                    >
                      <Check
                        size={14}
                        className="text-emerald-400 shrink-0 mt-0.5"
                        strokeWidth={3}
                      />

                      <span>{item}</span>
                    </div>
                  ))}

                </div>

                {/* Tiny Footer */}
                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">

                  <div>
                    <p className="text-[9px] uppercase tracking-widest text-white/35 font-black">
                      Active Systems
                    </p>

                    <p className="text-white text-sm font-black mt-1">
                      128 Services Online
                    </p>
                  </div>

                  <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[10px] uppercase tracking-widest font-black text-emerald-400">
                    Stable
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Final Action Box */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            whileHover={{ y: -5, rotateX: 1 }}
            transition={{ type: "spring", stiffness: 180, damping: 18 }}
            className="rounded-[2rem] bg-slate-950 text-white p-8 relative overflow-hidden [transform-style:preserve-3d]"
          >
            <div className="absolute inset-0 bg-[linear-gradient(120deg,transparent,rgba(227,30,36,0.18),transparent)] pointer-events-none" />
            <div className="relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <p className="text-[#ff5d22] text-[10px] font-black uppercase tracking-[0.3em] mb-4">Not sure where to start?</p>
                <h2 className="text-3xl md:text-5xl text-white/90 tracking-tighter mb-4">Start with the highest-friction desk.</h2>
                <p className="text-white/72 text-xs md:text-sm font-bold leading-relaxed">
                  Whether the immediate issue is reconciliation leakage, partner data quality, billing disputes, support backlog, or custom marketplace development, we can help you shape a focused pilot and scale from there.
                </p>
              </div>
              <Link to="/contact">
                <Button
                  variant="primary"
                  className="
    !px-8
    !py-4
    uppercase
    tracking-widest
    font-black
    text-[10px]
    whitespace-nowrap
    !bg-[#ff5d22]
    !border-[#ff5d22]
    !text-white
    hover:!bg-white
    hover:!text-[#ff5d22]
    hover:!border-[#ff5d22]
    transition-all
    duration-300
  "
                >
                  Start the Conversation
                  <ArrowRight size={14} />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <ContactSection id="contact" />
    </motion.div>
  );
};

const ContactSection = ({ id }: { id?: string }) => {
  const honeypotRef = useRef<HTMLInputElement | null>(null);
  const [formData, setFormData] = useState({
    inquiryType: 'Lifecycle Orchestration Quote',
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters.';
    }

    if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters.';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    } else {
      const commonProviders = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com', 'live.com'];
      const domain = formData.email.split('@')[1]?.toLowerCase();
      if (commonProviders.includes(domain)) {
        newErrors.email = 'Please use a professional/company email address.';
      }
    }

    if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setStatus('loading');
    setSubmitMessage('');

    try {
      const recaptchaAction = 'lead_contact';
      const recaptchaToken = await executeRecaptchaV3(recaptchaAction);
      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          website: honeypotRef.current?.value || '',
          recaptchaToken,
          recaptchaAction,
        })
      });

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const result = await response.json();
        if (response.ok && result.success) {
          setStatus('success');
          setSubmitMessage(result.message || 'Your request was submitted successfully.');
          setFormData({
            inquiryType: 'Lifecycle Orchestration Quote',
            firstName: '',
            lastName: '',
            email: '',
            message: ''
          });
          setErrors({});
          setTimeout(() => setStatus('idle'), 5000);
        } else {
          setSubmitMessage(result.error || 'Unable to submit the form right now.');
          setStatus('error');
        }
      } else {
        const text = await response.text();
        console.error("Server returned non-JSON response:", text);
        setSubmitMessage('The server returned an unexpected response. Please try again.');
        setStatus('error');
      }
    } catch (error) {
      console.error("Lead submission failed:", error);
      setSubmitMessage(error instanceof Error ? error.message : 'Unable to reach the lead API. Please make sure the app server is running.');
      setStatus('error');
    }
  };

  const responsePath = [
    {
      title: "Discovery",
      desc: "We identify the revenue, billing, marketplace, or lifecycle workflow you want to improve first.",
      icon: MessageSquare,
    },
    {
      title: "Solution Map",
      desc: "Our team maps your current systems, handoffs, integrations, and operational bottlenecks.",
      icon: Layers,
    },
    {
      title: "Demo Plan",
      desc: "You get a focused walkthrough around the flows that matter to your business.",
      icon: ShieldCheck,
    },
  ];

  const outcomeTags = [
    "Revenue orchestration",
    "Billing automation",
    "Marketplace launch",
    "Partner lifecycle",
  ];

  return (
    <section id={id} className="relative overflow-hidden bg-white py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid items-stretch gap-6 lg:grid-cols-2">
          <div className="relative flex min-h-[620px] overflow-hidden rounded-[2rem] bg-slate-950 p-7 text-white shadow-[0_26px_90px_rgba(15,23,42,0.18)] md:p-8">
            <div className="absolute right-0 top-0 h-72 w-72 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#ff5d22]/20 blur-[90px]" />
            <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:42px_42px]" />
            <div className="relative z-10 flex w-full flex-col">
              <div>
                <div className="inline-flex rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1 text-[9px] font-black uppercase tracking-[0.24em] text-[#ff8a5c]">
                  Connect
                </div>
                <h2 className="mt-5 max-w-xl text-3xl font-black leading-[0.98] tracking-tight text-white md:text-5xl">
                  Let's map your revenue operations.
                </h2>
                <p className="mt-5 max-w-xl text-sm font-bold leading-relaxed text-white/65 md:text-base">
                  Share what you are building, replacing, or scaling. We will route your request to the right AppGallop specialist.
                </p>
              </div>

              <div className="mt-8 border-y border-white/10">
                <div className="flex items-center justify-between gap-4 py-4">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.24em] text-[#ff8a5c]">Response Path</p>
                    <h3 className="mt-1 text-lg font-black tracking-tight text-white">What happens after you submit</h3>
                  </div>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#ff5d22] text-white shadow-lg shadow-[#ff5d22]/20">
                    <Zap size={18} />
                  </div>
                </div>
                {responsePath.map((item, index) => (
                  <div key={item.title} className="grid grid-cols-[44px_1fr] gap-4 border-t border-white/10 py-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#ff5d22]/20 bg-[#ff5d22]/10 text-[#ff8a5c]">
                      <item.icon size={16} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[8px] font-black uppercase tracking-widest text-white/35">0{index + 1}</span>
                        <h4 className="text-sm font-black text-white">{item.title}</h4>
                      </div>
                      <p className="mt-1 text-[11px] font-semibold leading-relaxed text-white/55">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>              

            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative flex min-h-[620px] overflow-hidden rounded-[2rem] border border-black/15 bg-slate-50 p-7 shadow-[0_26px_90px_rgba(15,23,42,0.08)] md:p-8"
          >
            <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-[#ff5d22]/8 blur-[70px]" />

            {status === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative z-10 flex w-full flex-col items-center justify-center text-center"
              >
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-500 mb-6">
                  <Check size={40} />
                </div>
                <h3 className="text-2xl font-black mb-2">Request Received.</h3>
                <p className="text-black/50 font-bold max-w-sm">Our sales team has been notified and will reach out to you within 24 hours.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="relative z-10 flex w-full flex-col">
                <input
                  ref={honeypotRef}
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="pointer-events-none absolute -left-[9999px] h-px w-px opacity-0"
                />
                <div className="mb-6">
                  <p className="text-[9px] font-black uppercase tracking-[0.24em] text-[#ff5d22]">Start the Conversation</p>
                  <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-950">Tell us what you need.</h3>
                  <p className="mt-2 text-xs font-bold leading-relaxed text-black/45">
                    A short note is enough. Our team will follow up with the right next step.
                  </p>
                </div>
                <div className="space-y-3.5">
                <div className="space-y-1">
                  <label className="text-[9px] font-black uppercase tracking-widest text-black/30 ml-2">Inquiry Type</label>
                  <select
                    value={formData.inquiryType}
                    onChange={(e) => setFormData({ ...formData, inquiryType: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white border border-black/5 focus:border-[#ff5d22]/50 outline-none transition-all font-bold text-[11px] ring-offset-2 focus:ring-2 ring-[#ff5d22]/10"
                  >
                    <option>Lifecycle Orchestration Quote</option>
                    <option>Partner Integration</option>
                    <option>Global Expansion Support</option>
                    <option>Press & Media</option>
                  </select>
                </div>
                <div className="grid md:grid-cols-2 gap-3.5">
                  <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase tracking-widest text-black/30 ml-2">First Name</label>
                    <input
                      type="text"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg bg-white border ${errors.firstName ? 'border-red-500' : 'border-black/5'} focus:border-[#ff5d22]/50 outline-none transition-all font-bold text-[11px] ring-offset-2 focus:ring-2 ring-[#ff5d22]/10`}
                    />
                    {errors.firstName && <p className="text-[8px] text-red-500 font-bold ml-2">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase tracking-widest text-black/30 ml-2">Last Name</label>
                    <input
                      type="text"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg bg-white border ${errors.lastName ? 'border-red-500' : 'border-black/5'} focus:border-[#ff5d22]/50 outline-none transition-all font-bold text-[11px] ring-offset-2 focus:ring-2 ring-[#ff5d22]/10`}
                    />
                    {errors.lastName && <p className="text-[8px] text-red-500 font-bold ml-2">{errors.lastName}</p>}
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black uppercase tracking-widest text-black/30 ml-2">Professional Email</label>
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-4 py-3 rounded-lg bg-white border ${errors.email ? 'border-red-500' : 'border-black/5'} focus:border-[#ff5d22]/50 outline-none transition-all font-bold text-[11px] ring-offset-2 focus:ring-2 ring-[#ff5d22]/10`}
                  />
                  {errors.email && <p className="text-[8px] text-red-500 font-bold ml-2">{errors.email}</p>}
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black uppercase tracking-widest text-black/30 ml-2">Message</label>
                  <textarea
                    rows={2}
                    placeholder="Briefly describe your requirements..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className={`w-full px-4 py-3 rounded-lg bg-white border ${errors.message ? 'border-red-500' : 'border-black/5'} focus:border-[#ff5d22]/50 outline-none transition-all font-bold text-[11px] resize-none ring-offset-2 focus:ring-2 ring-[#ff5d22]/10`}
                  />
                  {errors.message && <p className="text-[8px] text-red-500 font-bold ml-2">{errors.message}</p>}
                </div>
                </div>
                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  variant="primary"
                  className={`mt-auto w-full !py-3.5 uppercase tracking-widest font-black text-[9px] shadow-xl shadow-[#ff5d22]/10 ${status === 'loading' ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {status === 'loading' ? 'Processing...' : status === 'error' ? 'Error. Try Again' : 'Submit Request'}
                </Button>
                {status === 'error' && submitMessage && (
                  <p className="text-[10px] font-bold text-red-500 text-center leading-relaxed">
                    {submitMessage}
                  </p>
                )}
                <a
                  href="mailto:sales@appgallop.com"
                  className="mt-3 flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white px-4 py-3 text-slate-950 transition-all hover:border-[#ff5d22]/40 hover:text-[#ff5d22]"
                >
                  <div>
                    <p className="text-[8px] font-black uppercase tracking-widest text-black/35">Direct Email</p>
                    <p className="text-sm font-black">sales@appgallop.com</p>
                  </div>
                  <ArrowRight size={16} />
                </a>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ContactPage = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="pt-12"
  >
    <ContactSection id="contact" />
  </motion.div>
);

const MarketplacePage = () => {
  const lifecycleStages = [
    {
      title: "Catalog and Pricing",
      icon: LayoutGrid,
      desc: "Centralized offers with governed pricebooks.",
      items: ["Catalog structure", "Pricebooks", "Bundles"]
    },
    {
      title: "Campaigns",
      icon: Share2,
      desc: "Promotions and commercial motions managed in one place.",
      items: ["Promo codes", "Campaign timing", "Upsell paths"]
    },
    {
      title: "Cust/Partner Onboarding with KYC",
      icon: Users,
      desc: "Validated customer and partner intake with business checks.",
      items: ["Onboarding", "KYC checks", "Document flow"]
    },
    {
      title: "Quote and Deal Configuration",
      icon: FileText,
      desc: "Configure commercial terms before order conversion.",
      items: ["Deal setup", "Approvals", "Terms control"]
    },
    {
      title: "Order and Provisioning Automation",
      icon: Zap,
      desc: "Move accepted orders into activation workflows.",
      items: ["Order orchestration", "Provisioning", "Status sync"]
    },
    {
      title: "Billing and Collections",
      icon: CreditCard,
      desc: "Automate recurring billing, payments, and dunning.",
      items: ["Billing runs", "Collections", "Reconciliation"]
    },
    {
      title: "Revenue Recognition",
      icon: BarChart3,
      desc: "Govern revenue treatment and financial visibility.",
      items: ["Recognition logic", "Controls", "Reporting"]
    },
    {
      title: "Contracts and Renewals Management",
      icon: RefreshCcw,
      desc: "Manage contract continuity and retention workflows.",
      items: ["Contracts", "Renewals", "Lifecycle changes"]
    }
  ];

  const features = [
    {
      title: "White-Labeled Storefronts",
      desc: "Deploy a fully branded storefront with custom domains, themes, and controlled styling.",
      icon: Monitor,
      layout: "lg:col-span-2"
    },
    {
      title: "Multi-Tier Distribution",
      desc: "Orchestrate reseller networks with tiered catalogs and partner-aware pricing logic.",
      icon: Layers,
      layout: "lg:col-span-1"
    },
    {
      title: "Global Tax & Compliance",
      desc: "Native tax-engine integration with ISO 27001 and GDPR controls built in.",
      icon: Shield,
      layout: "lg:col-span-1"
    },
    {
      title: "Enterprise Workflow Management",
      desc: "Coordinate approvals, policy gates, provisioning handoffs, and operational ownership through governed workflow automation.",
      icon: Activity,
      layout: "lg:col-span-2"
    }
  ];

  const commerceOperations = [
    {
      title: "Product Catalog Management",
      icon: LayoutGrid,
      items: ["Multi-vendor catalog", "Product lifecycle management", "Bundles & service packages", "SKU management", "Product relationships"]
    },
    {
      title: "Pricing & Pricebooks",
      icon: Coins,
      items: ["Customer-specific pricing", "Segment-based pricing", "Distributor pricing", "Contract pricing", "Tiered pricing"]
    },
    {
      title: "Promotions & Campaigns",
      icon: Share2,
      items: ["Coupon management", "Promotional codes", "Campaign scheduling", "Upsell promotions", "Cross-sell campaigns"]
    },
    {
      title: "Order Management",
      icon: ShoppingCart,
      items: ["Shopping cart", "Checkout workflows", "Approval workflows", "Order orchestration", "Order amendments", "Order tracking"]
    }
  ];

  const commercePolicies = [
    {
      title: "Purchase Controls",
      icon: ShieldCheck,
      items: ["Spending thresholds", "Budget limits", "Purchase approvals", "Subscription governance"]
    },
    {
      title: "Product Eligibility",
      icon: Check,
      items: ["Region restrictions", "Segment eligibility", "Product entitlements", "Service qualification rules"]
    },
    {
      title: "Partner Policies",
      icon: Users,
      items: ["Partner-specific catalogs", "Channel visibility controls", "Distribution rights", "Margin governance"]
    },
    {
      title: "Compliance Controls",
      icon: FileCheck,
      items: ["Audit trails", "Approval history", "Policy enforcement", "Operational accountability"]
    }
  ];

  const financialControls = [
    {
      title: "Credit Management",
      icon: CreditCard,
      items: ["Credit wallets", "Credit allocation", "Parent-child credit pools", "Credit limits", "Auto recharge"]
    },
    {
      title: "Financial Governance",
      icon: Shield,
      items: ["Exposure monitoring", "Credit approvals", "Consumption thresholds", "Purchase controls", "Budget management"]
    },
    {
      title: "Collections Automation",
      icon: RefreshCw,
      items: ["Payment reminders", "Dunning automation", "Account suspension rules", "Payment reconciliation", "Collection tracking"]
    }
  ];

  const channelCommerce = [
    {
      title: "Distribution Management",
      icon: Network,
      items: ["Multi-tier partner hierarchy", "White-labeled storefronts", "Delegated administration", "Partner onboarding"]
    },
    {
      title: "Commercial Controls",
      icon: LineChart,
      items: ["Margin waterfalls", "Distributor pricing", "Revenue sharing", "Rebate programs"]
    },
    {
      title: "Settlement Automation",
      icon: Coins,
      items: ["Commission management", "Incentive tracking", "Partner settlements", "Financial reconciliation"]
    }
  ];

  const commissionPrograms = [
    {
      title: "Channel Partner Commission Rules",
      desc: "Configure channel partner slabs, deal-source attribution, tier incentives, and renewal commissions from the same marketplace order data.",
      icon: Users,
      items: ["Tier slabs", "Deal attribution", "Renewal incentives"]
    },
    {
      title: "Affiliate Partner Referral Governance",
      desc: "Manage affiliate partner referrals, co-sell motions, influence credit, and approval gates before commission liability is created.",
      icon: Network,
      items: ["Referral tracking", "Co-sell credit", "Approval gates"]
    },
    {
      title: "Automated Settlement",
      desc: "Convert eligible orders, activations, collections, and renewals into validated payout records with tax and reconciliation context.",
      icon: Coins,
      items: ["Payout runs", "Tax context", "Ledger sync"]
    }
  ];

  const commissionControls = [
    "Partner eligibility checks",
    "Order-to-commission mapping",
    "Invoice and collection validation",
    "Exception and dispute workflows",
    "Payout approval history",
    "ERP and ledger reconciliation"
  ];

  const channelPartnerLifecycle = [
    {
      title: "Partner Onboarding",
      icon: Users,
      items: ["Partner registration", "Tier assignment", "Document intake", "Activation workflow"]
    },
    {
      title: "Partner Verification",
      icon: FileCheck,
      items: ["KYC validation", "Business checks", "Tax ID validation", "Approval history"]
    },
    {
      title: "Channel Access Control",
      icon: Lock,
      items: ["Role-based access", "Delegated admin", "Hierarchy permissions", "Portal visibility"]
    },
    {
      title: "Partner Growth Management",
      icon: RefreshCcw,
      items: ["Enablement motions", "Performance tracking", "Incentive alignment", "Renewal coordination"]
    }
  ];

  const enterpriseControls = [
    { title: "Approval Workflows", icon: Check },
    { title: "Audit Logs", icon: FileText },
    { title: "Role-Based Access", icon: Lock },
    { title: "Segregation of Duties", icon: ShieldCheck },
    { title: "Delegated Administration", icon: Users },
    { title: "Policy Engine", icon: Settings },
    { title: "Compliance Monitoring", icon: Monitor },
    { title: "Activity Tracking", icon: Activity }
  ];

  return (
    <div className="relative bg-white selection:bg-[#ff5d22] selection:text-white">
      <div className="pt-20 pb-10 px-4 sm:px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <ProductHero data={productData.marketplace} theme={heroThemes.marketplace} />
        </div>
      </div>

      <section className="py-10 md:py-12 px-4 sm:px-6 md:px-12 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,93,34,0.16),transparent_45%),radial-gradient(circle_at_80%_70%,rgba(14,165,233,0.10),transparent_50%)]" />
          <div className="absolute inset-0 opacity-[0.10] [transform:perspective(900px)_rotateX(8deg)]">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.10)_1px,transparent_1px)] bg-[size:36px_36px]" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <SectionHeader
            pill="Operational Flow"
            title={<span className="text-white">The Digital Commerce Lifecycle</span>}
            subtitle={<span className="text-white/65">From product discovery to recurring revenue, orchestrate every stage of the customer and partner journey.</span>}
            align="center"
            maxWidth="max-w-5xl"
          />

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {lifecycleStages.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                transition={{ delay: i * 0.04 }}
                viewport={{ once: true }}
                className="group relative min-h-[230px] overflow-hidden rounded-2xl border border-white/10 bg-white/[0.045] p-5 shadow-sm shadow-black/20 transition-all duration-500 cursor-default hover:border-[#ff5d22]/25 hover:bg-white hover:shadow-xl"
              >
                <div className="absolute inset-0 rounded-2xl bg-[radial-gradient(circle_at_30%_20%,rgba(255,93,34,0.13),transparent_55%)] opacity-100 transition-opacity duration-500 group-hover:opacity-0" />
                <div className="relative z-10 flex h-full flex-col">
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-[#ff8a5c] shadow-sm transition-all duration-500 group-hover:border-black/5 group-hover:bg-[#ff5d22] group-hover:text-white">
                      <step.icon size={19} strokeWidth={1.8} />
                    </div>
                    <div className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.16em] text-[#ff8a5c] transition-all duration-500 group-hover:border-black/5 group-hover:bg-slate-50 group-hover:text-black/30">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>

                  <h3 className="text-sm font-black tracking-tight leading-snug text-white transition-colors duration-500 group-hover:text-black">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-[10.5px] font-bold leading-relaxed text-white/60 transition-colors duration-500 group-hover:text-black/45">
                    {step.desc}
                  </p>

                  <div className="mt-auto pt-5">
                    <div className="flex flex-wrap gap-1.5">
                      {step.items.map((item) => (
                        <span
                          key={item}
                          className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-1 text-[8px] font-black uppercase tracking-[0.1em] text-white/55 transition-all duration-500 group-hover:border-black/5 group-hover:bg-slate-50 group-hover:text-black/35"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 px-4 sm:px-6 md:px-12 bg-slate-50 relative overflow-hidden">
        <NetworkLattice />
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-black/5 to-transparent" />
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            pill="Platform Capabilities"
            title="Engineered for Scalability."
            subtitle="Enterprise-grade features that turn digital commerce operations into a monetization engine."
            align="left"
          />

          <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3">
            {features.map((feature, i) => {
              const isLarge = feature.layout === "lg:col-span-2";
              const isStorefrontFeature = feature.title === "White-Labeled Storefronts";
              return (
                <TiltCard key={i} delay={i * 0.05} className={feature.layout}>
                  <div className="group relative p-5 md:p-5 rounded-[2.25rem] bg-white border border-black/5 shadow-sm overflow-hidden flex flex-col justify-between hover:shadow-xl transition-all duration-700 h-full min-h-[220px]">
                    <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-[#ff5d22]/5 rounded-full blur-[80px] group-hover:bg-[#ff5d22]/10 transition-colors" />

                    <div className={`relative z-10 h-full gap-4 ${isLarge ? "grid grid-cols-1 lg:grid-cols-12 items-center" : "flex flex-col justify-between"}`}>
                      <div className={isLarge ? "lg:col-span-7 flex flex-col justify-between h-full" : "flex flex-col"}>
                        <div>
                          <div className="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center text-[#ff5d22] mb-3 group-hover:bg-[#ff5d22] group-hover:text-white transition-all duration-500">
                            <feature.icon size={20} strokeWidth={1.5} />
                          </div>
                          <h3 className="text-sm md:text-base font-black tracking-tight text-black mb-2">
                            {feature.title}
                          </h3>
                          <p className="text-[10px] font-bold text-black/50 leading-relaxed">
                            {feature.desc}
                          </p>
                        </div>

                        {isLarge && (
                          <div className="mt-5 flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                            <span className="text-[9px] font-black uppercase tracking-wider bg-slate-50 text-slate-500 px-2.5 py-1 rounded-md">
                              {isStorefrontFeature ? "Multi-tenant DNS" : "Workflow Routing"}
                            </span>
                            <span className="text-[9px] font-black uppercase tracking-wider bg-[#ff5d22]/5 text-[#ff5d22] px-2.5 py-1 rounded-md">
                              {isStorefrontFeature ? "Visual Theme Engine" : "Approval Governance"}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Visual Dashboard Panel on Right for Wide Cards */}
                      {isLarge && (
                        <div className={`lg:col-span-5 w-full flex justify-center ${isStorefrontFeature ? "items-stretch" : "items-center"}`}>
                          {isStorefrontFeature ? (
                            <div className="w-full max-w-[320px] overflow-hidden rounded-2xl border border-slate-100 bg-slate-50 p-3 shadow-inner">
                              <div className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-1.5">
                                    <span className="h-2 w-2 rounded-full bg-rose-400" />
                                    <span className="h-2 w-2 rounded-full bg-amber-400" />
                                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                                  </div>
                                  <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[8px] font-black uppercase tracking-widest text-emerald-600">
                                    Branded
                                  </span>
                                </div>
                                <div className="rounded-xl bg-slate-950 p-3 text-white">
                                  <div className="flex items-center justify-between gap-3">
                                    <div>
                                      <div className="text-[8px] font-black uppercase tracking-[0.16em] text-[#ff8a5c]">
                                        YourBrand Cloud
                                      </div>
                                      <div className="mt-1 text-[11px] font-black">Private storefront</div>
                                    </div>
                                    <Monitor size={16} className="text-[#ff8a5c]" />
                                  </div>
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-2">
                                  {["SaaS", "Cloud", "UCaaS"].map((category) => (
                                    <div key={category} className="rounded-lg border border-slate-100 bg-slate-50 p-2">
                                      <div className="mb-2 h-6 rounded-md bg-[#ff5d22]/10" />
                                      <div className="text-[8px] font-black uppercase tracking-wider text-slate-500">{category}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full max-w-[300px] overflow-hidden rounded-xl border border-white/10 bg-slate-950 p-3 text-white shadow-inner">
                              <div className="flex items-center justify-between gap-2 border-b border-white/10 pb-2.5">
                                <div>
                                  <div className="text-[8px] font-black uppercase tracking-[0.18em] text-[#ff8a5c]">
                                    Workflow Manager
                                  </div>
                                  <div className="mt-0.5 text-[10px] font-black tracking-tight text-white">
                                    Enterprise approval flow
                                  </div>
                                </div>
                                <span className="rounded-full border border-[#93BD57]/20 bg-[#93BD57]/10 px-1.5 py-0.5 text-[7px] font-black uppercase tracking-widest text-[#93BD57]">
                                  Live
                                </span>
                              </div>

                              <div className="mt-2.5 space-y-1.5">
                                {[
                                  { label: "Intake", status: "Validated", tone: "bg-[#93BD57]" },
                                  { label: "Policy Gate", status: "Approved", tone: "bg-[#ff5d22]" },
                                  { label: "Provisioning", status: "Running", tone: "bg-sky-400" },
                                ].map((row, rowIndex) => (
                                  <div key={row.label} className="relative rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-2">
                                    {rowIndex < 2 && (
                                      <div className="absolute left-[15px] top-full h-1.5 w-px bg-white/12" />
                                    )}
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex min-w-0 items-center gap-2">
                                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${row.tone}`} />
                                        <span className="truncate text-[8px] font-black uppercase tracking-[0.12em] text-white/70">
                                          {row.label}
                                        </span>
                                      </div>
                                      <span className="rounded bg-white/8 px-1.5 py-0.5 text-[7px] font-black text-white/55">
                                        {row.status}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <div className="mt-2.5 grid grid-cols-3 gap-1.5">
                                {[
                                  { value: "04", label: "Open tasks" },
                                  { value: "99%", label: "SLA health" },
                                  { value: "12m", label: "Cycle time" },
                                ].map((metric) => (
                                  <div key={metric.label} className="rounded-lg border border-white/10 bg-white/[0.04] p-1.5 text-center">
                                    <div className="text-xs font-black text-white">{metric.value}</div>
                                    <div className="mt-0.5 text-[6.5px] font-black uppercase tracking-[0.1em] text-white/35">
                                      {metric.label}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Footer graphics for small cards */}
                      {!isLarge && (
                        <div className="w-full shrink-0 overflow-hidden bg-slate-50/60 rounded-xl p-3 border border-slate-100 flex flex-col gap-2">
                          {i === 1 ? (
                            /* Distribution tree graphic mockup */
                            <div className="flex justify-between items-center text-[9px] font-mono text-slate-400 font-bold">
                              <span className="bg-white border border-slate-100 px-1.5 py-0.5 rounded text-black text-[8px] shadow-sm">Vendor</span>
                              <span className="text-slate-300">to</span>
                              <span className="bg-[#ff5d22]/5 border border-[#ff5d22]/10 text-[#ff5d22] px-1.5 py-0.5 rounded text-[8px] shadow-sm">Tier 1 Partner</span>
                              <span className="text-slate-300">to</span>
                              <span className="bg-white border border-slate-100 px-1.5 py-0.5 rounded text-black text-[8px] shadow-sm">Client</span>
                            </div>
                          ) : (
                            /* Compliance badge row mockup */
                            <div className="flex flex-wrap items-center justify-center gap-1.5">
                              <span className="whitespace-nowrap text-[8px] bg-white border border-slate-100 text-slate-600 font-black uppercase px-2 py-0.5 rounded shadow-sm">ISO 27001</span>
                              <span className="whitespace-nowrap text-[8px] bg-emerald-50 border border-emerald-100 text-emerald-600 font-black uppercase px-2 py-0.5 rounded shadow-sm">GDPR</span>
                              <span className="whitespace-nowrap text-[8px] bg-blue-50 border border-blue-100 text-blue-600 font-black uppercase px-2 py-0.5 rounded shadow-sm">SOC 2</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </TiltCard>
              );
            })}
          </div>
        </div>
      </section>      

      <section className="relative overflow-hidden bg-slate-950 px-4 py-12 text-white sm:px-6 md:px-12 md:py-14">
        <div className="absolute inset-0 opacity-10">
          <NetworkLattice />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_20%,rgba(255,93,34,0.18),transparent_32%),radial-gradient(circle_at_82%_72%,rgba(14,165,233,0.10),transparent_30%)]" />
        <div className="relative z-10 mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <div>
            <div className="inline-flex rounded-full border border-[#ff5d22]/25 bg-[#ff5d22]/10 px-3 py-1 text-[9px] font-black uppercase tracking-[0.22em] text-[#ff8a5c]">
              Governance
            </div>
            <h2 className="mt-5 max-w-xl text-3xl font-black tracking-tight text-white md:text-4xl">
              Control How Services Are Bought And Consumed
            </h2>
            <p className="mt-4 max-w-lg text-sm font-bold leading-relaxed text-white/60">
              Enforce commercial policies across customers, partners and business units.
            </p>
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/45">Policy Console</span>
                <span className="rounded-full border border-[#93BD57]/20 bg-[#93BD57]/10 px-2 py-1 text-[8px] font-black uppercase tracking-widest text-[#93BD57]">Active</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {["Spend", "Eligibility", "Channel", "Audit"].map((label) => (
                  <div key={label} className="rounded-xl border border-white/8 bg-white/[0.035] px-3 py-3">
                    <div className="text-[8px] font-black uppercase tracking-widest text-white/35">{label}</div>
                    <div className="mt-1 h-1.5 rounded-full bg-white/10">
                      <div className="h-full w-4/5 rounded-full bg-[#ff5d22]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {commercePolicies.map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-xl transition-all hover:-translate-y-1 hover:border-[#ff5d22]/35 hover:bg-white/[0.06]"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#ff5d22]/15 text-[#ff8a5c]">
                    <card.icon size={17} />
                  </div>
                  <h3 className="text-sm font-black tracking-tight text-white">{card.title}</h3>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {card.items.map((item) => (
                    <span key={item} className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[9px] font-bold text-white/60">
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-12 px-4 sm:px-6 md:px-12 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            pill="Financial Operations"
            title="Built For Revenue Protection"
            subtitle="Reduce leakage and improve cash flow with automated financial governance."
            align="center"
            maxWidth="max-w-5xl"
          />

          <div className="mt-7 grid gap-4 lg:grid-cols-3">
            {financialControls.map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="relative overflow-hidden rounded-2xl border border-black/6 bg-white p-5 shadow-sm"
              >
                <div className="absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/60 to-transparent" />
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[8px] font-black uppercase tracking-[0.2em] text-black/30">Control 0{i + 1}</p>
                    <h3 className="mt-2 text-base font-black tracking-tight text-slate-950">{card.title}</h3>
                  </div>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-[#ff8a5c]">
                    <card.icon size={18} />
                  </div>
                </div>
                <div className="mt-5 grid gap-2">
                  {card.items.map((item) => (
                    <div key={item} className="rounded-xl border border-black/6 bg-slate-50 px-3 py-2 text-[10px] font-bold text-black/55">
                      {item}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-white px-4 py-10 sm:px-6 md:px-12 md:py-12">
        <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(90deg,rgba(15,23,42,0.035)_1px,transparent_1px),linear-gradient(180deg,rgba(15,23,42,0.035)_1px,transparent_1px)] bg-[size:42px_42px]" />
        <div className="relative z-10 mx-auto grid max-w-7xl gap-5 lg:grid-cols-[0.95fr_1.05fr] lg:items-stretch">
          <div className="flex h-full flex-col rounded-[2rem] border border-black/6 bg-white/80 p-5 shadow-sm backdrop-blur-sm md:p-6">
            <SectionHeader
              pill="Multi-Tier Commerce"
              title="One Platform. Unlimited Channel Models."
              subtitle="Support complex distribution ecosystems with automated partner operations."
              align="left"
            />

            <div className="mt-6 flex-1 rounded-2xl border border-black/6 bg-slate-950 p-4 text-white shadow-xl md:p-5">
              <div className="mb-4 flex items-center justify-between gap-4">
                <div>
                  <div className="text-[8px] font-black uppercase tracking-[0.22em] text-[#ff8a5c]">Partner Ladder</div>
                  <div className="mt-1 text-sm font-black text-white">Distribution hierarchy</div>
                </div>
                <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[8px] font-black uppercase tracking-widest text-white/45">
                  5 tiers
                </span>
              </div>

              <div className="grid gap-2">
                {["Vendor", "Distributor", "Master Reseller", "Reseller", "Customer"].map((tier, i, list) => (
                  <div key={tier} className="relative">
                    <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.045] px-4 py-3 transition-colors hover:border-[#ff5d22]/30 hover:bg-white/[0.07]">
                      <div className="flex items-center gap-3">
                        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#ff5d22]/15 text-[9px] font-black text-[#ff8a5c]">
                          {i + 1}
                        </span>
                        <span className="text-[9px] font-black uppercase tracking-[0.18em] text-white/38">Tier {i + 1}</span>
                      </div>
                      <span className="text-sm font-black text-white">{tier}</span>
                    </div>
                    {i < list.length - 1 && (
                      <div className="ml-[29px] h-3 w-px bg-[#ff5d22]/55" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex h-full flex-col rounded-[2rem] border border-black/6 bg-white p-5 shadow-sm md:p-6">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">Channel Operations</div>
                <h3 className="mt-2 text-2xl font-black tracking-tight text-slate-950 md:text-3xl">
                  Govern every tier from one operating layer.
                </h3>
              </div>
              <span className="w-fit rounded-full border border-[#ff5d22]/15 bg-[#ff5d22]/8 px-3 py-1 text-[8px] font-black uppercase tracking-widest text-[#ff5d22]">
                Multi-party ready
              </span>
            </div>

            <div className="grid flex-1 gap-3">
              {channelCommerce.map((card, i) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="group grid gap-4 rounded-2xl border border-black/6 bg-slate-50 p-4 transition-all hover:border-[#ff5d22]/25 hover:bg-white hover:shadow-lg md:grid-cols-[auto_1fr]"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-[#ff5d22] shadow-sm transition-colors group-hover:bg-[#ff5d22] group-hover:text-white">
                    <card.icon size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="text-sm font-black tracking-tight text-slate-950">{card.title}</h4>
                      <span className="text-[8px] font-black uppercase tracking-[0.16em] text-black/25">Module 0{i + 1}</span>
                    </div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {card.items.map((item) => (
                        <div key={item} className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-[9.5px] font-bold leading-relaxed text-black/50 shadow-sm">
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#ff5d22]" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-4 rounded-2xl border border-black/6 bg-slate-950 p-4 text-white shadow-inner">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-[8px] font-black uppercase tracking-[0.2em] text-[#ff8a5c]">
                    Route To Revenue
                  </div>
                  <div className="mt-1 text-sm font-black text-white">Partner motion coverage</div>
                </div>
                <div className="grid grid-cols-3 gap-2 sm:w-[310px]">
                  {[
                    { value: "5", label: "tiers" },
                    { value: "3", label: "motions" },
                    { value: "1", label: "ledger" }
                  ].map((metric) => (
                    <div key={metric.label} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-center">
                      <div className="text-base font-black text-[#93BD57]">{metric.value}</div>
                      <div className="text-[7px] font-black uppercase tracking-[0.12em] text-white/35">{metric.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                {["Onboard", "Assign Catalog", "Price & Margin", "Settle"].map((step, i, list) => (
                  <div key={step} className="flex flex-1 items-center gap-2">
                    <div className="flex min-h-[62px] flex-1 flex-col justify-center rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
                      <div className="text-[8px] font-black uppercase tracking-[0.16em] text-[#ff8a5c]">0{i + 1}</div>
                      <div className="mt-1 text-[10px] font-black text-white/75">{step}</div>
                    </div>
                    {i < list.length - 1 && (
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-[#ff8a5c]">
                        <ArrowRight size={13} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-slate-50 px-4 py-10 sm:px-6 md:px-12 md:py-12">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-black/8 to-transparent" />
        <div className="relative z-10 mx-auto max-w-7xl">
          <SectionHeader
            pill="Commission Management"
            title={<><span className="text-[#ff5d22]">Commission Management</span> for <br></br>Every Partner Model</>}
            subtitle="Manage channel partner and affiliate partner commissions through one governed quote-to-settlement flow, covering attribution, approvals, payouts, and reconciliation."
            align="center"
            maxWidth="max-w-5xl"
          />

          <div className="mx-auto mt-6 grid max-w-4xl gap-3 sm:grid-cols-3">
            {[
              { value: "Channel Partner", label: "Commission slabs" },
              { value: "Affiliate Partner", label: "Referral credit" },
              { value: "O2C", label: "Order-to-commission sync" }
            ].map((metric) => (
              <div key={metric.label} className="rounded-2xl border border-black/6 bg-white p-4 text-center shadow-sm">
                <div className="text-lg font-black tracking-tight text-slate-950">{metric.value}</div>
                <div className="mt-1 text-[9px] font-black uppercase tracking-[0.14em] text-black/35">{metric.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-[1.45fr_0.75fr] lg:items-stretch">
            <div className="relative overflow-hidden rounded-[2rem] border border-black/6 bg-white p-5 shadow-xl shadow-slate-200/70">
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#ff5d22]/10 blur-3xl" />
              <div className="relative z-10 flex flex-col gap-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">
                      Commission Cockpit
                    </div>
                    <h3 className="mt-2 text-xl font-black tracking-tight text-slate-950">
                      Marketplace Partner Settlement
                    </h3>
                    <p className="mt-2 max-w-xl text-[11px] font-bold leading-relaxed text-black/45">
                      A governed view of the order, product value, discount rules, partner attribution, commission accrual, and settlement readiness.
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <span className="rounded-lg bg-[#ff5d22]/10 px-3 py-1 text-[8px] font-black uppercase tracking-widest text-[#ff5d22]">
                      Rule review
                    </span>
                    <span className="rounded-lg bg-[#93BD57]/10 px-3 py-1 text-[8px] font-black uppercase tracking-widest text-[#4f8a22]">
                      Ledger ready
                    </span>
                  </div>
                </div>

                <div className="grid gap-4 xl:grid-cols-[1fr_1.1fr]">
                  <div className="rounded-2xl border border-black/6 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-[9px] font-black uppercase tracking-[0.18em] text-black/30">Offer Context</div>
                        <div className="mt-2 text-sm font-black leading-snug text-slate-950">
                          Cloud marketplace bundle
                        </div>
                        <div className="mt-2 text-[10px] font-bold leading-relaxed text-black/45">
                          Customer order converted through partner storefront with recurring billing and managed renewal eligibility.
                        </div>
                      </div>
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#08245c] text-white">
                        <ShoppingCart size={18} />
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-2">
                      {[
                        { label: "Revenue", value: "INR 4,200" },
                        { label: "Discount", value: "0.00%" },
                        { label: "Quantity", value: "2" }
                      ].map((item) => (
                        <div key={item.label} className="rounded-xl border border-black/6 bg-white p-3 text-center">
                          <div className="text-sm font-black text-slate-950">{item.value}</div>
                          <div className="mt-1 text-[7px] font-black uppercase tracking-[0.12em] text-black/35">{item.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="relative rounded-2xl border border-black/6 bg-white p-4">
                    <div className="absolute left-8 top-[74px] hidden h-[calc(100%-120px)] w-px bg-slate-200 md:block" />
                    {[
                      {
                        title: "Channel Partner",
                        desc: "Gold reseller commission on collected marketplace revenue.",
                        rule: "10.00%",
                        accrual: "INR 300.00",
                        tone: "bg-[#ff5d22]"
                      },
                      {
                        title: "Affiliate Partner",
                        desc: "Referral credit for influenced SaaS add-on opportunity.",
                        rule: "4.00%",
                        accrual: "INR 48.00",
                        tone: "bg-[#93BD57]"
                      }
                    ].map((partner) => (
                      <div key={partner.title} className="relative mb-3 last:mb-0 rounded-xl border border-black/6 bg-slate-50 p-3">
                        <div className="flex items-start gap-3">
                          <span className={`mt-1 h-3 w-3 shrink-0 rounded-full ${partner.tone} ring-4 ring-white`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <h4 className="text-xs font-black text-slate-950">{partner.title}</h4>
                              <span className="rounded-md bg-blue-50 px-2.5 py-1 text-[8px] font-black uppercase tracking-wider text-[#08245c]">
                                Rule {partner.rule}
                              </span>
                            </div>
                            <p className="mt-1 text-[9.5px] font-bold leading-relaxed text-black/45">{partner.desc}</p>
                            <div className="mt-2 flex items-center justify-between rounded-lg bg-white px-3 py-2">
                              <span className="text-[8px] font-black uppercase tracking-[0.14em] text-black/35">Accrual</span>
                              <span className="text-xs font-black text-slate-950">{partner.accrual}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-[1.15fr_0.85fr]">
                  <div className="rounded-2xl border border-black/6 bg-slate-950 p-4 text-white">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="text-[8px] font-black uppercase tracking-[0.18em] text-white/35">Approval Path</div>
                        <div className="mt-1 text-sm font-black">Order to payout control</div>
                      </div>
                      <span className="rounded-full bg-[#93BD57]/15 px-3 py-1 text-[8px] font-black uppercase tracking-widest text-[#93BD57]">
                        Reconciled
                      </span>
                    </div>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-stretch">
                      {["Order matched", "Rules validated", "Finance approval"].map((step, i, list) => (
                        <div key={step} className="flex flex-1 items-center gap-2">
                          <div className="flex min-h-[84px] flex-1 flex-col rounded-xl border border-white/10 bg-white/[0.04] p-3">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#ff5d22]/15 text-[10px] font-black text-[#ff8a5c]">
                              0{i + 1}
                            </div>
                            <div className="mt-2 text-[9px] font-black uppercase tracking-[0.1em] text-white/60">{step}</div>
                          </div>
                          {i < list.length - 1 && (
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-[#ff8a5c] sm:h-9 sm:w-9">
                              <ArrowRight size={14} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-black/6 bg-slate-50 p-4">
                    <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">Commission Summary</div>
                    <div className="mt-3 space-y-2">
                      {[
                        { label: "Eligible revenue", value: "INR 4,200.00" },
                        { label: "Approved accrual", value: "INR 348.00" },
                        { label: "Payout run", value: "Next cycle" }
                      ].map((row) => (
                        <div key={row.label} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
                          <span className="text-[8px] font-black uppercase tracking-[0.12em] text-black/35">{row.label}</span>
                          <span className="text-[11px] font-black text-slate-950">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-black/6 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[0.2em] text-[#ff5d22]">Controls</div>
                  <h3 className="mt-1 text-base font-black tracking-tight text-slate-950">Commission leakage controls</h3>
                </div>
                <ShieldCheck size={20} className="shrink-0 text-[#ff5d22]" />
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {commissionControls.map((control) => (
                  <div key={control} className="flex items-center gap-2 rounded-xl border border-black/6 bg-slate-50 px-3 py-2 text-[10px] font-bold text-black/55">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#ff5d22]" />
                    {control}
                  </div>
                ))}
              </div>

              <div className="mt-5 border-t border-black/6 pt-5">
                <div className="mb-3 text-[9px] font-black uppercase tracking-[0.2em] text-black/30">
                  Commission Workflow
                </div>
                <div className="grid gap-3">
                  {commissionPrograms.map((program, i) => (
                    <motion.div
                      key={program.title}
                      initial={{ opacity: 0, x: 14 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="group flex gap-3 rounded-xl border border-transparent bg-slate-50 px-3 py-3 transition-all hover:border-[#ff5d22]/20 hover:bg-[#ff5d22]/5"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-[#ff5d22] shadow-sm transition-colors group-hover:bg-[#ff5d22] group-hover:text-white">
                        <program.icon size={15} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-[11px] font-black leading-snug text-slate-950">{program.title}</h4>
                        <p className="mt-1 text-[9.5px] font-bold leading-relaxed text-black/45">{program.desc}</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {program.items.map((item) => (
                            <span key={item} className="rounded-md bg-white px-2 py-1 text-[7px] font-black uppercase tracking-[0.1em] text-black/35 shadow-sm">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-12 px-4 sm:px-6 md:px-12 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_18%,rgba(255,93,34,0.16),transparent_34%)]" />
        <div className="relative z-10 mx-auto max-w-7xl">
          <SectionHeader
            pill="Channel Partner Operations"
            title={<span className="text-white">Expand Revenue Across Every Channel</span>}
            subtitle={<span className="text-white/60">Equip partners with the tools, visibility, and support needed to grow customer relationships and sales.</span>}
            align="center"
            maxWidth="max-w-5xl"
          />

          <div className="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {channelPartnerLifecycle.map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-5"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-[#ff8a5c]">
                    <card.icon size={17} />
                  </div>
                  <h3 className="text-sm font-black tracking-tight text-white">{card.title}</h3>
                </div>
                <div className="mt-4 space-y-2">
                  {card.items.map((item) => (
                    <div key={item} className="flex items-center gap-2 text-[10px] font-bold text-white/60">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#ff5d22]" />
                      {item}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-12 px-4 sm:px-6 md:px-12 bg-white">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-black/6 bg-slate-50 p-5 shadow-sm md:p-7">
          <div className="grid gap-7 lg:grid-cols-[0.72fr_1.28fr] lg:items-center">
            <div>
              <div className="inline-flex rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/8 px-3 py-1 text-[9px] font-black uppercase tracking-[0.22em] text-[#ff5d22]">
                Enterprise Governance
              </div>
              <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 md:text-4xl">
                Enterprise Controls <br></br>Built In
              </h2>
              <p className="mt-3 max-w-lg text-sm font-bold leading-relaxed text-black/55">
                Operate with confidence using built-in governance and operational safeguards.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {enterpriseControls.map((control, i) => (
                <motion.div
                  key={control.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.035 }}
                  className="rounded-xl border border-black/6 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:border-[#ff5d22]/25"
                >
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-slate-950 text-[#ff8a5c]">
                    <control.icon size={15} />
                  </div>
                  <div className="text-[10px] font-black leading-snug text-slate-950">{control.title}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#060816] py-14 md:py-16 px-4 sm:px-6 md:px-12 text-white">
        {/* Ambient Glow */}
        <div className="absolute inset-0">
          <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 md:w-[220px] md:h-[220px] lg:w-[420px] lg:h-[420px] bg-[#ff5d22]/8 blur-[120px] rounded-full" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-12 items-center">

          {/* Left - Compact description */}
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 backdrop-blur-xl">
              <div className="w-1.5 h-1.5 rounded-full bg-[#ff5d22]" />
              <span className="text-[9px] uppercase tracking-[0.25em] font-black text-white/50">
                Competitive Advantage
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl text-white/90 leading-tight tracking-tight max-w-xl">
              Unified Commerce
              <span className="block text-[#ff5d22]">
                Without Operational Debt.
              </span>
            </h2>

            <p className="max-w-lg text-xs leading-relaxed text-white/60 font-medium">
              Ditch fragmented systems, custom connectors, and continuous code rewrites. Switch to a pre-integrated lifecycle container.
            </p>

            {/* Compact bullet layouts */}
            <div className="grid sm:grid-cols-2 gap-2.5 pt-2">
              {[
                "Zero-Downtime Migration",
                "Billing Automation",
                "Global Compliance",
                "API-First Provisioning"
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2.5 rounded-xl border border-white/5 bg-white/[0.02] px-3.5 py-2.5 hover:border-[#ff5d22]/20 transition-all duration-300"
                >
                  <div className="w-5 h-5 rounded-md bg-[#ff5d22]/10 flex items-center justify-center shrink-0">
                    <Check
                      size={10}
                      strokeWidth={3}
                      className="text-[#ff5d22]"
                    />
                  </div>

                  <span className="text-[11px] font-bold text-white/70">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Precision Compact Matrix Dashboard */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.01] backdrop-blur-3xl p-5 md:p-6 shadow-[0_24px_80px_rgba(0,0,0,0.5)]">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />

              <div className="mb-5 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.15em] font-black text-white/80">
                  Operational Shift Matrix
                </div>
                <div className="text-[8px] font-mono text-[#93BD57] px-2 py-0.5 rounded bg-[#93BD57]/10 border border-[#93BD57]/20 font-black uppercase">
                  Audit Completed
                </div>
              </div>

              {/* High Contrast Compact Table Headers */}
              <div className="grid grid-cols-12 gap-2 px-1 pb-2 border-b border-white/5 text-[8px] font-black uppercase tracking-widest text-white/30">
                <div className="col-span-3">Category</div>
                <div className="col-span-4">Legacy Platform</div>
                <div className="col-span-5 pl-2">AppGallop Engine</div>
              </div>

              {/* Comparison List */}
              <div className="divide-y divide-white/5">
                {[
                  {
                    domain: "Catalog Sync",
                    legacy: "Manual & batch updates",
                    unified: "Real-time API sync",
                    impact: "Days to minutes sync",
                    accent: "24/7 Auto"
                  },
                  {
                    domain: "Partner Tiers",
                    legacy: "Fragmented portal setups",
                    unified: "Single Reseller hub",
                    impact: "1 unified view",
                    accent: "1 Portal"
                  },
                  {
                    domain: "Management",
                    legacy: "Hardcoded coding",
                    unified: "Dynamic Bundle engine",
                    impact: "Zero engineering delay",
                    accent: "10x Speed"
                  },
                  {
                    domain: "Tax & Compliance",
                    legacy: "Manual regional checks",
                    unified: "Automated controls",
                    impact: "ISO & GDPR built-in",
                    accent: "160+ regions"
                  }
                ].map((row, i) => (
                  <div key={i} className="py-3 grid grid-cols-12 gap-2 items-center group/row">
                    <div className="col-span-3 text-[9px] font-black text-white/40 group-hover/row:text-[#93BD57] transition-colors uppercase tracking-wider">{row.domain}</div>

                    <div className="col-span-4 text-[10px] font-semibold text-white/30 line-through decoration-white/20 truncate">
                      {row.legacy}
                    </div>

                    <div className="col-span-5 flex items-center justify-between gap-2 pl-2">
                      <div className="min-w-0">
                        <div className="text-[11px] font-black text-white flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#93BD57] shrink-0" />
                          <span className="truncate">{row.unified}</span>
                        </div>
                        <div className="text-[9px] font-bold text-white/45 mt-0.5 ml-3.5">
                          {row.impact}
                        </div>
                      </div>
                      <div className="shrink-0 text-[8px] font-mono font-bold bg-white/5 border border-white/10 px-2 py-0.5 rounded text-white/60">
                        {row.accent}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Precision Outcomes Footer */}
              <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-white/[0.01] border border-white/5 hover:border-white/10 transition-colors">
                  <span className="text-[8px] uppercase tracking-wider font-extrabold text-white/70 block">OPERATIONAL VALUE</span>
                  <span className="text-xl font-black text-[#93BD57] mt-0.5 block">4.2x <span className="text-[9px] font-semibold text-[#93BD57] ml-1">Speed Multiplier</span></span>
                </div>
                <div className="p-3.5 rounded-xl bg-[#ff5d22]/5 border border-[#ff5d22]/10 hover:border-[#ff5d22]/20 transition-colors">
                  <span className="text-[8px] uppercase tracking-wider font-extrabold text-white/70 block">LIABILITY REDUCTION</span>
                  <span className="text-xl font-black text-[#93BD57] mt-0.5 block">100% <span className="text-[9px] font-semibold text-[#93BD57] ml-1">Process Automated</span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 md:py-12 px-4 sm:px-6 md:px-8 bg-white">
        <div className="max-w-4xl mx-auto rounded-2xl bg-slate-950 p-6 md:p-10 text-center relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-tr from-[#ff5d22]/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
          <div className="relative z-10 space-y-6">
            <h2 className="text-2xl md:text-3xl font-black text-white tracking-tighter leading-tight">
              Ready to Orchestrate <br />
              <span className="text-[#ff5d22]">Your Growth?</span>
            </h2>
            <p className="text-white/60 text-sm md:text-base font-bold max-w-xl mx-auto">
              Join the world's leading Telecoms and MSPs who rely on AppGallop to power their global digital ecosystems.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 pt-4">
              <Button
                variant="primary"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="w-full sm:w-auto !px-6 sm:!px-8 !py-3 sm:!py-4 bg-[#ff5d22] hover:bg-white hover:text-black shadow-lg"
              >
                Request Live Environment
              </Button>
              <Button
                variant="outline"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="w-full sm:w-auto !px-6 sm:!px-8 !py-3 sm:!py-4 border-white/10 text-white hover:border-white"
              >
                Connect with Architecture Team
              </Button>
            </div>
          </div>
        </div>
      </section>

      <ContactSection id="contact" />
    </div>
  );
};

const ProductPage = () => {
  const { productId } = useParams();

  // Handle special cases
  if (!productId || productId === "marketplace") {
    return <MarketplacePage />;
  }

  if (productId === "rms") {
    return <RMSPage />;
  }

  if (productId === "revsimply" || productId === "cpq") {
    return <CPQPage />;
  }

  if (productId === "uc-billing") {
    return <UCBillingPage />;
  }

  const dataKey = productId || "marketplace";
  const data = productData[dataKey] || productData.marketplace;
  const theme = heroThemes[dataKey] || heroThemes.marketplace;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pt-20 pb-12 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Product Hero */}
        <div className="mb-12">
          <ProductHero data={data} theme={theme} />
        </div>

        {/* Operational Modules */}
        <div className="mb-20">
          <div className="max-w-3xl mb-12">
            <SectionHeader pill="Product Framework" title="Operational Intelligence Modules." align="left" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.modules.map((m: any, i: number) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="p-8 rounded-[3rem] bg-slate-50 border border-black/5 group hover:border-[#ff5d22]/30 hover:bg-white transition-all cursor-default"
              >
                <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-[#ff5d22] mb-6 group-hover:bg-[#ff5d22] group-hover:text-white transition-all duration-500">
                  <m.icon size={24} strokeWidth={1.5} />
                </div>
                <h4 className="text-base font-black uppercase tracking-tight text-black mb-4">{m.title}</h4>
                <p className="text-black/50 text-[11px] font-bold leading-relaxed">{m.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Platform Capabilities */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-20 bg-slate-950 p-6 md:p-10 rounded-[4rem] relative overflow-hidden">
          <div className="hidden md:block absolute top-0 right-0 md:w-[300px] md:h-[300px] lg:w-[600px] lg:h-[600px] bg-[#ff5d22]/10 blur-[120px] rounded-full translate-x-1/2 -translate-y-1/2" />
          <div className="lg:col-span-7 space-y-10 relative z-10">
            <h2 className="text-3xl md:text-4xl font-black text-white tracking-tighter leading-tight">
              Core Architecture <br />
              <span className="text-[#ff5d22]">Capabilities.</span>
            </h2>
            {/* If there are exactly 4 capabilities, render a 2x2 card layout: big / small then small / big */}
            {data.capabilities.length === 4 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data.capabilities.map((cap: string, i: number) => (
                  <div key={i} className={`p-6 rounded-2xl bg-white/5 border border-white/10 transition-all ${(i === 0 || i === 3)
                    ? 'min-h-[220px] md:min-h-[340px] flex items-start'
                    : 'min-h-[120px] md:min-h-[160px] flex items-center'
                    }`}>
                    <div className="w-3 h-3 rounded-full bg-[#ff5d22] mr-4 mt-1" />
                    <div className="text-white/80 font-bold">{cap}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-x-12 gap-y-6">
                {data.capabilities.map((cap: string, i: number) => (
                  <div key={i} className="flex items-center gap-4 group">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#ff5d22] group-hover:scale-150 transition-transform" />
                    <span className="text-white/60 font-medium tracking-tight group-hover:text-white transition-colors">{cap}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-5 relative z-10">
            <div className="p-8 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[3rem] shadow-massive">
              <MockDashboard className="opacity-60" />
            </div>
          </div>
        </div>

        {/* Quantified Business Outcomes */}
        <div className="mb-16">
          <SectionHeader pill="Success Metrics" title="Measurable Business Outcomes." align="center" />
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {data.metrics.map((metric: any, i: number) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="p-8 rounded-[3.5rem] bg-white border border-black/5 shadow-sm text-center group hover:bg-slate-50 transition-all border-b-4 hover:border-b-[#ff5d22]"
              >
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-black/30 mb-6">{metric.label}</p>
                <div className="text-4xl font-black tracking-tighter text-black mb-6 group-hover:scale-110 transition-transform duration-500">{metric.value}</div>
                <p className="text-black/50 text-[11px] font-bold leading-relaxed">{metric.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
        <ContactSection id="contact" />
      </div>
    </motion.div>
  );
};

const RMSPage = () => {
  const data = productData.rms;
  const theme = heroThemes.rms;

  return (
    <RMSPageContent
      data={data}
      theme={theme}
      ProductHero={ProductHero}
      ContactSection={ContactSection}
      SectionHeader={SectionHeader}
      MockDashboard={MockDashboard}
      Button={Button}
    />
  );
};

const CPQPage = () => {
  const data = productData.revsimply;
  const theme = heroThemes.revsimply;
  const { scrollY } = useScroll();
  const [activeJourneyStep, setActiveJourneyStep] = useState(0);
  const [isJourneyPlaying, setIsJourneyPlaying] = useState(false); // Disabled auto-play
  const [activeAIFeature, setActiveAIFeature] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);

  useEffect(() => {
    if (!containerRef.current) return;
    const handleResize = () => {
      setContainerWidth(containerRef.current?.offsetWidth || 0);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 3D Scroll Effects
  const heroParallax = useTransform(scrollY, [0, 800], [0, -200]);
  const journeyParallax = useTransform(scrollY, [400, 1200], [0, -150]);
  const aiParallax = useTransform(scrollY, [800, 1600], [0, -100]);
  const capabilitiesParallax = useTransform(scrollY, [1200, 2000], [0, -80]);
  const integrationParallax = useTransform(scrollY, [1600, 2400], [0, -60]);

  // Renew to Revenue Journey Steps
  const journeySteps = [
    {
      title: "OEM Data Ingestion",
      desc: "Renewal data is captured through structured CSV and Excel uploads, AI powered OCR for PDF quotes, and REST API integrations for up to five vendors out of the box. All records are validated, deduplicated, and normalized against product and partner masters before entering workflow.",
      icon: Search,
      color: "from-blue-500 to-cyan-400",
      stage: "Data Collection",
      aiFeature: "AI-Powered OCR",
      capabilities: ["CSV & Excel upload", "AI powered PDF OCR", "REST API integration", "Data validation", "SKU normalization", "Partner master lookup"]
    },
    {
      title: "Smart Opportunity Routing",
      desc: "Renewal opportunities are auto generated from vendor data and contracts nearing 7, 15, and 30 day expiry milestones. Each is routed to the right renewal specialist via vendor mapping with instant notifications. Incomplete data is flagged before quoting.",
      icon: Zap,
      color: "from-[#0F172A] to-[#ff5d22]",
      stage: "Opportunity Management",
      aiFeature: "Smart Routing Engine",
      capabilities: ["Auto opportunity creation", "Vendor based assignment", "Expiry tracking", "Email alerts", "Data enrichment flagging"]
    },
    {
      title: "AI Assisted Vendor Interaction",
      desc: "Specialists raise Service Requests directly from opportunities to clarify pricing, discounts, terms, or SKUs. The AI engine reads vendor email responses, maps them to the correct request, and maintains a complete searchable interaction log with SLA timestamps.",
      icon: Bot,
      color: "from-emerald-500 to-teal-400",
      stage: "Vendor Communication",
      aiFeature: "Email Intelligence",
      capabilities: ["SR creation from opportunity", "AI email mapping", "Drafting assistance", "SLA tracking", "Interaction audit log"]
    },
    {
      title: "Quote Creation and Pricing Governance",
      desc: "Structured quotes can be built from single or multiple opportunities. Historical pricing is pulled from SAP or RMS records. Partner discounts, multi currency hedging, and pricing exceptions are governed through a configured Delegation of Authority approval matrix before release.",
      icon: FileCheck,
      color: "from-purple-500 to-indigo-400",
      stage: "Quote Management",
      aiFeature: "Pricing Intelligence",
      capabilities: ["Multi opportunity quoting", "SAP price pull", "DOA approval routing", "Discount rules", "Multi currency handling", "Solution document upload"]
    },
    {
      title: "Partner Distribution and Negotiation",
      desc: "Quotes are shared via secure email links or the partner portal. Partners can accept items, propose revisions, or comment within a structured negotiation thread. All revisions and approvals are captured in a time stamped audit trail. Updated quotes are reissued within the platform.",
      icon: Users,
      color: "from-rose-500 to-pink-400",
      stage: "Partner Engagement",
      aiFeature: "Negotiation Tracking",
      capabilities: ["Email link acceptance", "Partner portal access", "Line item negotiation", "Quote revision and reissue", "Full audit trail"]
    },
    {
      title: "Order Creation and ERP Sync",
      desc: "Partner acceptance converts quotes into orders automatically. Vendor portal submission is tracked, and order status is updated within RMS. For SAP integrated environments, Sales Orders are pushed directly to ERP, accelerating billing and eliminating manual entry.",
      icon: RefreshCw,
      color: "from-green-500 to-emerald-400",
      stage: "Order Processing",
      aiFeature: "ERP Integration",
      capabilities: ["Auto quote to order conversion", "Vendor confirmation tracking", "SAP Sales Order push", "Fulfillment sync", "Order logging"]
    },
    {
      title: "Revenue Intelligence and KPI Dashboards",
      desc: "Performance is tracked across the entire lifecycle, including renewal pipeline by vendor, SLA adherence, quote to close ratios, churn risk, aging, and partner contribution. Leadership gains real time visibility into forecast accuracy and revenue protection.",
      icon: BarChart3,
      color: "from-indigo-500 to-purple-400",
      stage: "Analytics",
      aiFeature: "Revenue Intelligence",
      capabilities: ["Vendor funnel reporting", "SLA dashboards", "Renewal risk matrix", "Aging analysis", "Partner contribution analytics", "Pipeline forecasting"]
    }
  ];

  // AI-Powered Features
  const aiFeatures = [
    {
      title: "Predictive Quote Optimization",
      desc: "Machine learning algorithms analyze historical data to suggest optimal pricing strategies and product bundles that maximize win rates and margins.",
      icon: Bot,
      metrics: ["95% Quote Accuracy", "40% Faster Generation", "25% Higher Win Rate"],
      color: "from-blue-500 to-cyan-400"
    },
    {
      title: "Intelligent Renewal Forecasting",
      desc: "Advanced analytics predict renewal likelihood, identify expansion opportunities, and recommend proactive engagement strategies.",
      icon: BarChart3,
      metrics: ["90% Forecast Accuracy", "30% Revenue Growth", "85% Retention Rate"],
      color: "from-[#0F172A] to-[#ff5d22]"
    },
    {
      title: "Smart Contract Analysis",
      desc: "Natural language processing extracts key terms, identifies risks, and ensures compliance across complex enterprise agreements.",
      icon: FileText,
      metrics: ["99% Compliance Rate", "75% Faster Review", "Zero Contract Errors"],
      color: "from-emerald-500 to-teal-400"
    },
    {
      title: "Dynamic Pricing Intelligence",
      desc: "Real-time market analysis and competitor intelligence inform pricing decisions with margin optimization and risk assessment.",
      icon: LineChart,
      metrics: ["15% Margin Improvement", "Real-time Adjustments", "Market-driven Pricing"],
      color: "from-purple-500 to-indigo-400"
    }
  ];

  // Enhanced CPQ Capabilities
  const cpqCapabilities = [
    {
      title: "Neural Product Configuration",
      desc: "AI-driven product bundling that understands complex dependencies and suggests optimal configurations.",
      icon: Settings,
      benefits: ["Reduced Configuration Errors", "Faster Quote Generation", "Optimized Product Mix"]
    },
    {
      title: "Margin Intelligence Engine",
      desc: "Real-time margin analysis with cost optimization and profitability insights across all product lines.",
      icon: Coins,
      benefits: ["Improved Profitability", "Cost Transparency", "Margin Protection"]
    },
    {
      title: "Compliance Automation",
      desc: "Automated regulatory compliance checking with global tax calculation and contract term validation.",
      icon: Shield,
      benefits: ["Zero Compliance Violations", "Global Tax Accuracy", "Automated Auditing"]
    },
    {
      title: "Revenue Recognition Engine",
      desc: "FASB/IFRS compliant revenue scheduling with automated journal entries and financial reporting.",
      icon: Database,
      benefits: ["Accounting Compliance", "Automated Reporting", "Revenue Visibility"]
    }
  ];

  // Remove auto-play effect - only manual navigation
  // useEffect(() => {
  //   if (!isJourneyPlaying) return undefined;
  //   const interval = setInterval(() => {
  //     setActiveJourneyStep((prev) => (prev + 1) % journeySteps.length);
  //   }, 4000);
  //   return () => clearInterval(interval);
  // }, [isJourneyPlaying, journeySteps.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        setActiveJourneyStep(prev => prev > 0 ? prev - 1 : prev);
      } else if (event.key === 'ArrowRight') {
        setActiveJourneyStep(prev => prev < journeySteps.length - 1 ? prev + 1 : prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [journeySteps.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveAIFeature((prev) => (prev + 1) % aiFeatures.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [aiFeatures.length]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pt-20 pb-8 bg-white overflow-hidden">
      {/* Hero Section */}
      <motion.div
        style={{ y: heroParallax }}
        className="max-w-7xl mx-auto px-6 md:px-12 mb-12 relative"
      >
        <div className="absolute -inset-20 opacity-30">
          <NetworkLattice />
        </div>
        <div className="relative z-10">
          <ProductHero data={data} theme={theme} />
        </div>
      </motion.div>

      {/* Renew to Revenue Journey */}
      <section className="py-12 bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-5">
          <NetworkLattice />
        </div>

        {/* Floating Geometric Shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-16 right-16 w-20 h-20 border-2 border-[#ff5d22]/20 rounded-lg"
          />
          <motion.div
            animate={{
              rotate: [360, 0],
              y: [0, -15, 0]
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-24 left-12 w-12 h-12 bg-blue-500/10 rounded-full"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-5 gap-8 items-start">

            {/* Left Content - 2 columns */}
            <div className="lg:col-span-2">
              <div className="sticky top-20 space-y-6">
                {/* Header */}
                <div className="space-y-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="overflow-hidden"
                  >
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/20 text-xs font-bold text-[#ff5d22] mb-4">
                      Renew to Revenue (R2R)
                    </div>
                  </motion.div>

                  <motion.h2
                    initial={{ opacity: 0, rotateX: -15 }}
                    whileInView={{ opacity: 1, rotateX: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-3xl md:text-4xl font-black leading-tight tracking-tight"
                  >
                    <span className="bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent">
                      From Contract Expiry to Realized Revenue in One
                    </span>
                    <span className="block bg-gradient-to-r from-[#ff5d22] to-[#d41820] bg-clip-text text-transparent">
                      Continuous Workflow
                    </span>
                  </motion.h2>
                </div>

                {/* Navigation Controls */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setActiveJourneyStep(prev => prev > 0 ? prev - 1 : journeySteps.length - 1)}
                    disabled={activeJourneyStep === 0}
                    className={`w-10 h-10 rounded-full border-2 bg-white flex items-center justify-center transition-all duration-300 shadow-sm hover:shadow-md ${activeJourneyStep === 0
                      ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                      : 'border-slate-300 text-slate-600 hover:border-[#ff5d22] hover:text-[#ff5d22] hover:scale-105'
                      }`}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={() => setActiveJourneyStep(prev => prev < journeySteps.length - 1 ? prev + 1 : 0)}
                    disabled={activeJourneyStep === journeySteps.length - 1}
                    className={`w-10 h-10 rounded-full border-2 bg-white flex items-center justify-center transition-all duration-300 shadow-sm hover:shadow-md ${activeJourneyStep === journeySteps.length - 1
                      ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                      : 'border-slate-300 text-slate-600 hover:border-[#ff5d22] hover:text-[#ff5d22] hover:scale-105'
                      }`}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Right Carousel - 3 columns */}
            <div className="lg:col-span-3">
              <div className="relative z-20">
                {/* Carousel Container with proper boundaries */}
                <div className="relative">
                  {/* Visible area container - cleanly confined and compact */}
                  <div ref={containerRef} className="overflow-hidden relative py-2" style={{ width: '100%', minHeight: '370px' }}>
                    <motion.div
                      className="flex gap-4 py-2 px-1"
                      animate={{
                        x: `${(() => {
                          const totalWidth = journeySteps.length * (330 + 16) - 16;
                          const targetX = -activeJourneyStep * (330 + 16);
                          const clampedX = containerWidth > 0
                            ? Math.max(containerWidth - totalWidth, targetX)
                            : targetX;
                          return clampedX > 0 ? 0 : clampedX;
                        })()
                          }px`
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30
                      }}
                      style={{
                        width: `${journeySteps.length * (330 + 16)}px` // Compact total width
                      }}
                    >
                      {journeySteps.map((step, i) => {
                        const IconComponent = step.icon;
                        const isActive = activeJourneyStep === i;

                        return (
                          <motion.div
                            key={i}
                            className="flex-shrink-0 relative"
                            style={{ width: '330px' }} // Compact width
                            onClick={() => setActiveJourneyStep(i)}
                          >
                            <motion.div
                              whileHover={{
                                y: -4,
                                scale: 1.01,
                                zIndex: 10,
                                transition: { duration: 0.2 }
                              }}
                              className={`relative p-5 rounded-2xl border bg-white cursor-pointer transition-all duration-300 h-full min-h-[310px] shadow-sm hover:shadow-md ${isActive
                                ? 'border-[#ff5d22] shadow-md shadow-[#ff5d22]/10 z-20'
                                : 'border-slate-200/80 hover:border-slate-350 hover:shadow'
                                }`}
                            >

                              {/* Card Content */}
                              <div className="space-y-4 h-full flex flex-col">
                                {/* Content */}
                                <div className="space-y-3 flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="w-8 h-8 rounded-lg bg-[#ff5d22]/5 flex items-center justify-center text-[#ff5d22]">
                                      <IconComponent size={14} />
                                    </div>
                                    <span className="text-[10px] font-mono font-bold tracking-wider text-slate-400 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded">
                                      {step.stage}
                                    </span>
                                  </div>
                                  <h4 className="text-sm font-black text-slate-900 leading-snug">
                                    {step.title}
                                  </h4>
                                  <p className="text-slate-500 text-[11px] leading-relaxed font-semibold">
                                    {step.desc}
                                  </p>

                                  {/* Key Capabilities */}
                                  <div className="space-y-1.5">
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">
                                      Key capabilities
                                    </span>
                                    <div className="flex flex-wrap gap-1">
                                      {step.capabilities?.slice(0, 4).map((capability, j) => (
                                        <motion.span
                                          key={j}
                                          whileHover={{ scale: 1.02 }}
                                          className="px-2 py-0.5 bg-slate-50 text-slate-600 text-[10px] font-bold rounded border border-slate-150 transition-colors"
                                        >
                                          {capability}
                                        </motion.span>
                                      ))}
                                    </div>
                                  </div>
                                </div>

                                {/* Bottom Section - Always at bottom */}
                                <div className="flex justify-end mt-auto pt-2">
                                  <div className="flex items-center gap-1.5 text-base font-black">
                                    <span className="text-slate-400">
                                      {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <span className="text-slate-300">
                                      /{String(journeySteps.length).padStart(2, '0')}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Active Indicator */}
                              {isActive && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="absolute -top-1.5 -right-1.5 w-4.5 h-4.5 bg-[#ff5d22] rounded-full flex items-center justify-center shadow-md"
                                >
                                  <div className="w-1.5 h-1.5 bg-white rounded-full" />
                                </motion.div>
                              )}

                              {/* Interactive Hover Effects */}
                              <motion.div
                                className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#ff5d22]/3 to-transparent opacity-0 pointer-events-none"
                                whileHover={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                              />

                              {/* Pulse Animation for Active Card */}
                              {isActive && (
                                <motion.div
                                  className="absolute inset-0 overflow-visible rounded-2xl border border-[#ff5d22]/20"
                                  animate={{
                                    scale: [1, 1.01, 1],
                                    opacity: [0.5, 0.8, 0.5]
                                  }}
                                  transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                  }}
                                />
                              )}
                            </motion.div>
                          </motion.div>
                        );
                      })}
                    </motion.div>
                  </div>
                </div>

                {/* Progress Indicators */}
                <div className="flex justify-center mt-6 gap-2">
                  {journeySteps.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveJourneyStep(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${activeJourneyStep === i
                        ? "w-6 bg-[#ff5d22]"
                        : "w-1.5 bg-slate-300 hover:bg-slate-400"
                        }`}
                    />
                  ))}
                </div>

                {/* Navigation Info */}
                <div className="text-center mt-3 text-sm text-slate-500">
                  Step {activeJourneyStep + 1} of {journeySteps.length}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI-Powered Features */}
      <section className="py-8 bg-white relative overflow-hidden">
        <motion.div
          style={{ y: aiParallax, rotateX: useTransform(scrollY, [800, 1600], [0, 15]) }}
          className="absolute inset-0 opacity-10"
        >
          <NetworkLattice />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-[#ff5d22]/5" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            pill="Artificial Intelligence"
            title="AI-Powered Revenue Intelligence"
            subtitle="Leverage machine learning and predictive analytics to optimize every aspect of your quote-to-cash process."
            align="center"
          />

          <div className="mt-8 grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeAIFeature}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-5"
                >
                  {(() => {
                    const currentFeature = aiFeatures[activeAIFeature];
                    const IconComponent = currentFeature.icon;
                    return (
                      <>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-slate-100 border border-black/5 flex items-center justify-center text-black/70 shadow-sm">
                            <IconComponent size={20} />
                          </div>
                          <h3 className="text-xl font-black tracking-tight text-black">
                            {currentFeature.title}
                          </h3>
                        </div>

                        <p className="text-black/60 text-base font-bold leading-relaxed">
                          {currentFeature.desc}
                        </p>

                        <div className="grid grid-cols-3 gap-3">
                          {currentFeature.metrics.map((metric, i) => (
                            <div key={i} className="text-center p-3 rounded-xl bg-slate-50 border border-black/5">
                              <div className="text-sm font-black text-[#2563EB] mb-1">
                                {metric}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center gap-2">
                {aiFeatures.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveAIFeature(i)}
                    className={`h-2 rounded-full transition-all duration-500 ${activeAIFeature === i ? "w-8 bg-[#ff5d22]" : "w-2 bg-black/20 hover:bg-black/40"
                      }`}
                    aria-label={`Show AI feature ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-[#ff5d22]/10 to-transparent blur-3xl rounded-[2.5rem]" />
              <div className="relative bg-slate-50 rounded-[2rem] border border-black/5 p-6">
                <motion.img
                  src={aiRevenueImage}
                  alt="AI revenue"
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-fill rounded-[1.5rem] transition-transform duration-500 ease-out hover:scale-[1.02]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CPQ Capabilities */}
      <section className="py-8 bg-slate-50 relative overflow-hidden">
        <motion.div
          style={{
            y: capabilitiesParallax,
            rotateZ: useTransform(scrollY, [1200, 2000], [0, 5])
          }}
          className="absolute inset-0 opacity-10"
        >
          <NetworkLattice />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-emerald-500/5" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            pill="Advanced Capabilities"
            title="Next-Generation CPQ Engine"
            subtitle="Built for complex enterprise sales with intelligent automation and real-time optimization."
            align="center"
          />

          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cpqCapabilities.map((capability, i) => (
              <TiltCard key={i} delay={i * 0.05}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -4, rotateX: 3, scale: 1.02 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="p-5 rounded-2xl bg-white border border-black/5 group hover:shadow-lg transition-all h-full"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotateY: 180 }}
                    transition={{ duration: 0.3 }}
                    className="w-10 h-10 rounded-xl bg-slate-50 border border-black/10 flex items-center justify-center text-slate-700 mb-4 group-hover:bg-slate-900 group-hover:text-white transition-all duration-500"
                  >
                    <capability.icon size={18} />
                  </motion.div>
                  <h4 className="text-base font-black tracking-tight text-black mb-2">
                    {capability.title}
                  </h4>
                  <p className="text-black/60 text-xs font-bold leading-relaxed mb-4">
                    {capability.desc}
                  </p>
                  <div className="space-y-1">
                    {capability.benefits.slice(0, 2).map((benefit, j) => (
                      <div key={j} className="flex items-center gap-1.5">
                        <div className="w-1 h-1 rounded-full bg-slate-400" />
                        <span className="text-[9px] font-bold text-black/55">
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* Integration & Ecosystem */}
      <section className="py-8 bg-white relative overflow-hidden">
        <motion.div
          style={{
            y: integrationParallax,
            rotateX: useTransform(scrollY, [1600, 2400], [0, -10])
          }}
          className="absolute inset-0 opacity-10"
        >
          <NetworkLattice />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-rose-500/5" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <SectionHeader
                pill="Enterprise Integration"
                title="Seamless Ecosystem Connectivity"
                subtitle="Connect with your existing CRM, ERP, and financial systems for unified revenue operations."
                align="left"
              />

              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: "Salesforce", icon: Shield, desc: "Native CRM Integration" },
                  { name: "NetSuite", icon: Database, desc: "ERP Synchronization" },
                  { name: "Microsoft", icon: Globe, desc: "Office 365 Suite" },
                  { name: "SAP", icon: Cpu, desc: "Enterprise Systems" }
                ].map((integration, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ y: -5, scale: 1.02 }}
                    className="p-4 rounded-xl bg-slate-50 border border-black/5 text-center group hover:bg-white hover:shadow-lg transition-all"
                  >
                    <integration.icon size={28} className="text-[#ff5d22] mx-auto mb-2 opacity-70 group-hover:opacity-100" />
                    <div className="text-sm font-black text-black mb-1">
                      {integration.name}
                    </div>
                    <div className="text-xs font-bold text-black/50">
                      {integration.desc}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-[#ff5d22]/10 to-transparent blur-3xl rounded-[2.5rem]" />
              <div className="relative bg-slate-950 rounded-[2rem] p-6 text-white">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-xl bg-[#ff5d22] flex items-center justify-center text-white mx-auto mb-4 shadow-xl">
                    <Network size={24} />
                  </div>
                  <h4 className="text-lg font-black mb-3">API-First Architecture</h4>
                  <p className="text-white/70 text-sm font-bold leading-relaxed mb-4">
                    Built with modern REST APIs and webhooks for real-time data synchronization across your entire tech stack.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-white/10 border border-white/20">
                      <div className="text-xs font-black uppercase tracking-wider text-white/60 mb-1">
                        API Calls/Day
                      </div>
                      <div className="text-base font-black text-[#ff5d22]">
                        500K+
                      </div>
                    </div>
                    <div className="p-3 rounded-xl bg-white/10 border border-white/20">
                      <div className="text-xs font-black uppercase tracking-wider text-white/60 mb-1">
                        Uptime SLA
                      </div>
                      <div className="text-base font-black text-[#ff5d22]">
                        99.9%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 md:py-12 px-4 sm:px-6 md:px-8 bg-white relative overflow-hidden">
        <motion.div
          style={{
            y: useTransform(scrollY, [2000, 2800], [0, -50]),
            rotateZ: useTransform(scrollY, [2000, 2800], [0, -2])
          }}
          className="absolute inset-0 bg-gradient-to-br from-[#ff5d22]/5 via-transparent to-transparent"
        />
        <div className="max-w-4xl mx-auto rounded-2xl bg-slate-950 p-6 md:p-10 text-center relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-tr from-[#ff5d22]/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
          <div className="absolute -top-24 right-[-10%] h-56 w-56 rounded-full bg-[#ff5d22]/10 blur-3xl pointer-events-none" />
          <motion.h2
            whileInView={{ scale: [0.9, 1], opacity: [0, 1] }}
            viewport={{ once: true }}
            className="relative z-10 text-2xl md:text-3xl font-black text-white tracking-tighter leading-tight mb-4"
          >
            Transform Your <br />
            <span className="text-[#ff5d22]">Quote-to-Cash Process</span>
          </motion.h2>
          <p className="relative z-10 text-white/60 text-sm md:text-base font-bold max-w-xl mx-auto">
            Experience the power of AI-driven CPQ with intelligent automation, predictive analytics, and seamless integrations.
          </p>

          <div className="relative z-10 flex flex-col sm:flex-row justify-center gap-3 pt-4">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="primary"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="w-full sm:w-auto !px-6 sm:!px-8 !py-3 sm:!py-4 bg-[#ff5d22] hover:bg-white hover:text-black shadow-lg"
              >
                Schedule CPQ Demo
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="outline"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="w-full sm:w-auto !px-6 sm:!px-8 !py-3 sm:!py-4 border-white/10 text-white hover:border-white"
              >
                Explore Platform Integrations
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      <ContactSection id="contact" />
    </motion.div>
  );
};

const UCBillingPage = () => {
  const data = productData["uc-billing"];
  const theme = heroThemes["uc-billing"];

  return (
    <UCBillingPageContent
      data={data}
      theme={theme}
      ProductHero={ProductHero}
      ContactSection={ContactSection}
      SectionHeader={SectionHeader}
      MockDashboard={MockDashboard}
      Button={Button}
    />
  );
};

const SolutionDetailPage = () => {
  const { solutionId } = useParams();
  const { scrollY } = useScroll();
  const imageParallax = useTransform(scrollY, [0, 500], [0, 80]);
  const solutionData: Record<string, any> = {
    "cx-orchestration": {
      title: "CX Orchestration",
      subtitle: "Unified Commerce. Fragmented No More.",
      desc: "Architecting frictionless customer lifecycles across complex global ecosystems. We move beyond simple engagement to full journey synchronization, ensuring every touchpoint reinforces brand authority and drives sustained retention.",
      impacts: [
        { title: "Net Revenue Retention", value: "115%", desc: "Expanding existing account value through intelligent cross-sell triggers." },
        { title: "Customer Effort Score", value: "-40%", desc: "Drastic reduction in friction points throughout the renewal lifecycle." }
      ],
      points: [
        { title: "Journey Synchronization", desc: "Aligning marketing, sales, and service logic into a single cohesive customer flow.", icon: LayoutGrid },
        { title: "Intelligent Upsell", desc: "Neural triggers that identify expansion opportunities based on real-time consumption.", icon: Zap },
        { title: "Identity Resolution", desc: "Unified customer profiles that persistent across mobile, web, and marketplace channels.", icon: Users }
      ],
      capabilities: [
        "Dynamic Journey Mapping & Optimization",
        "Multi-Ecosystem Identity Fabric",
        "Automated Lifecycle Reco-Logic",
        "Behavioral Sentiment Orchestration"
      ],
      image: cxOrchestrationImage
    },
    "revenue-engineering": {
      title: "Revenue Engineering",
      subtitle: "Precision Monetization. Scaled for Hyperscale.",
      desc: "Stop reacting to revenue leakage. Start engineering fiscal certainty. Our platform provides the neural architecture needed to govern pricing, discounts, and revenue recognition across thousands of global stock-keeping units.",
      impacts: [
        { title: "Billing Accuracy", value: "99.99%", desc: "Eliminating reconciliation gaps between contracts and ledgers." },
        { title: "Margin Protection", value: "+8%", desc: "Hardening discount governance through automated approval cascades." }
      ],
      points: [
        { title: "Ledger Synchronization", desc: "Direct mapping of operational transactions to sub-ledger systems.", icon: Layers },
        { title: "Neural Quoting", desc: "Dynamic pricing models that adapt to regional tax and currency shifts.", icon: Cpu },
        { title: "Compliance Logic", desc: "Native support for ASC 606 and IFRS 15 revenue recognition standards.", icon: ShieldCheck }
      ],
      capabilities: [
        "Real-time Revenue Leakage Detection",
        "Automated Ledger-to-Contract Sync",
        "Global Tax & Compliance Governance",
        "Multi-Cloud Subscription Mediation"
      ],
      image: revenueEngineImage
    },
    "digital-lifecycle": {
      title: "Digital Lifecycle",
      subtitle: "Core Transformation. Zero Downtime Execution.",
      desc: "Managing the complex interplay between legacy infrastructure and cloud-native services. We provide the orchestration bridge that enables carriers and enterprises to scale digital services without breaking the legacy backbone.",
      impacts: [
        { title: "Legacy Migration", value: "5x Faster", desc: "Accelerating the shift from monolithic systems to micro-orchestration." },
        { title: "Service Uptime", value: "99.999%", desc: "Carrier-grade availability for high-concurrency distribution." }
      ],
      points: [
        { title: "Atomic Mediation", desc: "Processing extreme volumes of usage data with zero record loss.", icon: Activity },
        { title: "Ecosystem Bridge", desc: "Seamlessly connecting legacy billing with modern service catalogs.", icon: Unplug },
        { title: "Cloud Provisioning", desc: "Automated service activation across multi-vendor cloud fabrics.", icon: Cloud }
      ],
      capabilities: [
        "Hyperscale CDR Mediation & Processing",
        "Partner Ecosystem Interop-Layer",
        "Zero-Touch Service Provisioning",
        "Legacy-to-Cloud Transformation Gate"
      ],
      image: digitalLifecycleImage
    }
  };

  const data = solutionData[solutionId || "cx-orchestration"] || solutionData["cx-orchestration"];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pt-20 pb-12 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Solution Hero */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="space-y-8">
            <SectionHeader pill="Solution Strategy" title={data.title} subtitle={data.subtitle} align="left" />
            <p className="text-black/60 text-lg font-bold leading-relaxed border-l-4 border-[#ff5d22] pl-8 max-w-xl">{data.desc}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button variant="primary" className="!px-10 !py-4 text-[10px] font-black uppercase tracking-widest">Download Blueprint</Button>
              </Link>
              <Link to="/contact">
                <Button variant="secondary" className="!px-10 !py-4 text-[10px] font-black uppercase tracking-widest">Speak to an Architect</Button>
              </Link>
            </div>
          </div>
          <div className="relative rounded-[4rem] overflow-hidden group shadow-massive aspect-[4/3] bg-slate-100">
            <motion.img
              style={{ y: imageParallax }}
              src={data.image}
              className="w-full h-full object-fill grayscale-[10%] group-hover:grayscale-0 group-hover:scale-[1.02] transition-all duration-700 ease-out"
              alt={data.title}
              referrerPolicy="no-referrer"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-12 left-12 right-12 p-8 bg-white/10 backdrop-blur-md rounded-[2.5rem] border border-white/20">
              <p className="text-white text-xs font-black uppercase tracking-[0.2em] mb-2 opacity-60">Status</p>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <p className="text-white font-bold tracking-tight">Active Deployment Node: Global-01</p>
              </div>
            </div>
          </div>
        </div>

        {/* Strategic Framework */}
        <div className="mb-20">
          <div className="max-w-3xl mb-12">
            <SectionHeader pill="Strategic Framework" title="The Architecture of Impact." align="left" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.points.map((p: any, i: number) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="p-8 rounded-[3rem] bg-slate-50 border border-black/5 group hover:border-[#ff5d22]/30 hover:bg-white transition-all cursor-default"
              >
                <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-[#ff5d22] mb-6 group-hover:bg-[#ff5d22] group-hover:text-white transition-all duration-500">
                  <p.icon size={24} strokeWidth={1.5} />
                </div>
                <h4 className="text-base font-black uppercase tracking-tight text-black mb-4">{p.title}</h4>
                <p className="text-black/50 text-[11px] font-bold leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Core Capabilities */}
        <div className="grid lg:grid-cols-12 gap-12 items-center mb-20 bg-slate-950 p-10 rounded-[4rem] relative overflow-hidden">
          <div className="hidden md:block absolute top-0 right-0 md:w-[300px] md:h-[300px] lg:w-[600px] lg:h-[600px] bg-[#ff5d22]/10 blur-[120px] rounded-full translate-x-1/2 -translate-y-1/2" />
          <div className="lg:col-span-7 space-y-10 relative z-10">
            <h2 className="text-3xl md:text-4xl font-black text-white tracking-tighter leading-tight">
              Core Platform <br />
              <span className="text-[#ff5d22]">Capabilities.</span>
            </h2>
            {data.capabilities.length === 4 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data.capabilities.map((cap: string, i: number) => (
                  <div key={i} className={`p-6 rounded-2xl bg-white/5 border border-white/10 transition-all ${(i === 0 || i === 3) ? 'min-h-[220px] flex items-start' : 'min-h-[120px] flex items-center'
                    }`}>
                    <div className="w-3 h-3 rounded-full bg-[#ff5d22] mr-4 mt-1" />
                    <div className="text-white/80 font-bold">{cap}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-x-12 gap-y-6">
                {data.capabilities.map((cap: string, i: number) => (
                  <div key={i} className="flex items-center gap-4 group">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#ff5d22] group-hover:scale-150 transition-transform" />
                    <span className="text-white/60 font-medium tracking-tight group-hover:text-white transition-colors">{cap}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-5 relative z-10">
            <div className="p-8 bg-white/5 backdrop-blur-xl border border-white/10 rounded-[3rem] shadow-massive">
              <MockDashboard className="opacity-60" />
            </div>
          </div>
        </div>

        {/* Quantified Value */}
        <div className="mb-16">
          <SectionHeader pill="Quantified Value" title="Measurable Outcomes." align="center" />
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {data.impacts.map((imp: any, i: number) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -10, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="p-8 rounded-[3.5rem] bg-white border border-black/5 shadow-sm text-center group hover:bg-slate-50 transition-all border-b-4 hover:border-b-[#ff5d22]"
              >
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-black/30 mb-6">{imp.title}</p>
                <div className="text-5xl font-black tracking-tighter text-black mb-6 group-hover:scale-110 transition-transform duration-500">{imp.value}</div>
                <p className="text-black/50 text-[11px] font-bold leading-relaxed">{imp.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
        <ContactSection id="contact" />
      </div>
    </motion.div>
  );
};

const PlatformPage = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-16 pb-6 overflow-x-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div id="about">
          <SectionHeader
            pill="The Infrastructure"
            title="The AppGallop revenue fabric."
            subtitle="A unified platform that aligns sales, operations, and finance from intent to ledger."
          />

          <div className="mt-6 grid lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-8">
              <div className="grid gap-4">
                {[
                  { title: "Orchestration Layer", desc: "Event-driven workflows that connect quoting, provisioning, billing, and collections.", icon: Activity },
                  { title: "Ledger Integrity", desc: "Automated GL mapping and revenue recognition aligned to enterprise controls.", icon: Lock },
                  { title: "Global Distribution", desc: "Multi-region design that keeps catalogs, pricing, and entitlements in sync.", icon: Globe }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 group">
                    <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-black/5 flex items-center justify-center text-[#ff5d22] group-hover:bg-[#ff5d22] group-hover:text-white transition-all duration-500 shadow-sm">
                      <item.icon size={24} strokeWidth={2} />
                    </div>
                    <div>
                      <h4 className="text-lg font-black tracking-tight mb-2">{item.title}</h4>
                      <p className="text-black/50 text-xs font-bold leading-relaxed max-w-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative overflow-hidden md:overflow-visible">
              <div className="absolute -inset-20 bg-[#ff5d22]/5 blur-[120px] rounded-full pointer-events-none" />
              <MockDashboard className="scale-105 md:scale-110 shadow-massive rotate-1 md:rotate-2 hover:rotate-0 transition-transform duration-1000" />
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-black/5">
            <SectionHeader
              pill="The Mission"
              title="Orchestrate. Govern. Scale."
              subtitle="AppGallop eliminates revenue friction by connecting lifecycle actions to financial truth in one controlled system."
              align="left"
            />

            <div className="mt-6 grid lg:grid-cols-2 gap-8 items-center">
              <div className="relative group rounded-[4rem] overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=2070"
                  className="w-full h-full object-fill grayscale group-hover:grayscale-0 transition-all duration-1000"
                  alt="Operational Excellence"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-all duration-1000" />
              </div>
              <div className="space-y-10">
                <h3 className="text-3xl font-black tracking-tighter">Unified by design.</h3>
                <p className="text-black/50 text-lg font-bold leading-relaxed">
                  AppGallop consolidates revenue operations into a governed platform so every transaction is priced correctly, fulfilled on time, and posted with audit-ready accuracy.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-4xl font-black text-[#ff5d22] mb-2">$500M+</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-black/40">Annual revenue orchestrated</div>
                  </div>
                  <div>
                    <div className="text-4xl font-black text-black mb-2">32</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-black/40">Global edge zones</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Architecture Section */}
        <div id="architecture" className="mt-16 pt-16 border-t border-black/5">
          <SectionHeader
            pill="System Architecture"
            title="Built for enterprise scale."
            subtitle="A composable architecture that keeps lifecycle data consistent across every channel."
          />
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              { title: "Event-Driven Core", desc: "Reactive architecture ensures sub-millisecond response times for complex provisioning tasks.", icon: Activity },
              { title: "Multi-Cloud Lattice", desc: "Native integration with AWS, Azure, GCP, and specialized carrier fabrics.", icon: Cloud },
              { title: "API-First DNA", desc: "Extensive RESTful and GraphQL endpoints for seamless enterprise integration.", icon: Terminal }
            ].map((item, i) => (
              <div key={i} className="p-10 rounded-[3rem] bg-slate-50 border border-black/5 group hover:border-[#ff5d22]/30 hover:bg-white transition-all">
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center text-[#ff5d22] mb-6 group-hover:bg-[#ff5d22] group-hover:text-white transition-all">
                  <item.icon size={24} />
                </div>
                <h4 className="text-lg font-black mb-3">{item.title}</h4>
                <p className="text-black/50 text-sm font-bold leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Pillars */}
        <div id="pillars" className="mt-16 pt-16 border-t border-black/5">
          <SectionHeader
            pill="Platform Modules"
            title="Lifecycle-to-ledger modules."
            subtitle="Core capabilities that align commercial execution with financial governance."
          />
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Identity & Account Graph",
                desc: "Unified customer identity, account hierarchies, and entitlement mapping across every channel.",
                features: ["360 Account View", "Entitlement Graph", "Multi-tenant Segmentation"],
                icon: Users,
                color: "blue"
              },
              {
                title: "CPQ & Orchestration",
                desc: "Policy-driven pricing, approvals, and order decomposition with automated provisioning handoffs.",
                features: ["Governed Pricing", "Approval Routing", "Provisioning Sync"],
                icon: FileText,
                color: "emerald"
              },
              {
                title: "Billing & Ledger",
                desc: "High-volume rating, automated invoicing, and sub-ledger posting with audit-ready integrity.",
                features: ["Usage Rating", "Automated Invoicing", "ERP Posting"],
                icon: CreditCard,
                color: "rose"
              }
            ].map((pillar, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -5 }}
                className="p-8 rounded-[3.5rem] bg-slate-50 border border-black/5 group hover:bg-white hover:shadow-2xl transition-all duration-500 overflow-hidden relative"
              >
                <div className={`absolute top-0 right-0 w-32 h-32 bg-${pillar.color}-500/5 blur-3xl`} />
                <div className={`w-14 h-14 rounded-2xl bg-white border border-black/5 flex items-center justify-center text-black group-hover:bg-black group-hover:text-white transition-all duration-300 mb-6 shadow-sm`}>
                  <pillar.icon size={26} strokeWidth={2} />
                </div>
                <h4 className="text-2xl font-black tracking-tight mb-3">{pillar.title}</h4>
                <p className="text-black/50 text-sm font-bold leading-relaxed mb-6">{pillar.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {pillar.features.map((f, j) => (
                    <span key={j} className="px-4 py-2 bg-black/5 rounded-full text-[9px] font-black uppercase tracking-widest text-black/60 group-hover:bg-black group-hover:text-white transition-colors">
                      {f}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Ecosystem Section */}
        <div id="ecosystem" className="mt-16 pt-16 border-t border-black/5">
          <div className="bg-black rounded-[4rem] p-8 md:p-12 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <NetworkLattice />
            </div>
            <div className="relative z-10 grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-block px-4 py-1.5 rounded-full bg-[#ff5d22] text-white text-[8px] font-black uppercase tracking-widest mb-6">
                  Integrations
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-white tracking-tighter mb-6 leading-[1.1]">
                  A Unified Fabric for Your <br className="hidden md:block" />
                  <span className="text-white/70 italic">Entire Tech Stack.</span>
                </h2>
                <p className="text-white/70 text-base font-bold leading-relaxed mb-8">
                  AppGallop doesn't live in a vacuum. We provide deep, native integrations with the tools your team already relies on, ensuring data flows seamlessly from front-end engagement to back-end accounting.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {[
                    "Microsoft Azure", "AWS", "Google Cloud",
                    "Salesforce", "Dynamics 365", "NetSuite",
                    "SAP S/4HANA", "Oracle ERP", "Workday"
                  ].map((brand, i) => (
                    <div key={i} className="px-6 py-4 rounded-2xl bg-white/5 border border-white/10 text-white/70 text-[10px] font-black uppercase tracking-widest text-center hover:bg-white/10 hover:text-white transition-all cursor-default">
                      {brand}
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="absolute -inset-10 bg-[#ff5d22]/20 blur-[100px] rounded-full" />
                <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-[3rem] p-8 flex flex-col items-center justify-center min-h-[320px]">
                  <div className="w-24 h-24 rounded-[2rem] bg-[#ff5d22] flex items-center justify-center text-white mb-8 shadow-2xl animate-pulse">
                    <Logo />
                  </div>
                  <div className="text-center">
                    <h4 className="text-xl font-black text-white mb-2">Omni-Channel API</h4>
                    <p className="text-white/70 text-xs font-bold">Standardized endpoints for any <br />third-party ecosystem.</p>
                  </div>
                  <div className="mt-8 flex gap-4">
                    <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/50">
                      <Code size={20} />
                    </div>
                    <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/50">
                      <Activity size={20} />
                    </div>
                    <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center text-white/50">
                      <Database size={20} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div id="security" className="mt-16 pt-16 border-t border-black/5">
          <SectionHeader
            pill="Enterprise Security"
            title="Zero Trust. Total Integrity."
            subtitle="Security isn't an afterthought. It's the foundation of everything we build."
          />
          <div className="mt-10 grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-8">
              <div className="p-6 rounded-[2.5rem] bg-emerald-50 border border-emerald-100 flex gap-6 items-center">
                <ShieldCheck className="text-emerald-600 shrink-0" size={40} />
                <div>
                  <h4 className="font-black text-emerald-900">ISO 27001 & SOC 2 Type II</h4>
                  <p className="text-emerald-700/60 text-sm font-bold">Maintaining the highest global standards for data protection.</p>
                </div>
              </div>
              <div className="space-y-6 pl-6">
                {[
                  "End-to-end encryption for all data at rest and in transit.",
                  "Multi-factor authentication and SAML/SSO integration.",
                  "Continuous automated vulnerability scanning and pen-testing.",
                  "Strict RBAC controls and comprehensive audit logging."
                ].map((text, i) => (
                  <div key={i} className="flex items-center gap-4 group">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 group-hover:scale-150 transition-transform" />
                    <span className="text-black/60 font-bold text-sm">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-[4rem] overflow-hidden shadow-massive relative group">
              <img
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=2070"
                className="w-full h-full object-fill grayscale group-hover:grayscale-0 transition-all duration-1000"
                alt="Security Operations"
              />
              <div className="absolute inset-0 bg-emerald-900/10 group-hover:bg-transparent transition-all duration-1000" />
            </div>
          </div>
        </div>

        <FAQSection />
        <ContactSection id="contact" />
      </div>
    </motion.div>
  );
};

const PlatformIntegrationsPage = () => {
  const integrations = [
    { name: "Avalara", category: "Tax & Compliance", desc: "Automated tax calculation, compliance checks, and transaction-ready tax workflows.", icon: FileCheck },
    { name: "GST APIs", category: "Tax & Compliance", desc: "GST validation, compliance data exchange, and India-ready taxation workflows.", icon: FileText },
    { name: "PayPal", category: "Payments", desc: "Digital payment acceptance and reconciliation-ready transaction flows.", icon: CreditCard },
    { name: "Stripe", category: "Payments", desc: "Stripe payment processing and integration for seamless transaction workflows.", icon: CreditCard },
    { name: "BillDesk", category: "Payments", desc: "Payment gateway connectivity for invoice, collection, and settlement operations.", icon: CreditCard },
    { name: "Yes Bank", category: "Payments", desc: "Banking workflows for payments, collections, and enterprise financial operations.", icon: Database },
    { name: "ICICI Bank", category: "Payments", desc: "Banking integration support for payment and finance operations.", icon: Database },
    { name: "Microsoft", category: "Cloud & Enterprise", desc: "Microsoft ecosystem connectivity for cloud commerce and enterprise workflows.", icon: Cloud },
    { name: "Azure ARM", category: "Cloud & Enterprise", desc: "Azure Resource Manager integration for cloud provisioning and lifecycle control.", icon: Cloud },
    { name: "Google", category: "Cloud & Enterprise", desc: "Google Cloud and Workspace connectivity for cloud commerce and enterprise productivity workflows.", icon: Cloud },
    { name: "Acronis", category: "Cloud & Enterprise", desc: "Cyber protection, backup, and cloud service lifecycle integration for managed service catalogs.", icon: ShieldCheck },
    { name: "AWS", category: "Cloud & Enterprise", desc: "AWS marketplace, cloud provisioning, and service catalog workflows for cloud commerce operations.", icon: Cloud },
    { name: "Dynamics 365", category: "CRM & ERP", desc: "CRM and business process synchronization across sales, finance, and customer operations.", icon: LineChart },
    { name: "SAP", category: "CRM & ERP", desc: "ERP connectivity for finance, order, billing, and enterprise data alignment.", icon: Layers },
    { name: "McAfee", category: "CRM & ERP", desc: "Security service integration support for managed cloud and digital service catalogs.", icon: ShieldCheck },
    { name: "Dropbox Sign", category: "Documents", desc: "Digital agreement and signature workflows for customer and partner documents.", icon: FileCheck },
    { name: "SendGrid", category: "Messaging", desc: "Transactional email delivery for notifications, alerts, and lifecycle communications.", icon: MessageSquare },
    { name: "Microsoft Graph", category: "Messaging", desc: "Unified REST API gateway and developer platform for the Microsoft Cloud", icon: MessageSquare },
    { name: "Send Clean", category: "Messaging", desc: "Marketing and communication workflow support for campaign-led engagement.", icon: MessageSquare },
    { name: "SmartFlo", category: "Telecom", desc: "UCaaS and CCaaS workflow connectivity for telecom-grade service operations.", icon: Phone },
    { name: "WABA", category: "Telecom", desc: "WhatsApp Business API connectivity for customer messaging, notifications, and telecom engagement workflows.", icon: MessageSquare },
    { name: "Freshdesk", category: "ITSM & Support", desc: "Support desk integration for tickets, customer service, and operational workflows.", icon: LifeBuoy },
    { name: "ServiceNow", category: "ITSM & Support", desc: "Enterprise service management integration for workflow, ticketing, and approvals.", icon: Settings },
    { name: "JIRA", category: "ITSM & Support", desc: "Issue tracking and delivery workflow integration for product and operations teams.", icon: Activity },
  ];

  const categories = Array.from(new Set(integrations.map((item) => item.category)));
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedApi, setSelectedApi] = useState(integrations[0].name);
  const [isApiModalOpen, setIsApiModalOpen] = useState(false);
  const [isApiDropdownOpen, setIsApiDropdownOpen] = useState(false);
  const [apiSearch, setApiSearch] = useState("");
  const apiLeadHoneypotRef = useRef<HTMLInputElement | null>(null);
  const [apiLeadForm, setApiLeadForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    useCase: "",
  });
  const [apiLeadStatus, setApiLeadStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [apiLeadMessage, setApiLeadMessage] = useState("");

  const visibleIntegrations =
    activeCategory === "All"
      ? integrations
      : integrations.filter((item) => item.category === activeCategory);

  const filteredApiOptions = integrations.filter((item) =>
    `${item.name} ${item.category}`.toLowerCase().includes(apiSearch.trim().toLowerCase())
  );

  useEffect(() => {
    if (!isApiModalOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isApiModalOpen]);

  const openApiModal = (apiName = selectedApi) => {
    setSelectedApi(apiName);
    setApiLeadStatus("idle");
    setApiLeadMessage("");
    setApiSearch("");
    setIsApiDropdownOpen(false);
    setIsApiModalOpen(true);
  };

  const closeApiModal = () => {
    setIsApiModalOpen(false);
    setIsApiDropdownOpen(false);
    setApiSearch("");
  };

  const handleApiLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiLeadStatus("loading");
    setApiLeadMessage("");

    try {
      const recaptchaAction = "lead_api_access";
      const recaptchaToken = await executeRecaptchaV3(recaptchaAction);
      const response = await fetch("/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          inquiryType: "API Access Request",
          requestedApi: selectedApi,
          message: apiLeadForm.useCase || `Requested access details for ${selectedApi}.`,
          ...apiLeadForm,
          website: apiLeadHoneypotRef.current?.value || '',
          recaptchaToken,
          recaptchaAction,
        }),
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || "Unable to submit API request.");
      }

      setApiLeadStatus("success");
      setApiLeadMessage("Your request is conveyed to our team, and they will contact you soon.");
      setApiLeadForm({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        useCase: "",
      });
    } catch (error) {
      console.error("API access request failed:", error);
      setApiLeadStatus("error");
      setApiLeadMessage(error instanceof Error ? error.message : "Unable to submit the request right now. Please try again shortly.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative isolate min-h-screen overflow-x-hidden bg-slate-50 pt-20 pb-12"
    >
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <motion.div
          aria-hidden="true"
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              "linear-gradient(115deg, rgba(255,93,34,0.10), transparent 28%, rgba(15,23,42,0.055) 52%, transparent 76%), linear-gradient(to right, rgba(15,23,42,0.055) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.045) 1px, transparent 1px)",
            backgroundSize: "180% 180%, 46px 46px, 46px 46px",
          }}
          animate={{
            backgroundPosition: ["0% 50%, 0 0, 0 0", "100% 50%, 46px 0, 0 46px", "0% 50%, 0 0, 0 0"],
          }}
          transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(248,250,252,0.78),rgba(248,250,252,0.94)_34%,rgba(248,250,252,0.82))]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <section className="relative overflow-hidden rounded-[2rem] md:rounded-[3rem] border border-black/8 bg-white p-7 md:p-12 lg:p-14 shadow-[0_24px_90px_rgba(15,23,42,0.08)] ring-1 ring-black/[0.03]">
          <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/70 to-transparent" />
          <div className="absolute right-0 top-0 h-72 w-72 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#ff5d22]/10 blur-[90px]" />
          <div className="absolute inset-0 opacity-[0.035] bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:38px_38px]" />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/8 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff5d22]">
                AppGallop APIs
              </div>
              <h1 className="mt-6 max-w-3xl text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-slate-950">
                Enterprise integrations for <span className="text-[#ff5d22]">connected cloud commerce.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-sm md:text-base font-bold leading-relaxed text-black/55">
                Explore the API integrations available across AppGallop for payments, tax, banking, cloud provisioning, CRM, ERP, ITSM, messaging, security, and telecom operations.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="#api-directory" className="inline-flex min-h-12 items-center rounded-2xl bg-[#ff5d22] px-6 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-[#ff5d22]/20 transition-all hover:bg-slate-950">
                  View APIs
                </a>
                <button type="button" onClick={() => openApiModal()} className="inline-flex min-h-12 items-center rounded-2xl border border-black/10 bg-white px-6 text-[10px] font-black uppercase tracking-widest text-slate-950 shadow-sm transition-all hover:border-[#ff5d22]/30 hover:bg-[#ff5d22]/5">
                  Get API Access
                </button>
              </div>
            </div>

            <div className="lg:col-span-5">
  <div className="rounded-[1.75rem] border border-black/8 bg-slate-950 p-5 text-white shadow-xl">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[9px] font-black uppercase tracking-[0.22em] text-white/35">
          API Performance
        </p>
        <p className="mt-1 text-lg font-black tracking-tight">
          Integration Snapshot
        </p>
      </div>

      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-[#ff8a5c]">
        <Network size={22} />
      </div>
    </div>

    <div className="mt-5 grid grid-cols-2 gap-3">
      {[
        { label: "APIs Live", value: integrations.length },
        { label: "Uptime", value: "99.9%" },
        { label: "Avg Latency", value: "180ms" },
        { label: "Success Rate", value: "98.7%" },
      ].map((kpi) => (
        <div
          key={kpi.label}
          className="rounded-xl border border-white/10 bg-white/[0.04] p-3"
        >
          <p className="text-[9px] font-black uppercase tracking-widest text-white/35">
            {kpi.label}
          </p>
          <p className="mt-2 text-xl font-black tracking-tight text-[#ff8a5c]">
            {kpi.value}
          </p>
        </div>
      ))}
    </div>

    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-black uppercase tracking-widest text-white/45">
          Gateway Status
        </p>
        <span className="rounded-full bg-[#ff5d22]/15 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-[#ff8a5c]">
          Operational
        </span>
      </div>
    </div>
  </div>
</div>
          </div>
        </section>

        <section id="api-directory" className="mt-16 mb-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full bg-white px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-black/35 shadow-sm ring-1 ring-black/6">
                API Directory
              </div>
              <h2 className="mt-5 text-3xl md:text-5xl font-black tracking-tighter leading-tight text-slate-950">
                Available AppGallop <span className="text-[#ff5d22]">integrations.</span>
              </h2>
            </div>
            <div className="flex max-w-full gap-2 overflow-x-auto responsive-scroll pb-1">
              {["All", ...categories].map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`shrink-0 rounded-full px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                    activeCategory === category
                      ? "bg-slate-950 text-white shadow-lg"
                      : "border border-black/8 bg-white text-black/45 hover:border-[#ff5d22]/20 hover:text-[#ff5d22]"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleIntegrations.map((item, i) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                className="group flex min-h-[250px] flex-col rounded-[1.35rem] border border-black/6 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-[#ff5d22]/25 hover:shadow-[0_18px_55px_rgba(15,23,42,0.10)]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#ff5d22]/15 bg-[#ff5d22]/8 text-[#ff5d22] transition-colors group-hover:bg-[#ff5d22] group-hover:text-white">
                    <item.icon size={21} />
                  </div>
                  <span className="rounded-full bg-slate-50 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-black/35">
                    {item.category}
                  </span>
                </div>
                <h3 className="mt-6 text-xl font-black tracking-tight text-slate-950">{item.name}</h3>
                <p className="mt-3 text-sm font-bold leading-relaxed text-black/50">{item.desc}</p>
                <button
                  type="button"
                  onClick={() => openApiModal(item.name)}
                  className="mt-auto inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-[10px] font-black uppercase tracking-widest text-white transition-all hover:bg-[#ff5d22]"
                >
                  Get API <ArrowRight size={14} />
                </button>
              </motion.div>
            ))}
          </div>
        </section>

      </div>
      <ContactSection />
      <AnimatePresence>
        {isApiModalOpen && (
          <div className="fixed inset-0 z-[6000] flex items-center justify-center p-3 sm:p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeApiModal}
              className="absolute inset-0 bg-slate-950/65 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 24 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="relative w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-2xl md:rounded-[2rem] border border-white/70 bg-white shadow-2xl"
            >
              <button
                type="button"
                onClick={closeApiModal}
                aria-label="Close API request modal"
                className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-xl bg-white/90 text-black/45 shadow-sm ring-1 ring-black/6 transition-all hover:bg-slate-950 hover:text-white"
              >
                <X size={18} />
              </button>

              <div className="max-h-[92vh] overflow-y-auto responsive-scroll">
                <div className="grid grid-cols-1 lg:grid-cols-12">
                  <div className="relative overflow-hidden bg-slate-950 p-7 md:p-10 lg:col-span-5">
                    <div className="absolute right-0 top-0 h-72 w-72 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#ff5d22]/20 blur-[90px]" />
                    <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:34px_34px]" />
                    <div className="relative z-10 pr-10 lg:pr-0">
                      <div className="inline-flex rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff8a5c]">
                        Get API
                      </div>
                      <h2 className="mt-5 text-3xl md:text-4xl font-black tracking-tighter leading-tight text-white">
                        Request access to an <span className="text-[#ff8a5c]">AppGallop API.</span>
                      </h2>
                      <p className="mt-5 text-sm font-bold leading-relaxed text-white/62">
                        Choose the API you need and share your details. The request is sent to the AppGallop team for follow-up.
                      </p>
                      <div className="mt-8 rounded-xl border border-white/10 bg-white/5 px-5 py-4">
                        <p className="text-[10px] font-black uppercase tracking-widest text-white/35">Selected API</p>
                        <p className="mt-2 text-2xl font-black tracking-tight text-[#ff8a5c]">{selectedApi}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 md:p-10 lg:col-span-7">
                    <form onSubmit={handleApiLeadSubmit} className="space-y-4">
                      <input
                        ref={apiLeadHoneypotRef}
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        className="pointer-events-none absolute -left-[9999px] h-px w-px opacity-0"
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[9px] font-black uppercase tracking-widest text-black/35 ml-1">First Name</label>
                          <input
                            required
                            type="text"
                            value={apiLeadForm.firstName}
                            onChange={(e) => setApiLeadForm({ ...apiLeadForm, firstName: e.target.value })}
                            className="w-full rounded-xl border border-black/6 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-[#ff5d22]/40 focus:bg-white focus:ring-2 focus:ring-[#ff5d22]/10"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black uppercase tracking-widest text-black/35 ml-1">Last Name</label>
                          <input
                            required
                            type="text"
                            value={apiLeadForm.lastName}
                            onChange={(e) => setApiLeadForm({ ...apiLeadForm, lastName: e.target.value })}
                            className="w-full rounded-xl border border-black/6 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-[#ff5d22]/40 focus:bg-white focus:ring-2 focus:ring-[#ff5d22]/10"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[9px] font-black uppercase tracking-widest text-black/35 ml-1">Business Email</label>
                          <input
                            required
                            type="email"
                            value={apiLeadForm.email}
                            onChange={(e) => setApiLeadForm({ ...apiLeadForm, email: e.target.value })}
                            className="w-full rounded-xl border border-black/6 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-[#ff5d22]/40 focus:bg-white focus:ring-2 focus:ring-[#ff5d22]/10"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[9px] font-black uppercase tracking-widest text-black/35 ml-1">Company</label>
                          <input
                            required
                            type="text"
                            value={apiLeadForm.company}
                            onChange={(e) => setApiLeadForm({ ...apiLeadForm, company: e.target.value })}
                            className="w-full rounded-xl border border-black/6 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-[#ff5d22]/40 focus:bg-white focus:ring-2 focus:ring-[#ff5d22]/10"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-widest text-black/35 ml-1">API Required</label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setIsApiDropdownOpen((value) => !value)}
                            className="flex w-full items-center justify-between gap-4 rounded-xl border border-black/6 bg-slate-50 px-4 py-3 text-left text-sm font-bold outline-none transition-all hover:bg-white focus:border-[#ff5d22]/40 focus:bg-white focus:ring-2 focus:ring-[#ff5d22]/10"
                          >
                            <span>{selectedApi}</span>
                            <ChevronDown size={16} className={`shrink-0 text-black/35 transition-transform ${isApiDropdownOpen ? "rotate-180" : ""}`} />
                          </button>

                          <AnimatePresence>
                            {isApiDropdownOpen && (
                              <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 8 }}
                                className="absolute left-0 right-0 top-[calc(100%+8px)] z-30 overflow-hidden rounded-xl border border-black/8 bg-white shadow-2xl"
                              >
                                <div className="border-b border-black/6 p-3">
                                  <input
                                    type="search"
                                    value={apiSearch}
                                    onChange={(e) => setApiSearch(e.target.value)}
                                    placeholder="Search API..."
                                    className="w-full rounded-lg border border-black/6 bg-slate-50 px-3 py-2 text-sm font-bold outline-none focus:border-[#ff5d22]/40 focus:bg-white"
                                    autoFocus
                                  />
                                </div>
                                <div className="max-h-56 overflow-y-auto responsive-scroll p-2">
                                  {filteredApiOptions.length > 0 ? (
                                    filteredApiOptions.map((item) => (
                                      <button
                                        key={item.name}
                                        type="button"
                                        onClick={() => {
                                          setSelectedApi(item.name);
                                          setIsApiDropdownOpen(false);
                                          setApiSearch("");
                                        }}
                                        className={`flex w-full items-center justify-between gap-4 rounded-lg px-3 py-2.5 text-left transition-all ${
                                          selectedApi === item.name
                                            ? "bg-[#ff5d22]/10 text-[#ff5d22]"
                                            : "hover:bg-slate-50 text-slate-950"
                                        }`}
                                      >
                                        <span className="text-sm font-black">{item.name}</span>
                                        <span className="text-[9px] font-black uppercase tracking-widest text-black/35">{item.category}</span>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="px-3 py-4 text-center text-xs font-bold text-black/40">
                                      No APIs found.
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-widest text-black/35 ml-1">Use Case</label>
                        <textarea
                          required
                          rows={4}
                          value={apiLeadForm.useCase}
                          onChange={(e) => setApiLeadForm({ ...apiLeadForm, useCase: e.target.value })}
                          placeholder="Tell us how you plan to use this API."
                          className="w-full resize-none rounded-xl border border-black/6 bg-slate-50 px-4 py-3 text-sm font-bold outline-none transition-all focus:border-[#ff5d22]/40 focus:bg-white focus:ring-2 focus:ring-[#ff5d22]/10"
                        />
                      </div>

                      {apiLeadMessage && (
                        <div className={`rounded-xl px-4 py-3 text-sm font-bold leading-relaxed ${
                          apiLeadStatus === "success"
                            ? "border border-emerald-500/20 bg-emerald-50 text-emerald-700"
                            : "border border-red-500/20 bg-red-50 text-red-600"
                        }`}>
                          {apiLeadMessage}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={apiLeadStatus === "loading"}
                        className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#ff5d22] px-6 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-[#ff5d22]/20 transition-all hover:bg-slate-950 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {apiLeadStatus === "loading" ? "Sending Request..." : "Submit API Request"} <ArrowRight size={14} />
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const PlatformSecurityPage = () => {
  const trustPillars = [
    {
      title: "Zero trust access",
      desc: "Access is shaped around identity, role, environment, and action context so sensitive workflows stay limited to the right people and systems.",
      icon: Lock,
    },
    {
      title: "Data protection",
      desc: "Revenue records, customer data, billing events, and integration payloads are handled through encrypted paths and controlled exposure.",
      icon: ShieldCheck,
    },
    {
      title: "Audit readiness",
      desc: "Administrative actions, workflow decisions, access changes, and API activity are structured for review, traceability, and evidence collection.",
      icon: FileCheck,
    },
  ];

  const controlGroups = [
    {
      title: "Identity and access",
      icon: Users,
      items: ["SSO and MFA-ready access patterns", "Role-based permission boundaries", "Conditional access for sensitive workflows"],
    },
    {
      title: "Platform and API security",
      icon: Terminal,
      items: ["Scoped API credentials", "Webhook and integration event logging", "Secure provisioning and billing workflows"],
    },
    {
      title: "Data governance",
      icon: Database,
      items: ["Encrypted data paths", "Retention-aware records", "Operational evidence and traceability"],
    },
    {
      title: "Operational resilience",
      icon: Activity,
      items: ["Continuous control monitoring", "Incident triage and escalation paths", "Recovery-aware workflow design"],
    },
  ];

  const securitySignals = [
    { value: "SSO", label: "identity-ready access control" },
    { value: "MFA", label: "strong authentication posture" },
    { value: "RBAC", label: "role-based permission model" },
    { value: "Logs", label: "traceable workflow evidence" },
  ];

  const certifications = [
    { name: "SOC 1 Type II", category: "Financial controls", logo: soc1Type2Logo },
    { name: "SOC 2 Type II", category: "Security controls", logo: soc2Type2Logo },
    { name: "Avalara Partner", category: "Tax ecosystem", logo: avalaraPartnerLogo },
    { name: "IDC", category: "Market validation", logo: idcLogo },
    { name: "ISO", category: "Governance", logo: isoLogo },
    { name: "Microsoft Certified", category: "Cloud ecosystem", logo: microsoftCertifiedLogo },
  ];

  const assuranceCoverage = [
    "Control evidence for identity, permissions, workflow changes, and integration activity",
    "Operational review support for finance, security, tax, and platform stakeholders",
    "Partner and cloud ecosystem alignment for enterprise procurement conversations",
  ];

  const protectedSurfaces = [
    {
      title: "Customer and partner records",
      desc: "Account profiles, reseller relationships, contacts, contract references, and lifecycle history are protected through scoped access and governed visibility.",
      icon: Users,
    },
    {
      title: "Billing and ledger events",
      desc: "Invoices, collections, tax decisions, payment events, reconciliation data, and revenue handoffs are handled with transaction-level traceability.",
      icon: CreditCard,
    },
    {
      title: "API and webhook traffic",
      desc: "External system calls, provisioning callbacks, notification events, and integration payloads are designed for secure exchange and reviewable activity trails.",
      icon: Terminal,
    },
    {
      title: "Admin and support actions",
      desc: "Configuration changes, operator actions, ticket-linked updates, and manual interventions are logged so sensitive operations remain accountable.",
      icon: LifeBuoy,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative isolate min-h-screen overflow-x-hidden bg-slate-50 pt-20 pb-12"
    >
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <motion.div
          aria-hidden="true"
          className="absolute inset-0 opacity-80"
          style={{
            backgroundImage:
              "linear-gradient(115deg, rgba(255,93,34,0.10), transparent 28%, rgba(15,23,42,0.055) 52%, transparent 76%), linear-gradient(to right, rgba(15,23,42,0.055) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.045) 1px, transparent 1px)",
            backgroundSize: "180% 180%, 46px 46px, 46px 46px",
          }}
          animate={{
            backgroundPosition: ["0% 50%, 0 0, 0 0", "100% 50%, 46px 0, 0 46px", "0% 50%, 0 0, 0 0"],
          }}
          transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(248,250,252,0.72),rgba(248,250,252,0.92)_34%,rgba(248,250,252,0.82))]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <section className="relative overflow-hidden rounded-[2rem] md:rounded-[3rem] border border-black/8 bg-white p-7 md:p-12 lg:p-14 shadow-[0_24px_90px_rgba(15,23,42,0.08)] ring-1 ring-black/[0.03]">
          <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/70 to-transparent" />
          <div className="absolute inset-0 opacity-[0.035] bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:38px_38px]" />
          <div className="relative z-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/8 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff5d22]">
                Trust Center
              </div>
              <h1 className="mt-6 max-w-3xl text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-slate-950">
                Security built into every <span className="text-[#ff5d22]">revenue workflow.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-sm md:text-base font-bold leading-relaxed text-black/55">
                AppGallop protects the systems that carry customer identity, subscriptions, invoices, payments, partner payouts, provisioning requests, and ledger handoffs.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="#security-controls" className="inline-flex min-h-12 items-center rounded-2xl bg-[#ff5d22] px-6 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-[#ff5d22]/20 transition-all hover:bg-slate-950">
                  View Controls
                </a>
                <a href="#certifications" className="inline-flex min-h-12 items-center rounded-2xl border border-black/10 bg-white px-6 text-[10px] font-black uppercase tracking-widest text-slate-950 shadow-sm transition-all hover:border-[#ff5d22]/30 hover:bg-[#ff5d22]/5">
                  Trust Signals
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-[1.75rem] border border-black/8 bg-slate-950 p-5 text-white shadow-xl">
                <div className="flex items-center justify-between border-b border-white/10 pb-5">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.22em] text-white/70">Security posture</p>
                    <p className="mt-1 text-lg font-black tracking-tight">Control Snapshot</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-[#6CA651]">
                    <ShieldCheck size={22} />
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  {securitySignals.map((signal) => (
                    <div key={signal.label} className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
                      <p className="text-xl font-black tracking-tight text-[#fff]">{signal.value}</p>
                      <p className="mt-2 text-[9px] font-black uppercase tracking-widest leading-relaxed text-white/65">{signal.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-white/65">Control Status</p>
                    <span className="rounded-full bg-[#6CA651]/15 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-[#6CA651]">
                      Active
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="certifications" className="mt-16 overflow-hidden rounded-[2rem] border border-black/6 bg-white shadow-sm">
          <div className="grid grid-cols-1 items-stretch lg:grid-cols-12">
            <div className="flex h-full flex-col border-b border-black/6 bg-slate-50/80 p-7 md:p-8 lg:col-span-4 lg:border-b-0 lg:border-r">
              <div>
                <div className="inline-flex items-center rounded-full border border-[#ff5d22]/15 bg-[#ff5d22]/5 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff5d22]">
                  Trust Signals
                </div>
                <h2 className="mt-5 text-3xl md:text-4xl font-black tracking-tighter leading-tight text-slate-950">
                  Certifications and <span className="text-[#ff5d22]">ecosystem assurance.</span>
                </h2>
                <p className="mt-4 text-sm font-bold leading-relaxed text-black/55">
                  Security assurance is organized for enterprise review, combining independent validations with ecosystem signals your risk, finance, and procurement teams can recognize.
                </p>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-3 lg:mt-auto lg:grid-cols-1">
                {[
                  { label: "Audit scope", value: "SOC + ISO" },
                  { label: "Review teams", value: "Risk + Finance" },
                  { label: "Evidence model", value: "Lifecycle logs" },
                ].map((item) => (
                  <div key={item.label} className="rounded-xl border border-black/6 bg-white px-4 py-3 shadow-sm">
                    <p className="text-[9px] font-black uppercase tracking-[0.18em] text-black/35">{item.label}</p>
                    <p className="mt-1 text-sm font-black tracking-tight text-slate-950">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex h-full flex-col justify-between p-5 md:p-6 lg:col-span-8">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {certifications.map((item) => (
                  <div key={item.name} className="group flex min-h-[116px] items-center gap-4 rounded-xl border border-black/6 bg-white px-4 py-4 transition-all hover:border-[#ff5d22]/25 hover:bg-slate-50 hover:shadow-lg">
                    <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-lg border border-black/6 bg-white px-2">
                      <img src={item.logo} alt={item.name} className="max-h-10 w-auto object-contain" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[9px] font-black uppercase tracking-widest leading-relaxed text-[#ff5d22]">{item.category}</div>
                      <div className="mt-1 text-sm font-black leading-snug text-slate-950">{item.name}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-xl border border-black/6 bg-white p-4">
                <p className="text-[9px] font-black uppercase tracking-[0.22em] text-black/35">Evidence coverage</p>
                <div className="mt-4 space-y-3">
                  {assuranceCoverage.map((item) => (
                    <div key={item} className="flex items-start gap-2 text-xs font-bold leading-relaxed text-black/55">
                      <Check size={13} className="mt-0.5 shrink-0 text-[#ff5d22]" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-black/6 bg-slate-950 px-4 py-4 text-white">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.22em] text-white/35">Review posture</p>
                    <p className="mt-1 text-sm font-black text-white">Structured for security, finance, and compliance conversations.</p>
                  </div>
                  <span className="inline-flex w-fit rounded-full bg-[#ff5d22]/15 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-[#ff8a5c]">
                    Audit Ready
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-16">
          <div className="mb-8 max-w-3xl">
            <div className="inline-flex items-center rounded-full border border-[#ff5d22]/15 bg-white px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff5d22] shadow-sm">
              Protected Surfaces
            </div>
            <h2 className="mt-5 text-3xl md:text-5xl font-black tracking-tighter leading-tight text-slate-950">
              Security coverage across the <span className="text-[#ff5d22]">revenue path.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-sm md:text-base font-bold leading-relaxed text-black/55">
              AppGallop security is focused on the real operating surfaces that move revenue from customer intent to financial record.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {protectedSurfaces.map((surface, i) => (
              <motion.div
                key={surface.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group rounded-[1.35rem] border border-black/6 bg-white p-5 shadow-sm transition-all hover:border-[#ff5d22]/25 hover:shadow-[0_18px_55px_rgba(15,23,42,0.10)]"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#ff5d22]/15 bg-[#ff5d22]/8 text-[#ff5d22] transition-colors group-hover:bg-[#ff5d22] group-hover:text-white">
                    <surface.icon size={19} />
                  </div>
                  <div>
                    <h3 className="text-lg font-black tracking-tight text-slate-950">{surface.title}</h3>
                    <p className="mt-2 text-sm font-bold leading-relaxed text-black/50">{surface.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="mt-16">
          <div className="mb-8 max-w-3xl">
            <div className="inline-flex items-center rounded-full border border-[#ff5d22]/15 bg-white px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff5d22] shadow-sm">
              Security Foundation
            </div>
            <h2 className="mt-5 text-3xl md:text-5xl font-black tracking-tighter leading-tight text-slate-950">
              Layered protection for <span className="text-[#ff5d22]">commercial data.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-sm md:text-base font-bold leading-relaxed text-black/55">
              Security is not one gate at login. It is a set of controls across identity, data movement, workflows, integrations, and operational evidence.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {trustPillars.map((pillar, i) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="group rounded-[1.35rem] border border-black/6 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-[#ff5d22]/25 hover:shadow-[0_18px_55px_rgba(15,23,42,0.10)]"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#ff5d22]/15 bg-[#ff5d22]/8 text-[#ff5d22] transition-colors group-hover:bg-[#ff5d22] group-hover:text-white">
                  <pillar.icon size={21} />
                </div>
                <h3 className="mt-6 text-xl font-black tracking-tight text-slate-950">{pillar.title}</h3>
                <p className="mt-3 text-sm font-bold leading-relaxed text-black/50">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section id="security-controls" className="mt-16 overflow-hidden rounded-[2rem] border border-black/6 bg-white shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="relative overflow-hidden bg-slate-950 p-7 md:p-10 lg:col-span-6">
              <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:34px_34px]" />
              <div className="relative z-10 flex h-full min-h-[560px] flex-col">
                <div>
                  <div className="inline-flex rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff8a5c]">
                    Control Framework
                  </div>
                  <h2 className="mt-5 text-3xl md:text-4xl font-black tracking-tighter leading-tight text-white">
                    Governed controls from <span className="text-[#ff8a5c]">access to ledger.</span>
                  </h2>
                  <p className="mt-5 text-sm font-bold leading-relaxed text-white/70">
                    AppGallop is designed around practical enterprise controls that protect how users, partners, integrations, and finance workflows interact with the platform.
                  </p>
                </div>

                <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    { title: "Access gate", desc: "Verify identity, role, MFA, and request context.", icon: Lock },
                    { title: "Event capture", desc: "Record workflow, API, webhook, and admin actions.", icon: Terminal },
                    { title: "Finance trace", desc: "Connect billing, tax, payment, and ledger decisions.", icon: CreditCard },
                    { title: "Review pack", desc: "Prepare evidence for security and finance review.", icon: FileCheck },
                  ].map((item) => (
                    <div key={item.title} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#ff5d22]/25 bg-[#ff5d22]/10 text-[#ff8a5c]">
                          <item.icon size={16} />
                        </div>
                        <div>
                          <p className="text-xs font-black text-white">{item.title}</p>
                          <p className="mt-1 text-[11px] font-bold leading-relaxed text-white/45">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-4 shadow-[0_20px_70px_rgba(0,0,0,0.22)]">
                  <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-4">
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-[0.22em] text-white/35">Control lifecycle</p>
                      <p className="mt-1 text-sm font-black text-white">Review-ready operating loop</p>
                    </div>
                    <span className="rounded-full bg-[#ff5d22]/15 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-[#ff8a5c]">
                      Live
                    </span>
                  </div>

                  <div className="mt-4 space-y-3">
                    {[
                      { label: "Verify", desc: "Identity, role, and policy context" },
                      { label: "Record", desc: "Workflow, API, and admin evidence" },
                      { label: "Report", desc: "Security and finance review package" },
                    ].map((step, index) => (
                      <div key={step.label} className="grid grid-cols-[32px_minmax(0,1fr)] gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#ff5d22]/25 bg-[#ff5d22]/10 text-[10px] font-black text-[#ff8a5c]">
                          0{index + 1}
                        </div>
                        <div>
                          <p className="text-xs font-black text-white">{step.label}</p>
                          <p className="mt-1 text-[11px] font-bold leading-relaxed text-white/45">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            </div>

            <div className="p-7 md:p-10 lg:col-span-6">
              <div className="flex h-full flex-col rounded-2xl border border-black/6 bg-slate-50 p-3">
                {controlGroups.map((group, i) => (
                  <motion.div
                    key={group.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="group rounded-xl border border-transparent bg-white p-4 transition-all hover:border-[#ff5d22]/25 hover:shadow-lg"
                  >
                    <div className="grid gap-4 md:grid-cols-[minmax(180px,0.85fr)_minmax(0,1.15fr)] md:items-start">
                      <div className="flex items-start gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#ff5d22]/10 text-[#ff5d22] transition-colors group-hover:bg-[#ff5d22] group-hover:text-white">
                          <group.icon size={18} />
                        </div>
                        <div>
                          <div className="text-[9px] font-black uppercase tracking-[0.18em] text-black/30">Control 0{i + 1}</div>
                          <h3 className="mt-1 text-sm font-black tracking-tight text-slate-950">{group.title}</h3>
                        </div>
                      </div>

                      <div className="grid gap-2">
                          {group.items.map((item) => (
                            <div key={item} className="flex items-start gap-2 rounded-lg border border-black/6 bg-slate-50 px-3 py-2 text-xs font-bold leading-relaxed text-black/55">
                              <Check size={13} className="mt-0.5 shrink-0 text-[#ff5d22]" />
                              <span>{item}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-16 rounded-[2rem] bg-slate-950 p-8 text-white md:p-10">
          <div className="flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-white">
                Need security details for <span className="text-[#ff8a5c]">your review?</span>
              </h2>
              <p className="mt-3 max-w-2xl text-sm md:text-base font-bold leading-relaxed text-white/70">
                Share your review requirements and we will walk through identity controls, data handling, audit evidence, and deployment readiness.
              </p>
            </div>
            <Link to="/contact" className="inline-flex min-h-12 shrink-0 items-center justify-center rounded-2xl bg-[#ff5d22] px-6 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-[#ff5d22]/20 transition-all hover:bg-white hover:text-slate-950">
              Contact Security Team
            </Link>
          </div>
        </section>
      </div>
      <ContactSection id="contact" />
    </motion.div>
  );
};

const RevampedPlatformAboutPage = ({ onDemoRequest }: { onDemoRequest: () => void }) => {
  const handleContactJump = () => {
    onDemoRequest();
  };

  const aboutStats = [
    { value: "99.9%", label: "availability-focused operating standard" },
    { value: "6 wks", label: "accelerated marketplace launch framework" },
    { value: "30+", label: "years of leadership depth across cloud and IT" },
  ];

  const edgeCards = [
    {
      title: "Cloud monetization expertise",
      desc: "Built for telecom operators, cloud distributors, MSPs, and ISVs that need commercial systems to move as fast as the market.",
      icon: Cloud,
    },
    {
      title: "Lifecycle-to-ledger automation",
      desc: "Unifies partner engagement, product configuration, billing, provisioning, renewals, and financial operations in one connected flow.",
      icon: RefreshCw,
    },
    {
      title: "Enterprise foundation",
      desc: "SOC 2 and ISO-driven processes, robust support, and deep connectivity with hyperscalers, ISVs, ERP, CRM, tax, and currency systems.",
      icon: ShieldCheck,
    },
  ];

  const operatingPillars = [
    "Low-touch, self-serve marketplace operations",
    "Automated cloud billing and monetization",
    "Partner, reseller, and customer lifecycle control",
    "Rapid pilot-to-production deployment framework",
  ];

  const leaderMessages = [
    {
      name: "Rajeev Vij",
      title: "Rajeev Vij",
      role: "Founder & CEO",
      image: rajeevImage,
      quote: "Our purpose is to turn cloud complexity into a scalable growth engine for the businesses building tomorrow's digital ecosystems.",
      bio: "Rajeev brings more than 30 years of technology leadership, including two decades shaping SaaS and cloud-led innovation. He has led go-to-market programs with global technology leaders and guides AppGallop with a focus on speed, clarity, and measurable business impact.",
    },    
    {
      name: "Saranjit Singh",
      title: "Saranjit Singh",
      role: "Chief Technology Officer",
      image: saranSirImage,
      quote: "Modern platforms should remove friction, anticipate scale, and give every stakeholder a reliable path from intent to execution.",
      bio: "Saranjit has over 20 years of experience architecting enterprise solutions across banking, telecom, and public sector domains. At AppGallop, he leads engineering across customer engagement, OEM revenue automation, and lifecycle management.",
    },
    {
      name: "Ashok Kumar",
      title: "Ashok Kumar",
      role: "Chief Operating Officer",
      image: ashokImage,
      quote: "Operational excellence means creating systems that help teams deploy faster, scale confidently, and protect profitability.",
      bio: "Ashok brings more than 30 years of business transformation experience across IT, cloud, travel, distribution, and product markets. As COO, he drives operational discipline, certification readiness, deployment acceleration, and low-touch execution at scale.",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20 pb-12 overflow-x-hidden min-h-screen bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <section className="relative overflow-hidden rounded-[2rem] md:rounded-[3rem] border border-black/8 bg-white text-slate-950 shadow-[0_24px_90px_rgba(15,23,42,0.08)] ring-1 ring-black/[0.03]">
          <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/70 to-transparent" />
          <div className="absolute inset-x-8 bottom-0 h-px bg-gradient-to-r from-transparent via-black/10 to-transparent" />
          <div className="absolute right-0 top-0 h-72 w-72 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#ff5d22]/10 blur-[90px]" />
          <div className="absolute left-0 bottom-0 h-64 w-64 -translate-x-1/3 translate-y-1/3 rounded-full bg-slate-900/[0.04] blur-[80px]" />
          <div className="absolute inset-0 opacity-[0.035] bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:38px_38px]" />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 p-7 md:p-12 lg:p-14 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/8 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff5d22] shadow-sm">
                About AppGallop
              </div>
              <h1 className="mt-6 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-slate-950">
                Built for the ones who <span className="text-[#ff5d22]">build the cloud.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-sm md:text-base font-bold leading-relaxed text-black/55">
                AppGallop helps telecom operators, IT distributors, MSPs, and ISVs simplify how digital products are sold, billed, provisioned, renewed, and governed. The platform brings monetization, automated billing, and lifecycle operations into one low-touch operating system for sustainable cloud growth.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button onClick={handleContactJump} variant="primary">
                  Talk to AppGallop
                </Button>
                <Link
                  to="/products/marketplace"
                  className="inline-flex min-h-12 items-center rounded-2xl border border-black/10 bg-white px-6 text-[10px] font-black uppercase tracking-widest text-slate-950 shadow-sm transition-all hover:border-[#ff5d22]/30 hover:bg-[#ff5d22]/5"
                >
                  Explore Platform
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-[1.5rem] border border-black/8 bg-slate-50/80 p-5 md:p-6 shadow-inner">
                <div className="grid grid-cols-1 gap-4">
                  {aboutStats.map((stat, i) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 14 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 }}
                      className="flex items-center justify-between gap-5 rounded-xl border border-black/8 bg-white px-5 py-4 shadow-sm"
                    >
                      <div className="text-3xl font-black text-[#000] tracking-tighter">{stat.value}</div>
                      <div className="max-w-[220px] text-right text-[10px] font-black uppercase tracking-widest leading-relaxed text-black/45">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center rounded-full border border-[#ff5d22]/15 bg-white px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff5d22] shadow-sm">
                What we do
              </div>
              <h2 className="mt-5 max-w-xl text-3xl md:text-5xl font-black tracking-tighter leading-tight text-slate-950">
                We simplify the business layer behind <span className="text-[#ff5d22]">cloud growth.</span>
              </h2>
              <p className="mt-5 max-w-xl text-sm md:text-base font-bold leading-relaxed text-black/55">
                AppGallop connects the commercial, operational, and financial work that sits between cloud demand and revenue. Teams get a cleaner way to launch marketplaces, manage partners, automate billing, and govern lifecycle events without stitching together disconnected tools.
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-3">
                {[
                  "Marketplace launch",
                  "Billing automation",
                  "Lifecycle governance",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-xl border border-black/6 bg-slate-50 px-4 py-3">
                    <div className="h-2 w-2 rounded-full bg-[#ff5d22]" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-black/50">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="overflow-hidden rounded-[1.75rem] border border-black/8 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.08)]">
                <div className="border-b border-black/6 bg-slate-50/80 px-5 py-4 md:px-6">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-black/35">
                      Core operating model
                    </p>
                    <p className="text-xs font-bold text-black/45">
                      Structured for launch, scale, and control
                    </p>
                  </div>
                </div>

                <div className="divide-y divide-black/6">
                  {edgeCards.map((item, i) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, x: 18 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 }}
                      className="group grid grid-cols-1 gap-4 px-5 py-6 transition-colors hover:bg-slate-50/80 sm:grid-cols-[64px_1fr] md:px-6"
                    >
                      <div className="flex items-center gap-3 sm:flex-col sm:items-start">
                        <span className="text-[10px] font-black uppercase tracking-widest text-black/25">0{i + 1}</span>
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#ff5d22]/15 bg-[#ff5d22]/8 text-[#ff5d22] transition-colors group-hover:bg-[#ff5d22] group-hover:text-white">
                          <item.icon size={20} />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg md:text-xl font-black tracking-tight text-slate-950">{item.title}</h3>
                        <p className="mt-2 max-w-2xl text-sm font-bold leading-relaxed text-black/50">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="grid grid-cols-1 border-t border-black/6 bg-slate-950 sm:grid-cols-3">
                  {aboutStats.map((stat) => (
                    <div key={stat.label} className="border-b border-white/10 px-5 py-4 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0 sm:border-white/10">
                      <div className="text-2xl font-black tracking-tighter text-[#fff]">{stat.value}</div>
                      <div className="mt-1 text-[9px] font-black uppercase tracking-widest leading-relaxed text-white/65">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-16 overflow-hidden rounded-[2rem] border border-black/6 bg-white shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="relative overflow-hidden bg-slate-950 p-7 md:p-10 lg:col-span-5">
              <div className="absolute right-0 top-0 h-72 w-72 translate-x-1/3 -translate-y-1/3 rounded-full bg-[#ff5d22]/20 blur-[90px]" />
              <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:34px_34px]" />
              <div className="relative z-10">
                <div className="inline-flex rounded-full border border-[#ff5d22]/20 bg-[#ff5d22]/10 px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#ff8a5c]">
                  Why AppGallop exists
                </div>
                <h2 className="mt-5 text-3xl md:text-4xl font-black tracking-tighter leading-tight text-white">
                  To turn complexity into <span className="text-[#ff8a5c]">clarity</span> for modern cloud providers.
                </h2>
                <p className="mt-5 text-sm font-bold leading-relaxed text-white/70">
                  Digital commerce, cloud distribution, and partner ecosystems are too complex for disconnected tools. AppGallop gives teams a structured operating layer for sales, channels, billing, lifecycle automation, and business process control.
                </p>
              </div>
            </div>

            <div className="p-7 md:p-10 lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {operatingPillars.map((item, i) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="group min-h-[132px] rounded-xl border border-black/6 bg-slate-50 p-5 transition-all hover:border-[#ff5d22]/25 hover:bg-white hover:shadow-lg"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#ff5d22]/10 text-[#ff5d22] transition-colors group-hover:bg-[#ff5d22] group-hover:text-white">
                        <Check size={16} />
                      </div>
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-black/30">Pillar 0{i + 1}</div>
                        <p className="mt-2 text-sm font-black leading-snug text-slate-950">{item}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <blockquote className="relative mt-5 rounded-xl border border-[#ff5d22]/15 bg-[#ff5d22]/5 px-5 py-5">
                <span className="absolute left-4 top-2 text-5xl font-black leading-none text-[#ff5d22]/20">"</span>
                <p className="relative pl-7 text-sm font-black italic leading-relaxed text-slate-950">
                  The result is a cleaner commercial engine where partner operations, customer lifecycle events, billing decisions, and financial controls work from the same source of truth.
                </p>
              </blockquote>
            </div>
          </div>
        </section>

        <section className="mt-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="inline-flex items-center rounded-full bg-black px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] text-white">
                From Our Leaders
              </div>
              <h2 className="mt-5 text-3xl md:text-5xl font-black tracking-tighter leading-tight text-slate-950">
                What our leaders say about <span className="text-[#ff5d22]">scale, speed, and trust.</span>
              </h2>
            </div>
            <p className="max-w-md text-sm font-bold leading-relaxed text-black/50">
              The reference story is simple: help cloud-led businesses monetize faster while keeping execution clean, resilient, and enterprise-ready.
            </p>
          </div>

          <div className="mt-9 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {leaderMessages.map((leader, i) => (
              <motion.article
                key={leader.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -6 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="group relative overflow-hidden rounded-[1.5rem] border border-black/6 bg-white p-6 shadow-sm transition-all hover:border-[#ff5d22]/25 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)]"
              >
                <div className="pointer-events-none absolute right-0 top-0 h-36 w-36 translate-x-10 -translate-y-10 rounded-full bg-[#ff5d22]/10 blur-3xl transition-opacity group-hover:opacity-80" />
                <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />

                <div className="relative z-10 flex min-h-[420px] flex-col">
                  <div className="flex items-center justify-between gap-4">
                    <div className="inline-flex items-center rounded-full border border-[#ff5d22]/15 bg-[#ff5d22]/5 px-3 py-1 text-[9px] font-black uppercase tracking-[0.18em] text-[#ff5d22]">
                      {leader.role}
                    </div>
                    <Quote size={22} className="shrink-0 text-[#ff5d22]/45" />
                  </div>

                  <p className="mt-8 text-xl font-black leading-snug tracking-tight text-slate-950">
                    "{leader.quote}"
                  </p>

                  <p className="mt-5 text-sm font-bold leading-relaxed text-black/50">
                    {leader.bio}
                  </p>

                  <div className="mt-auto pt-8">
                    <div className="flex items-center gap-4 border-t border-black/6 pt-5">
                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-black/8 bg-slate-100 shadow-sm">
                        <img
                          src={leader.image}
                          alt={leader.name}
                          className="h-full w-full object-fill duration-700 group-hover:scale-105 group-hover:grayscale-0"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-black tracking-tight text-slate-950">{leader.title}</h3>
                        <p className="mt-1 text-[10px] font-black uppercase tracking-[0.18em] text-black/35">
                          AppGallop Leadership
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        <ProductEcosystem />

        <section className="mt-20 rounded-[2rem] bg-slate-950 text-white p-8 md:p-10 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[360px] h-[360px] bg-[#ff5d22]/20 blur-[90px] rounded-full translate-x-1/3 -translate-y-1/3" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-7">
            <div>
              <h3 className="text-3xl md:text-4xl font-black text-white/90 tracking-tighter">Ready to <span className="text-[#ff8a5c]">accelerate your growth?</span></h3>
              <p className="text-white/70 text-sm md:text-base font-bold mt-3">
                Build a cleaner commercial engine for digital products, cloud services, and partner-led revenue.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Button onClick={handleContactJump} variant="primary">
                Get in touch
              </Button>
            </div>
          </div>
        </section>
      </div>
      <ContactSection id="contact" />
    </motion.div>
  );
};

const PUBLIC_EMAIL_DOMAINS = new Set([
    // Google
    'gmail.com',
    'googlemail.com',
 
    // Microsoft
    'outlook.com',
    'hotmail.com',
    'live.com',
    'msn.com',
 
    // Yahoo
    'yahoo.com',
    'yahoo.co.in',
    'yahoo.co.uk',
    'yahoo.ca',
    'yahoo.com.au',
    'yahoo.co.nz',
    'ymail.com',
    'rocketmail.com',
 
    // AOL
    'aol.com',
    'aim.com',
    'aol.in',
 
    // Apple
    'icloud.com',
    'me.com',
    'mac.com',
 
    // Proton
    'proton.me',
    'protonmail.com',
    'pm.me',
 
    // Zoho
    'zoho.com',
 
    // GMX
    'gmx.com',
    'gmx.net',
    'gmx.de',
    'gmx.at',
    'gmx.ch',
 
    // Mail.com
    'mail.com',
 
    // Yandex
    'yandex.com',
    'yandex.ru',
    'ya.ru',
 
    // Russian / European providers
    'mail.ru',
    'inbox.ru',
    'list.ru',
    'bk.ru',
    'rambler.ru',
 
    // German providers
    'web.de',
    'freenet.de',
 
    // Tuta
    'tuta.com',
    'tutanota.com',
    'tutanota.de',
 
    // Fastmail
    'fastmail.com',
    'fastmail.fm',
 
    // Mailfence
    'mailfence.com',
 
    // India
    'rediffmail.com',
    'rediff.com',
 
    // China
    'qq.com',
    'foxmail.com',
    '163.com',
    '126.com',
    'yeah.net',
 
    // Korea
    'naver.com',
    'daum.net',
    'hanmail.net',
 
    // Japan
    'mail.yahoo.co.jp',
 
    // Other
    'hushmail.com',
    'lycos.com',
    'excite.com',
    'inbox.com',
    'mail2world.com',
]);

const isCompanyEmail = (email: string): boolean => {
  const domain = email.trim().toLowerCase().split('@')[1];

  return Boolean(domain) && !PUBLIC_EMAIL_DOMAINS.has(domain);
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [footerEmail, setFooterEmail] = useState("");
  const footerLeadHoneypotRef = useRef<HTMLInputElement | null>(null);
  const [footerLeadStatus, setFooterLeadStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [footerLeadMessage, setFooterLeadMessage] = useState("");

  useEffect(() => {
    setIsNavigating(false);
  }, [location.pathname, location.search, location.hash]);

  const handleFooterLeadSubmit = async (
  event: React.FormEvent<HTMLFormElement>
) => {
  event.preventDefault();

  const trimmedEmail = footerEmail.trim().toLowerCase();
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(trimmedEmail)) {
    setFooterLeadStatus('error');
    setFooterLeadMessage('Please enter a valid business email address.');
    return;
  }

  if (!isCompanyEmail(trimmedEmail)) {
    setFooterLeadStatus('error');
    setFooterLeadMessage(
      'Please use your company email address. Personal email addresses are not accepted.'
    );
    return;
  }

  setFooterLeadStatus('loading');
  setFooterLeadMessage('');

  try {
    const recaptchaAction = 'lead_footer';
    const recaptchaToken = await executeRecaptchaV3(recaptchaAction);

    const response = await fetch('/api/lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        inquiryType: 'Footer newsletter signup',
        email: trimmedEmail,
        message: 'Submitted from the footer newsletter form.',
        sourcePage: window.location.pathname,
        website: footerLeadHoneypotRef.current?.value || '',
        recaptchaToken,
        recaptchaAction,
      }),
    });

    const result = await response.json();

    if (response.ok && result.success) {
      setFooterLeadStatus('success');
      setFooterLeadMessage('Thanks. Our team has received your email.');
      setFooterEmail('');

      window.setTimeout(() => {
        setFooterLeadStatus('idle');
        setFooterLeadMessage('');
      }, 5000);
    } else {
      setFooterLeadStatus('error');
      setFooterLeadMessage(
        result.error || 'Unable to submit right now. Please try again.'
      );
    }
  } catch {
    setFooterLeadStatus('error');
    setFooterLeadMessage(
      'Unable to submit right now. Please try again.'
    );
  }
};

  const openContactPage = () => {
    storeCurrentNavigationOrigin();
    storeNavigationIntent(new URL('/contact', window.location.href));
    navigate('/contact');
  };

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const handleReloadNavigation = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a');
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const rawHref = anchor.getAttribute('href');
      if (!rawHref) return;
      if (rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.href === window.location.href) return;
      if (
        url.hash &&
        url.pathname === window.location.pathname &&
        url.search === window.location.search
      ) {
        event.preventDefault();
        navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
        return;
      }

      event.preventDefault();
      storeCurrentNavigationOrigin();
      storeNavigationIntent(url);
      setIsNavigating(true);
      window.setTimeout(() => {
        navigate(`${url.pathname}${url.search}${url.hash}`);
      }, 150);
    };

    const handlePageHide = () => storeCurrentNavigationOrigin();

    window.addEventListener('click', handleReloadNavigation, true);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('click', handleReloadNavigation, true);
      window.removeEventListener('pagehide', handlePageHide);
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, [navigate]);

  return (
    <>
      <AnalyticsManager />
      <RecaptchaV3Badge />
      <ScrollToTop />
      <SeoManager />
      <div className="min-h-screen font-sans selection:bg-[#ff5d22] selection:text-white relative bg-white">
        {isNavigating && (
          <>
            <div className="fixed top-0 left-0 right-0 z-[6000] h-1 bg-[#ff5d22]/20 overflow-hidden">
              <div className="nav-loading-bar h-full bg-[#ff5d22]" />
            </div>
            <div className="fixed inset-0 z-[5999] bg-white/60 backdrop-blur-sm flex items-center justify-center">
              <div className="h-12 w-12 rounded-full border-2 border-black/10 border-t-[#ff5d22] animate-spin" />
            </div>
          </>
        )}
        <ScrollProgress />
        <Navbar />
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={<HomePage onDemoRequest={openContactPage} />} />
            <Route path="/platform" element={<Navigate to="/platform/about" replace />} />
            <Route path="/platform/integrations" element={<PlatformIntegrationsPage />} />
            <Route path="/platform/security" element={<PlatformSecurityPage />} />
            <Route path="/platform/about" element={<RevampedPlatformAboutPage onDemoRequest={openContactPage} />} />
            <Route path="/platform/lead-to-order" element={<LeadToOrderPage />} />
            <Route path="/platform/order-to-cash" element={<OrderToCashPage />} />
            <Route path="/platform/ticket-to-resolution" element={<TicketToResolutionPage />} />
            <Route path="/platform/core-capabilities" element={<Navigate to="/platform/lead-to-order" replace />} />
            <Route path="/platform/system-resilience" element={<SystemResiliencePage />} />
            <Route path="/platform/core-advantage" element={<CoreAdvantagePage />} />
            <Route path="/services" element={<Navigate to="/services/revenue-assurance" replace />} />
            <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
            <Route path="/solutions" element={<ServicesPage />} />
            <Route path="/solutions/:serviceId" element={<ServiceDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/case-studies/:caseStudyId" element={<CaseStudiesPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />

            {/* New Dynamic Routes */}
            <Route path="/products/:productId" element={<ProductPage />} />
            <Route path="/solutions/:solutionId" element={<SolutionDetailPage />} />
            <Route path="/industries/telco-to-techco" element={<TelcoToTechcoPage />} />
            <Route path="/industries/cloud-distribution" element={<CloudDistributionPage />} />
            <Route path="/industries/saas-isv" element={<SaasIsvPage />} />
            <Route path="/capabilities" element={<CoreCapabilitiesPage />} />
            <Route path="/system-resilience" element={<SystemResiliencePage />} />
            <Route path="/core-advantage" element={<CoreAdvantagePage />} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AnimatePresence>

        <ScrollToTopButton />
        <Chatbot />

        <LogoCloud />
        {/* Footer */}
        <footer className="bg-[#050505] text-white relative overflow-hidden border-t border-[#ff5d22]/20">
          <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-7 pb-8 border-b border-white/10">
              <div className="space-y-3 max-w-xl">
                <img
                  src={appGallopLogoWhite}
                  alt="AppGallop"
                  className="h-9 md:h-10 w-auto object-contain"
                />
                <p className="text-sm font-bold leading-relaxed text-white/55 max-w-lg">
                  Unified digital commerce, billing automation, and lifecycle orchestration for enterprise revenue teams.
                </p>
              </div>

              <form
                onSubmit={handleFooterLeadSubmit}
                className="relative w-full max-w-xl"
              >
                <input
                  ref={footerLeadHoneypotRef}
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="pointer-events-none absolute -left-[9999px] h-px w-px opacity-0"
                />
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    aria-label="Email address"
                    placeholder="Business email"
                    value={footerEmail}
                    onChange={(event) => {
                      setFooterEmail(event.target.value);
                      if (footerLeadStatus !== 'loading') {
                        setFooterLeadStatus('idle');
                        setFooterLeadMessage('');
                      }
                    }}
                    className="min-h-11 flex-1 rounded-lg border border-white/15 bg-white/[0.03] px-4 text-sm font-bold text-white outline-none transition-colors placeholder:text-white/35 focus:border-[#ff5d22] focus:bg-white/[0.05]"
                    disabled={footerLeadStatus === 'loading'}
                  />
                  <button
                    type="submit"
                    disabled={footerLeadStatus === 'loading'}
                    className="min-h-11 rounded-lg px-6 bg-[#ff5d22] text-white text-[10px] font-black uppercase tracking-widest transition-all hover:bg-white hover:text-black hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:bg-[#ff5d22] disabled:hover:text-white"
                  >
                    {footerLeadStatus === 'loading' ? 'Sending' : 'Submit'}
                  </button>
                </div>
                {footerLeadMessage && (
                  <p className={`mt-2 text-[11px] font-bold ${footerLeadStatus === 'success' ? 'text-emerald-400' : 'text-[#ff8a5c]'}`}>
                    {footerLeadMessage}
                  </p>
                )}
              </form>
            </div>

            <div className="py-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
              <div className="space-y-4">
                <h5 className="text-[10px] font-black uppercase tracking-widest text-[#ff5d22]">Products</h5>
                <ul className="space-y-2.5 text-sm font-bold text-white/60">
                  {[
                    { name: "Digital Commerce Platform", path: "/products/marketplace" },
                    { name: "Appgallop CPQ", path: "/products/revsimply" },
                    { name: "Appgallop RMS", path: "/products/rms" },
                    { name: "UC Billing", path: "/products/uc-billing" },
                  ].map((link) => (
                    <motion.li key={link.name} whileHover={{ x: 4 }}>
                      <Link to={link.path} className="hover:text-[#ff5d22] transition-colors">
                        {link.name}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <h5 className="text-[10px] font-black uppercase tracking-widest text-[#ff5d22]">Platform</h5>
                <ul className="space-y-2.5 text-sm font-bold text-white/60">
                  {[
                    { name: "About AppGallop", path: "/platform/about" },
                    { name: "Core Capabilities", path: "/capabilities" },
                    { name: "System Resilience", path: "/platform/system-resilience" },
                    { name: "Core Advantage", path: "/platform/core-advantage" },
                    { name: "Integrations", path: "/platform/integrations" },
                  ].map((link) => (
                    <motion.li key={link.name} whileHover={{ x: 4 }}>
                      <Link to={link.path} className="hover:text-[#ff5d22] transition-colors">
                        {link.name}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <h5 className="text-[10px] font-black uppercase tracking-widest text-[#ff5d22]">Solutions</h5>
                <ul className="space-y-2.5 text-sm font-bold text-white/60">
                  {[
                    { name: "Telco Digital Monetization", path: "/industries/telco-to-techco" },
                    { name: "Cloud Distribution", path: "/industries/cloud-distribution" },
                    { name: "Software Revenue Management", path: "/industries/saas-isv" },
                    { name: "Revenue Assurance", path: "/services/revenue-assurance" },
                    { name: "Security", path: "/platform/security" },
                  ].map((link) => (
                    <motion.li key={link.name} whileHover={{ x: 4 }}>
                      <Link to={link.path} className="hover:text-[#ff5d22] transition-colors">
                        {link.name}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <h5 className="text-[10px] font-black uppercase tracking-widest text-[#ff5d22]">Resources</h5>
                <ul className="space-y-2.5 text-sm font-bold text-white/60">
                  {[
                    { name: "Lead to Order", path: "/platform/lead-to-order" },
                    { name: "Order to Cash", path: "/platform/order-to-cash" },
                    { name: "Ticket to Resolution", path: "/platform/ticket-to-resolution" },
                    { name: "Case Studies", path: "/case-studies" },
                  ].map((link) => (
                    <motion.li key={link.name} whileHover={{ x: 4 }}>
                      <Link to={link.path} className="hover:text-[#ff5d22] transition-colors">
                        {link.name}
                      </Link>
                    </motion.li>
                  ))}
                  <li>
                    <Link to="/privacy-policy" className="hover:text-[#ff5d22] transition-colors">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms-and-conditions" className="hover:text-[#ff5d22] transition-colors">
                      Terms and Conditions
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="space-y-4">
                <h5 className="text-[10px] font-black uppercase tracking-widest text-[#ff5d22]">Connect</h5>
                <div className="space-y-2.5 text-sm font-bold text-white/60">
                  <Link to="/contact" className="block hover:text-[#ff5d22] transition-colors">
                    Request a Demo
                  </Link>                  
                  <a href="mailto:sales@appgallop.com" className="block hover:text-[#ff5d22] transition-colors">
                    Email: sales@appgallop.com
                  </a>
                  <div className="flex items-center gap-3 pt-1">
                    <motion.a
                      href="https://www.linkedin.com/company/appgallop/"
                      target="_blank"
                      rel="noreferrer"
                      aria-label="AppGallop LinkedIn"
                      whileHover={{ y: -2, scale: 1.08 }}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/[0.03] text-white/70 transition-colors hover:border-[#ff5d22] hover:text-[#ff5d22]"
                    >
                      <Linkedin size={16} />
                    </motion.a>
                    <motion.a
                      href="https://www.instagram.com/appgallop/"
                      target="_blank"
                      rel="noreferrer"
                      aria-label="AppGallop Instagram"
                      whileHover={{ y: -2, scale: 1.08 }}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/[0.03] text-white/70 transition-colors hover:border-[#ff5d22] hover:text-[#ff5d22]"
                    >
                      <Instagram size={16} />
                    </motion.a>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-5 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <p className="text-xs font-bold text-white/40">
                &copy; 2026 AppGallop. All rights reserved.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-[10px] font-black uppercase tracking-widest text-white/35">
                <span>GDPR Compliant</span>
                <span>ISO 27001 Certified</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
